
/*!*************************************************************************
 * Copyright 2021 Adobe
 * All Rights Reserved.
 *
 * NOTICE: Adobe permits you to use, modify, and distribute this file in
 * accordance with the terms of the Adobe license agreement accompanying
 * it. 
 **************************************************************************/

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

var makeError = {exports: {}};

(function (module, exports) {

// ===================================================================

var construct = typeof Reflect !== "undefined" ? Reflect.construct : undefined;
var defineProperty = Object.defineProperty;

// -------------------------------------------------------------------

var captureStackTrace = Error.captureStackTrace;
if (captureStackTrace === undefined) {
  captureStackTrace = function captureStackTrace(error) {
    var container = new Error();

    defineProperty(error, "stack", {
      configurable: true,
      get: function getStack() {
        var stack = container.stack;

        // Replace property with value for faster future accesses.
        defineProperty(this, "stack", {
          configurable: true,
          value: stack,
          writable: true,
        });

        return stack;
      },
      set: function setStack(stack) {
        defineProperty(error, "stack", {
          configurable: true,
          value: stack,
          writable: true,
        });
      },
    });
  };
}

// -------------------------------------------------------------------

function BaseError(message) {
  if (message !== undefined) {
    defineProperty(this, "message", {
      configurable: true,
      value: message,
      writable: true,
    });
  }

  var cname = this.constructor.name;
  if (cname !== undefined && cname !== this.name) {
    defineProperty(this, "name", {
      configurable: true,
      value: cname,
      writable: true,
    });
  }

  captureStackTrace(this, this.constructor);
}

BaseError.prototype = Object.create(Error.prototype, {
  // See: https://github.com/JsCommunity/make-error/issues/4
  constructor: {
    configurable: true,
    value: BaseError,
    writable: true,
  },
});

// -------------------------------------------------------------------

// Sets the name of a function if possible (depends of the JS engine).
var setFunctionName = (function() {
  function setFunctionName(fn, name) {
    return defineProperty(fn, "name", {
      configurable: true,
      value: name,
    });
  }
  try {
    var f = function() {};
    setFunctionName(f, "foo");
    if (f.name === "foo") {
      return setFunctionName;
    }
  } catch (_) {}
})();

// -------------------------------------------------------------------

function makeError(constructor, super_) {
  if (super_ == null || super_ === Error) {
    super_ = BaseError;
  } else if (typeof super_ !== "function") {
    throw new TypeError("super_ should be a function");
  }

  var name;
  if (typeof constructor === "string") {
    name = constructor;
    constructor =
      construct !== undefined
        ? function() {
            return construct(super_, arguments, this.constructor);
          }
        : function() {
            super_.apply(this, arguments);
          };

    // If the name can be set, do it once and for all.
    if (setFunctionName !== undefined) {
      setFunctionName(constructor, name);
      name = undefined;
    }
  } else if (typeof constructor !== "function") {
    throw new TypeError("constructor should be either a string or a function");
  }

  // Also register the super constructor also as `constructor.super_` just
  // like Node's `util.inherits()`.
  //
  // eslint-disable-next-line dot-notation
  constructor.super_ = constructor["super"] = super_;

  var properties = {
    constructor: {
      configurable: true,
      value: constructor,
      writable: true,
    },
  };

  // If the name could not be set on the constructor, set it on the
  // prototype.
  if (name !== undefined) {
    properties.name = {
      configurable: true,
      value: name,
      writable: true,
    };
  }
  constructor.prototype = Object.create(super_.prototype, properties);

  return constructor;
}
exports = module.exports = makeError;
exports.BaseError = BaseError;
}(makeError, makeError.exports));

class IncompatibleBrowserError extends makeError.exports.BaseError {
    constructor() {
        super("The browser you are using isn't compatible with this application, or HTTPS is not being used on a non-localhost domain.");
    }
}
class MissingWasmSourceError extends makeError.exports.BaseError {
    constructor() {
        super('No WebAssembly source URL was passed');
    }
}
class MissingWorkerSourceError extends makeError.exports.BaseError {
    constructor() {
        super('No web worker source URL was passed');
    }
}
class InvalidWorkerSourceError extends makeError.exports.BaseError {
    constructor(url, res, err) {
        super(`Could not fetch web worker from ${url}`);
        Object.defineProperty(this, "url", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "response", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "originalError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.url = url;
        this.response = res;
        this.originalError = err !== null && err !== void 0 ? err : null;
    }
}
class InvalidInputError extends makeError.exports.BaseError {
    constructor() {
        super(`Invalid input passed`);
    }
}
class InvalidMimeTypeError extends makeError.exports.BaseError {
    constructor(mimeType) {
        super(`Invalid mime type found on asset`);
        Object.defineProperty(this, "mimeType", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.mimeType = mimeType;
    }
}
class UrlFetchError extends makeError.exports.BaseError {
    constructor(url, res, err) {
        super(`Could not fetch resource from ${url}`);
        Object.defineProperty(this, "url", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "response", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "originalError", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.url = url;
        this.response = res;
        this.originalError = err !== null && err !== void 0 ? err : null;
    }
}
class DictionaryUrlNotFoundError extends makeError.exports.BaseError {
    constructor() {
        super(`Dictionary URL not found. Please check you have an 'adobe.dictionary' assertion with a 'url' key.`);
    }
}

var error = /*#__PURE__*/Object.freeze({
	__proto__: null,
	IncompatibleBrowserError: IncompatibleBrowserError,
	MissingWasmSourceError: MissingWasmSourceError,
	MissingWorkerSourceError: MissingWorkerSourceError,
	InvalidWorkerSourceError: InvalidWorkerSourceError,
	InvalidInputError: InvalidInputError,
	InvalidMimeTypeError: InvalidMimeTypeError,
	UrlFetchError: UrlFetchError,
	DictionaryUrlNotFoundError: DictionaryUrlNotFoundError
});

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

function __classPrivateFieldSet(receiver, state, value, kind, f) {
    if (kind === "m") throw new TypeError("Private method is not writable");
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a setter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot write private member to an object whose class did not declare it");
    return (kind === "a" ? f.call(receiver, value) : f ? f.value = value : state.set(receiver, value)), value;
}

var _mapping = {};

/** Used to map aliases to their real names. */

(function (exports) {
exports.aliasToReal = {

  // Lodash aliases.
  'each': 'forEach',
  'eachRight': 'forEachRight',
  'entries': 'toPairs',
  'entriesIn': 'toPairsIn',
  'extend': 'assignIn',
  'extendAll': 'assignInAll',
  'extendAllWith': 'assignInAllWith',
  'extendWith': 'assignInWith',
  'first': 'head',

  // Methods that are curried variants of others.
  'conforms': 'conformsTo',
  'matches': 'isMatch',
  'property': 'get',

  // Ramda aliases.
  '__': 'placeholder',
  'F': 'stubFalse',
  'T': 'stubTrue',
  'all': 'every',
  'allPass': 'overEvery',
  'always': 'constant',
  'any': 'some',
  'anyPass': 'overSome',
  'apply': 'spread',
  'assoc': 'set',
  'assocPath': 'set',
  'complement': 'negate',
  'compose': 'flowRight',
  'contains': 'includes',
  'dissoc': 'unset',
  'dissocPath': 'unset',
  'dropLast': 'dropRight',
  'dropLastWhile': 'dropRightWhile',
  'equals': 'isEqual',
  'identical': 'eq',
  'indexBy': 'keyBy',
  'init': 'initial',
  'invertObj': 'invert',
  'juxt': 'over',
  'omitAll': 'omit',
  'nAry': 'ary',
  'path': 'get',
  'pathEq': 'matchesProperty',
  'pathOr': 'getOr',
  'paths': 'at',
  'pickAll': 'pick',
  'pipe': 'flow',
  'pluck': 'map',
  'prop': 'get',
  'propEq': 'matchesProperty',
  'propOr': 'getOr',
  'props': 'at',
  'symmetricDifference': 'xor',
  'symmetricDifferenceBy': 'xorBy',
  'symmetricDifferenceWith': 'xorWith',
  'takeLast': 'takeRight',
  'takeLastWhile': 'takeRightWhile',
  'unapply': 'rest',
  'unnest': 'flatten',
  'useWith': 'overArgs',
  'where': 'conformsTo',
  'whereEq': 'isMatch',
  'zipObj': 'zipObject'
};

/** Used to map ary to method names. */
exports.aryMethod = {
  '1': [
    'assignAll', 'assignInAll', 'attempt', 'castArray', 'ceil', 'create',
    'curry', 'curryRight', 'defaultsAll', 'defaultsDeepAll', 'floor', 'flow',
    'flowRight', 'fromPairs', 'invert', 'iteratee', 'memoize', 'method', 'mergeAll',
    'methodOf', 'mixin', 'nthArg', 'over', 'overEvery', 'overSome','rest', 'reverse',
    'round', 'runInContext', 'spread', 'template', 'trim', 'trimEnd', 'trimStart',
    'uniqueId', 'words', 'zipAll'
  ],
  '2': [
    'add', 'after', 'ary', 'assign', 'assignAllWith', 'assignIn', 'assignInAllWith',
    'at', 'before', 'bind', 'bindAll', 'bindKey', 'chunk', 'cloneDeepWith',
    'cloneWith', 'concat', 'conformsTo', 'countBy', 'curryN', 'curryRightN',
    'debounce', 'defaults', 'defaultsDeep', 'defaultTo', 'delay', 'difference',
    'divide', 'drop', 'dropRight', 'dropRightWhile', 'dropWhile', 'endsWith', 'eq',
    'every', 'filter', 'find', 'findIndex', 'findKey', 'findLast', 'findLastIndex',
    'findLastKey', 'flatMap', 'flatMapDeep', 'flattenDepth', 'forEach',
    'forEachRight', 'forIn', 'forInRight', 'forOwn', 'forOwnRight', 'get',
    'groupBy', 'gt', 'gte', 'has', 'hasIn', 'includes', 'indexOf', 'intersection',
    'invertBy', 'invoke', 'invokeMap', 'isEqual', 'isMatch', 'join', 'keyBy',
    'lastIndexOf', 'lt', 'lte', 'map', 'mapKeys', 'mapValues', 'matchesProperty',
    'maxBy', 'meanBy', 'merge', 'mergeAllWith', 'minBy', 'multiply', 'nth', 'omit',
    'omitBy', 'overArgs', 'pad', 'padEnd', 'padStart', 'parseInt', 'partial',
    'partialRight', 'partition', 'pick', 'pickBy', 'propertyOf', 'pull', 'pullAll',
    'pullAt', 'random', 'range', 'rangeRight', 'rearg', 'reject', 'remove',
    'repeat', 'restFrom', 'result', 'sampleSize', 'some', 'sortBy', 'sortedIndex',
    'sortedIndexOf', 'sortedLastIndex', 'sortedLastIndexOf', 'sortedUniqBy',
    'split', 'spreadFrom', 'startsWith', 'subtract', 'sumBy', 'take', 'takeRight',
    'takeRightWhile', 'takeWhile', 'tap', 'throttle', 'thru', 'times', 'trimChars',
    'trimCharsEnd', 'trimCharsStart', 'truncate', 'union', 'uniqBy', 'uniqWith',
    'unset', 'unzipWith', 'without', 'wrap', 'xor', 'zip', 'zipObject',
    'zipObjectDeep'
  ],
  '3': [
    'assignInWith', 'assignWith', 'clamp', 'differenceBy', 'differenceWith',
    'findFrom', 'findIndexFrom', 'findLastFrom', 'findLastIndexFrom', 'getOr',
    'includesFrom', 'indexOfFrom', 'inRange', 'intersectionBy', 'intersectionWith',
    'invokeArgs', 'invokeArgsMap', 'isEqualWith', 'isMatchWith', 'flatMapDepth',
    'lastIndexOfFrom', 'mergeWith', 'orderBy', 'padChars', 'padCharsEnd',
    'padCharsStart', 'pullAllBy', 'pullAllWith', 'rangeStep', 'rangeStepRight',
    'reduce', 'reduceRight', 'replace', 'set', 'slice', 'sortedIndexBy',
    'sortedLastIndexBy', 'transform', 'unionBy', 'unionWith', 'update', 'xorBy',
    'xorWith', 'zipWith'
  ],
  '4': [
    'fill', 'setWith', 'updateWith'
  ]
};

/** Used to map ary to rearg configs. */
exports.aryRearg = {
  '2': [1, 0],
  '3': [2, 0, 1],
  '4': [3, 2, 0, 1]
};

/** Used to map method names to their iteratee ary. */
exports.iterateeAry = {
  'dropRightWhile': 1,
  'dropWhile': 1,
  'every': 1,
  'filter': 1,
  'find': 1,
  'findFrom': 1,
  'findIndex': 1,
  'findIndexFrom': 1,
  'findKey': 1,
  'findLast': 1,
  'findLastFrom': 1,
  'findLastIndex': 1,
  'findLastIndexFrom': 1,
  'findLastKey': 1,
  'flatMap': 1,
  'flatMapDeep': 1,
  'flatMapDepth': 1,
  'forEach': 1,
  'forEachRight': 1,
  'forIn': 1,
  'forInRight': 1,
  'forOwn': 1,
  'forOwnRight': 1,
  'map': 1,
  'mapKeys': 1,
  'mapValues': 1,
  'partition': 1,
  'reduce': 2,
  'reduceRight': 2,
  'reject': 1,
  'remove': 1,
  'some': 1,
  'takeRightWhile': 1,
  'takeWhile': 1,
  'times': 1,
  'transform': 2
};

/** Used to map method names to iteratee rearg configs. */
exports.iterateeRearg = {
  'mapKeys': [1],
  'reduceRight': [1, 0]
};

/** Used to map method names to rearg configs. */
exports.methodRearg = {
  'assignInAllWith': [1, 0],
  'assignInWith': [1, 2, 0],
  'assignAllWith': [1, 0],
  'assignWith': [1, 2, 0],
  'differenceBy': [1, 2, 0],
  'differenceWith': [1, 2, 0],
  'getOr': [2, 1, 0],
  'intersectionBy': [1, 2, 0],
  'intersectionWith': [1, 2, 0],
  'isEqualWith': [1, 2, 0],
  'isMatchWith': [2, 1, 0],
  'mergeAllWith': [1, 0],
  'mergeWith': [1, 2, 0],
  'padChars': [2, 1, 0],
  'padCharsEnd': [2, 1, 0],
  'padCharsStart': [2, 1, 0],
  'pullAllBy': [2, 1, 0],
  'pullAllWith': [2, 1, 0],
  'rangeStep': [1, 2, 0],
  'rangeStepRight': [1, 2, 0],
  'setWith': [3, 1, 2, 0],
  'sortedIndexBy': [2, 1, 0],
  'sortedLastIndexBy': [2, 1, 0],
  'unionBy': [1, 2, 0],
  'unionWith': [1, 2, 0],
  'updateWith': [3, 1, 2, 0],
  'xorBy': [1, 2, 0],
  'xorWith': [1, 2, 0],
  'zipWith': [1, 2, 0]
};

/** Used to map method names to spread configs. */
exports.methodSpread = {
  'assignAll': { 'start': 0 },
  'assignAllWith': { 'start': 0 },
  'assignInAll': { 'start': 0 },
  'assignInAllWith': { 'start': 0 },
  'defaultsAll': { 'start': 0 },
  'defaultsDeepAll': { 'start': 0 },
  'invokeArgs': { 'start': 2 },
  'invokeArgsMap': { 'start': 2 },
  'mergeAll': { 'start': 0 },
  'mergeAllWith': { 'start': 0 },
  'partial': { 'start': 1 },
  'partialRight': { 'start': 1 },
  'without': { 'start': 1 },
  'zipAll': { 'start': 0 }
};

/** Used to identify methods which mutate arrays or objects. */
exports.mutate = {
  'array': {
    'fill': true,
    'pull': true,
    'pullAll': true,
    'pullAllBy': true,
    'pullAllWith': true,
    'pullAt': true,
    'remove': true,
    'reverse': true
  },
  'object': {
    'assign': true,
    'assignAll': true,
    'assignAllWith': true,
    'assignIn': true,
    'assignInAll': true,
    'assignInAllWith': true,
    'assignInWith': true,
    'assignWith': true,
    'defaults': true,
    'defaultsAll': true,
    'defaultsDeep': true,
    'defaultsDeepAll': true,
    'merge': true,
    'mergeAll': true,
    'mergeAllWith': true,
    'mergeWith': true,
  },
  'set': {
    'set': true,
    'setWith': true,
    'unset': true,
    'update': true,
    'updateWith': true
  }
};

/** Used to map real names to their aliases. */
exports.realToAlias = (function() {
  var hasOwnProperty = Object.prototype.hasOwnProperty,
      object = exports.aliasToReal,
      result = {};

  for (var key in object) {
    var value = object[key];
    if (hasOwnProperty.call(result, value)) {
      result[value].push(key);
    } else {
      result[value] = [key];
    }
  }
  return result;
}());

/** Used to map method names to other names. */
exports.remap = {
  'assignAll': 'assign',
  'assignAllWith': 'assignWith',
  'assignInAll': 'assignIn',
  'assignInAllWith': 'assignInWith',
  'curryN': 'curry',
  'curryRightN': 'curryRight',
  'defaultsAll': 'defaults',
  'defaultsDeepAll': 'defaultsDeep',
  'findFrom': 'find',
  'findIndexFrom': 'findIndex',
  'findLastFrom': 'findLast',
  'findLastIndexFrom': 'findLastIndex',
  'getOr': 'get',
  'includesFrom': 'includes',
  'indexOfFrom': 'indexOf',
  'invokeArgs': 'invoke',
  'invokeArgsMap': 'invokeMap',
  'lastIndexOfFrom': 'lastIndexOf',
  'mergeAll': 'merge',
  'mergeAllWith': 'mergeWith',
  'padChars': 'pad',
  'padCharsEnd': 'padEnd',
  'padCharsStart': 'padStart',
  'propertyOf': 'get',
  'rangeStep': 'range',
  'rangeStepRight': 'rangeRight',
  'restFrom': 'rest',
  'spreadFrom': 'spread',
  'trimChars': 'trim',
  'trimCharsEnd': 'trimEnd',
  'trimCharsStart': 'trimStart',
  'zipAll': 'zip'
};

/** Used to track methods that skip fixing their arity. */
exports.skipFixed = {
  'castArray': true,
  'flow': true,
  'flowRight': true,
  'iteratee': true,
  'mixin': true,
  'rearg': true,
  'runInContext': true
};

/** Used to track methods that skip rearranging arguments. */
exports.skipRearg = {
  'add': true,
  'assign': true,
  'assignIn': true,
  'bind': true,
  'bindKey': true,
  'concat': true,
  'difference': true,
  'divide': true,
  'eq': true,
  'gt': true,
  'gte': true,
  'isEqual': true,
  'lt': true,
  'lte': true,
  'matchesProperty': true,
  'merge': true,
  'multiply': true,
  'overArgs': true,
  'partial': true,
  'partialRight': true,
  'propertyOf': true,
  'random': true,
  'range': true,
  'rangeRight': true,
  'subtract': true,
  'zip': true,
  'zipObject': true,
  'zipObjectDeep': true
};
}(_mapping));

/**
 * The default argument placeholder value for methods.
 *
 * @type {Object}
 */

var placeholder = {};

var mapping = _mapping,
    fallbackHolder = placeholder;

/** Built-in value reference. */
var push = Array.prototype.push;

/**
 * Creates a function, with an arity of `n`, that invokes `func` with the
 * arguments it receives.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} n The arity of the new function.
 * @returns {Function} Returns the new function.
 */
function baseArity(func, n) {
  return n == 2
    ? function(a, b) { return func.apply(undefined, arguments); }
    : function(a) { return func.apply(undefined, arguments); };
}

/**
 * Creates a function that invokes `func`, with up to `n` arguments, ignoring
 * any additional arguments.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @param {number} n The arity cap.
 * @returns {Function} Returns the new function.
 */
function baseAry(func, n) {
  return n == 2
    ? function(a, b) { return func(a, b); }
    : function(a) { return func(a); };
}

/**
 * Creates a clone of `array`.
 *
 * @private
 * @param {Array} array The array to clone.
 * @returns {Array} Returns the cloned array.
 */
function cloneArray(array) {
  var length = array ? array.length : 0,
      result = Array(length);

  while (length--) {
    result[length] = array[length];
  }
  return result;
}

/**
 * Creates a function that clones a given object using the assignment `func`.
 *
 * @private
 * @param {Function} func The assignment function.
 * @returns {Function} Returns the new cloner function.
 */
function createCloner(func) {
  return function(object) {
    return func({}, object);
  };
}

/**
 * A specialized version of `_.spread` which flattens the spread array into
 * the arguments of the invoked `func`.
 *
 * @private
 * @param {Function} func The function to spread arguments over.
 * @param {number} start The start position of the spread.
 * @returns {Function} Returns the new function.
 */
function flatSpread(func, start) {
  return function() {
    var length = arguments.length,
        lastIndex = length - 1,
        args = Array(length);

    while (length--) {
      args[length] = arguments[length];
    }
    var array = args[start],
        otherArgs = args.slice(0, start);

    if (array) {
      push.apply(otherArgs, array);
    }
    if (start != lastIndex) {
      push.apply(otherArgs, args.slice(start + 1));
    }
    return func.apply(this, otherArgs);
  };
}

/**
 * Creates a function that wraps `func` and uses `cloner` to clone the first
 * argument it receives.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} cloner The function to clone arguments.
 * @returns {Function} Returns the new immutable function.
 */
function wrapImmutable(func, cloner) {
  return function() {
    var length = arguments.length;
    if (!length) {
      return;
    }
    var args = Array(length);
    while (length--) {
      args[length] = arguments[length];
    }
    var result = args[0] = cloner.apply(undefined, args);
    func.apply(undefined, args);
    return result;
  };
}

/**
 * The base implementation of `convert` which accepts a `util` object of methods
 * required to perform conversions.
 *
 * @param {Object} util The util object.
 * @param {string} name The name of the function to convert.
 * @param {Function} func The function to convert.
 * @param {Object} [options] The options object.
 * @param {boolean} [options.cap=true] Specify capping iteratee arguments.
 * @param {boolean} [options.curry=true] Specify currying.
 * @param {boolean} [options.fixed=true] Specify fixed arity.
 * @param {boolean} [options.immutable=true] Specify immutable operations.
 * @param {boolean} [options.rearg=true] Specify rearranging arguments.
 * @returns {Function|Object} Returns the converted function or object.
 */
function baseConvert$1(util, name, func, options) {
  var isLib = typeof name == 'function',
      isObj = name === Object(name);

  if (isObj) {
    options = func;
    func = name;
    name = undefined;
  }
  if (func == null) {
    throw new TypeError;
  }
  options || (options = {});

  var config = {
    'cap': 'cap' in options ? options.cap : true,
    'curry': 'curry' in options ? options.curry : true,
    'fixed': 'fixed' in options ? options.fixed : true,
    'immutable': 'immutable' in options ? options.immutable : true,
    'rearg': 'rearg' in options ? options.rearg : true
  };

  var defaultHolder = isLib ? func : fallbackHolder,
      forceCurry = ('curry' in options) && options.curry,
      forceFixed = ('fixed' in options) && options.fixed,
      forceRearg = ('rearg' in options) && options.rearg,
      pristine = isLib ? func.runInContext() : undefined;

  var helpers = isLib ? func : {
    'ary': util.ary,
    'assign': util.assign,
    'clone': util.clone,
    'curry': util.curry,
    'forEach': util.forEach,
    'isArray': util.isArray,
    'isError': util.isError,
    'isFunction': util.isFunction,
    'isWeakMap': util.isWeakMap,
    'iteratee': util.iteratee,
    'keys': util.keys,
    'rearg': util.rearg,
    'toInteger': util.toInteger,
    'toPath': util.toPath
  };

  var ary = helpers.ary,
      assign = helpers.assign,
      clone = helpers.clone,
      curry = helpers.curry,
      each = helpers.forEach,
      isArray = helpers.isArray,
      isError = helpers.isError,
      isFunction = helpers.isFunction,
      isWeakMap = helpers.isWeakMap,
      keys = helpers.keys,
      rearg = helpers.rearg,
      toInteger = helpers.toInteger,
      toPath = helpers.toPath;

  var aryMethodKeys = keys(mapping.aryMethod);

  var wrappers = {
    'castArray': function(castArray) {
      return function() {
        var value = arguments[0];
        return isArray(value)
          ? castArray(cloneArray(value))
          : castArray.apply(undefined, arguments);
      };
    },
    'iteratee': function(iteratee) {
      return function() {
        var func = arguments[0],
            arity = arguments[1],
            result = iteratee(func, arity),
            length = result.length;

        if (config.cap && typeof arity == 'number') {
          arity = arity > 2 ? (arity - 2) : 1;
          return (length && length <= arity) ? result : baseAry(result, arity);
        }
        return result;
      };
    },
    'mixin': function(mixin) {
      return function(source) {
        var func = this;
        if (!isFunction(func)) {
          return mixin(func, Object(source));
        }
        var pairs = [];
        each(keys(source), function(key) {
          if (isFunction(source[key])) {
            pairs.push([key, func.prototype[key]]);
          }
        });

        mixin(func, Object(source));

        each(pairs, function(pair) {
          var value = pair[1];
          if (isFunction(value)) {
            func.prototype[pair[0]] = value;
          } else {
            delete func.prototype[pair[0]];
          }
        });
        return func;
      };
    },
    'nthArg': function(nthArg) {
      return function(n) {
        var arity = n < 0 ? 1 : (toInteger(n) + 1);
        return curry(nthArg(n), arity);
      };
    },
    'rearg': function(rearg) {
      return function(func, indexes) {
        var arity = indexes ? indexes.length : 0;
        return curry(rearg(func, indexes), arity);
      };
    },
    'runInContext': function(runInContext) {
      return function(context) {
        return baseConvert$1(util, runInContext(context), options);
      };
    }
  };

  /*--------------------------------------------------------------------------*/

  /**
   * Casts `func` to a function with an arity capped iteratee if needed.
   *
   * @private
   * @param {string} name The name of the function to inspect.
   * @param {Function} func The function to inspect.
   * @returns {Function} Returns the cast function.
   */
  function castCap(name, func) {
    if (config.cap) {
      var indexes = mapping.iterateeRearg[name];
      if (indexes) {
        return iterateeRearg(func, indexes);
      }
      var n = !isLib && mapping.iterateeAry[name];
      if (n) {
        return iterateeAry(func, n);
      }
    }
    return func;
  }

  /**
   * Casts `func` to a curried function if needed.
   *
   * @private
   * @param {string} name The name of the function to inspect.
   * @param {Function} func The function to inspect.
   * @param {number} n The arity of `func`.
   * @returns {Function} Returns the cast function.
   */
  function castCurry(name, func, n) {
    return (forceCurry || (config.curry && n > 1))
      ? curry(func, n)
      : func;
  }

  /**
   * Casts `func` to a fixed arity function if needed.
   *
   * @private
   * @param {string} name The name of the function to inspect.
   * @param {Function} func The function to inspect.
   * @param {number} n The arity cap.
   * @returns {Function} Returns the cast function.
   */
  function castFixed(name, func, n) {
    if (config.fixed && (forceFixed || !mapping.skipFixed[name])) {
      var data = mapping.methodSpread[name],
          start = data && data.start;

      return start  === undefined ? ary(func, n) : flatSpread(func, start);
    }
    return func;
  }

  /**
   * Casts `func` to an rearged function if needed.
   *
   * @private
   * @param {string} name The name of the function to inspect.
   * @param {Function} func The function to inspect.
   * @param {number} n The arity of `func`.
   * @returns {Function} Returns the cast function.
   */
  function castRearg(name, func, n) {
    return (config.rearg && n > 1 && (forceRearg || !mapping.skipRearg[name]))
      ? rearg(func, mapping.methodRearg[name] || mapping.aryRearg[n])
      : func;
  }

  /**
   * Creates a clone of `object` by `path`.
   *
   * @private
   * @param {Object} object The object to clone.
   * @param {Array|string} path The path to clone by.
   * @returns {Object} Returns the cloned object.
   */
  function cloneByPath(object, path) {
    path = toPath(path);

    var index = -1,
        length = path.length,
        lastIndex = length - 1,
        result = clone(Object(object)),
        nested = result;

    while (nested != null && ++index < length) {
      var key = path[index],
          value = nested[key];

      if (value != null &&
          !(isFunction(value) || isError(value) || isWeakMap(value))) {
        nested[key] = clone(index == lastIndex ? value : Object(value));
      }
      nested = nested[key];
    }
    return result;
  }

  /**
   * Converts `lodash` to an immutable auto-curried iteratee-first data-last
   * version with conversion `options` applied.
   *
   * @param {Object} [options] The options object. See `baseConvert` for more details.
   * @returns {Function} Returns the converted `lodash`.
   */
  function convertLib(options) {
    return _.runInContext.convert(options)(undefined);
  }

  /**
   * Create a converter function for `func` of `name`.
   *
   * @param {string} name The name of the function to convert.
   * @param {Function} func The function to convert.
   * @returns {Function} Returns the new converter function.
   */
  function createConverter(name, func) {
    var realName = mapping.aliasToReal[name] || name,
        methodName = mapping.remap[realName] || realName,
        oldOptions = options;

    return function(options) {
      var newUtil = isLib ? pristine : helpers,
          newFunc = isLib ? pristine[methodName] : func,
          newOptions = assign(assign({}, oldOptions), options);

      return baseConvert$1(newUtil, realName, newFunc, newOptions);
    };
  }

  /**
   * Creates a function that wraps `func` to invoke its iteratee, with up to `n`
   * arguments, ignoring any additional arguments.
   *
   * @private
   * @param {Function} func The function to cap iteratee arguments for.
   * @param {number} n The arity cap.
   * @returns {Function} Returns the new function.
   */
  function iterateeAry(func, n) {
    return overArg(func, function(func) {
      return typeof func == 'function' ? baseAry(func, n) : func;
    });
  }

  /**
   * Creates a function that wraps `func` to invoke its iteratee with arguments
   * arranged according to the specified `indexes` where the argument value at
   * the first index is provided as the first argument, the argument value at
   * the second index is provided as the second argument, and so on.
   *
   * @private
   * @param {Function} func The function to rearrange iteratee arguments for.
   * @param {number[]} indexes The arranged argument indexes.
   * @returns {Function} Returns the new function.
   */
  function iterateeRearg(func, indexes) {
    return overArg(func, function(func) {
      var n = indexes.length;
      return baseArity(rearg(baseAry(func, n), indexes), n);
    });
  }

  /**
   * Creates a function that invokes `func` with its first argument transformed.
   *
   * @private
   * @param {Function} func The function to wrap.
   * @param {Function} transform The argument transform.
   * @returns {Function} Returns the new function.
   */
  function overArg(func, transform) {
    return function() {
      var length = arguments.length;
      if (!length) {
        return func();
      }
      var args = Array(length);
      while (length--) {
        args[length] = arguments[length];
      }
      var index = config.rearg ? 0 : (length - 1);
      args[index] = transform(args[index]);
      return func.apply(undefined, args);
    };
  }

  /**
   * Creates a function that wraps `func` and applys the conversions
   * rules by `name`.
   *
   * @private
   * @param {string} name The name of the function to wrap.
   * @param {Function} func The function to wrap.
   * @returns {Function} Returns the converted function.
   */
  function wrap(name, func, placeholder) {
    var result,
        realName = mapping.aliasToReal[name] || name,
        wrapped = func,
        wrapper = wrappers[realName];

    if (wrapper) {
      wrapped = wrapper(func);
    }
    else if (config.immutable) {
      if (mapping.mutate.array[realName]) {
        wrapped = wrapImmutable(func, cloneArray);
      }
      else if (mapping.mutate.object[realName]) {
        wrapped = wrapImmutable(func, createCloner(func));
      }
      else if (mapping.mutate.set[realName]) {
        wrapped = wrapImmutable(func, cloneByPath);
      }
    }
    each(aryMethodKeys, function(aryKey) {
      each(mapping.aryMethod[aryKey], function(otherName) {
        if (realName == otherName) {
          var data = mapping.methodSpread[realName],
              afterRearg = data && data.afterRearg;

          result = afterRearg
            ? castFixed(realName, castRearg(realName, wrapped, aryKey), aryKey)
            : castRearg(realName, castFixed(realName, wrapped, aryKey), aryKey);

          result = castCap(realName, result);
          result = castCurry(realName, result, aryKey);
          return false;
        }
      });
      return !result;
    });

    result || (result = wrapped);
    if (result == func) {
      result = forceCurry ? curry(result, 1) : function() {
        return func.apply(this, arguments);
      };
    }
    result.convert = createConverter(realName, func);
    result.placeholder = func.placeholder = placeholder;

    return result;
  }

  /*--------------------------------------------------------------------------*/

  if (!isObj) {
    return wrap(name, func, defaultHolder);
  }
  var _ = func;

  // Convert methods by ary cap.
  var pairs = [];
  each(aryMethodKeys, function(aryKey) {
    each(mapping.aryMethod[aryKey], function(key) {
      var func = _[mapping.remap[key] || key];
      if (func) {
        pairs.push([key, wrap(key, func, _)]);
      }
    });
  });

  // Convert remaining methods.
  each(keys(_), function(key) {
    var func = _[key];
    if (typeof func == 'function') {
      var length = pairs.length;
      while (length--) {
        if (pairs[length][0] == key) {
          return;
        }
      }
      func.convert = createConverter(key, func);
      pairs.push([key, func]);
    }
  });

  // Assign to `_` leaving `_.prototype` unchanged to allow chaining.
  each(pairs, function(pair) {
    _[pair[0]] = pair[1];
  });

  _.convert = convertLib;
  _.placeholder = _;

  // Assign aliases.
  each(keys(_), function(key) {
    each(mapping.realToAlias[key] || [], function(alias) {
      _[alias] = _[key];
    });
  });

  return _;
}

var _baseConvert = baseConvert$1;

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */

function identity$5(value) {
  return value;
}

var identity_1 = identity$5;

/** Detect free variable `global` from Node.js. */

var freeGlobal$1 = typeof commonjsGlobal == 'object' && commonjsGlobal && commonjsGlobal.Object === Object && commonjsGlobal;

var _freeGlobal = freeGlobal$1;

var freeGlobal = _freeGlobal;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root$c = freeGlobal || freeSelf || Function('return this')();

var _root = root$c;

var root$b = _root;

/** Built-in value references. */
var Symbol$7 = root$b.Symbol;

var _Symbol = Symbol$7;

var Symbol$6 = _Symbol;

/** Used for built-in method references. */
var objectProto$h = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$e = objectProto$h.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$h.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$6 ? Symbol$6.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag$1(value) {
  var isOwn = hasOwnProperty$e.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

var _getRawTag = getRawTag$1;

/** Used for built-in method references. */

var objectProto$g = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$g.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString$1(value) {
  return nativeObjectToString.call(value);
}

var _objectToString = objectToString$1;

var Symbol$5 = _Symbol,
    getRawTag = _getRawTag,
    objectToString = _objectToString;

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol$5 ? Symbol$5.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag$7(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

var _baseGetTag = baseGetTag$7;

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */

function isObject$b(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

var isObject_1 = isObject$b;

var baseGetTag$6 = _baseGetTag,
    isObject$a = isObject_1;

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag$2 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction$3(value) {
  if (!isObject$a(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = baseGetTag$6(value);
  return tag == funcTag$2 || tag == genTag$1 || tag == asyncTag || tag == proxyTag;
}

var isFunction_1 = isFunction$3;

var root$a = _root;

/** Used to detect overreaching core-js shims. */
var coreJsData$1 = root$a['__core-js_shared__'];

var _coreJsData = coreJsData$1;

var coreJsData = _coreJsData;

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked$1(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

var _isMasked = isMasked$1;

/** Used for built-in method references. */

var funcProto$2 = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$2 = funcProto$2.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource$2(func) {
  if (func != null) {
    try {
      return funcToString$2.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

var _toSource = toSource$2;

var isFunction$2 = isFunction_1,
    isMasked = _isMasked,
    isObject$9 = isObject_1,
    toSource$1 = _toSource;

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$f = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$d = objectProto$f.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$d).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative$1(value) {
  if (!isObject$9(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction$2(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource$1(value));
}

var _baseIsNative = baseIsNative$1;

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */

function getValue$1(object, key) {
  return object == null ? undefined : object[key];
}

var _getValue = getValue$1;

var baseIsNative = _baseIsNative,
    getValue = _getValue;

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative$7(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

var _getNative = getNative$7;

var getNative$6 = _getNative,
    root$9 = _root;

/* Built-in method references that are verified to be native. */
var WeakMap$3 = getNative$6(root$9, 'WeakMap');

var _WeakMap = WeakMap$3;

var WeakMap$2 = _WeakMap;

/** Used to store function metadata. */
var metaMap$2 = WeakMap$2 && new WeakMap$2;

var _metaMap = metaMap$2;

var identity$4 = identity_1,
    metaMap$1 = _metaMap;

/**
 * The base implementation of `setData` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to associate metadata with.
 * @param {*} data The metadata.
 * @returns {Function} Returns `func`.
 */
var baseSetData$2 = !metaMap$1 ? identity$4 : function(func, data) {
  metaMap$1.set(func, data);
  return func;
};

var _baseSetData = baseSetData$2;

var isObject$8 = isObject_1;

/** Built-in value references. */
var objectCreate = Object.create;

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate$4 = (function() {
  function object() {}
  return function(proto) {
    if (!isObject$8(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

var _baseCreate = baseCreate$4;

var baseCreate$3 = _baseCreate,
    isObject$7 = isObject_1;

/**
 * Creates a function that produces an instance of `Ctor` regardless of
 * whether it was invoked as part of a `new` expression or by `call` or `apply`.
 *
 * @private
 * @param {Function} Ctor The constructor to wrap.
 * @returns {Function} Returns the new wrapped function.
 */
function createCtor$4(Ctor) {
  return function() {
    // Use a `switch` statement to work with class constructors. See
    // http://ecma-international.org/ecma-262/7.0/#sec-ecmascript-function-objects-call-thisargument-argumentslist
    // for more details.
    var args = arguments;
    switch (args.length) {
      case 0: return new Ctor;
      case 1: return new Ctor(args[0]);
      case 2: return new Ctor(args[0], args[1]);
      case 3: return new Ctor(args[0], args[1], args[2]);
      case 4: return new Ctor(args[0], args[1], args[2], args[3]);
      case 5: return new Ctor(args[0], args[1], args[2], args[3], args[4]);
      case 6: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5]);
      case 7: return new Ctor(args[0], args[1], args[2], args[3], args[4], args[5], args[6]);
    }
    var thisBinding = baseCreate$3(Ctor.prototype),
        result = Ctor.apply(thisBinding, args);

    // Mimic the constructor's `return` behavior.
    // See https://es5.github.io/#x13.2.2 for more details.
    return isObject$7(result) ? result : thisBinding;
  };
}

var _createCtor = createCtor$4;

var createCtor$3 = _createCtor,
    root$8 = _root;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$6 = 1;

/**
 * Creates a function that wraps `func` to invoke it with the optional `this`
 * binding of `thisArg`.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createBind$1(func, bitmask, thisArg) {
  var isBind = bitmask & WRAP_BIND_FLAG$6,
      Ctor = createCtor$3(func);

  function wrapper() {
    var fn = (this && this !== root$8 && this instanceof wrapper) ? Ctor : func;
    return fn.apply(isBind ? thisArg : this, arguments);
  }
  return wrapper;
}

var _createBind = createBind$1;

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */

function apply$3(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

var _apply = apply$3;

/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeMax$3 = Math.max;

/**
 * Creates an array that is the composition of partially applied arguments,
 * placeholders, and provided arguments into a single array of arguments.
 *
 * @private
 * @param {Array} args The provided arguments.
 * @param {Array} partials The arguments to prepend to those provided.
 * @param {Array} holders The `partials` placeholder indexes.
 * @params {boolean} [isCurried] Specify composing for a curried function.
 * @returns {Array} Returns the new array of composed arguments.
 */
function composeArgs$2(args, partials, holders, isCurried) {
  var argsIndex = -1,
      argsLength = args.length,
      holdersLength = holders.length,
      leftIndex = -1,
      leftLength = partials.length,
      rangeLength = nativeMax$3(argsLength - holdersLength, 0),
      result = Array(leftLength + rangeLength),
      isUncurried = !isCurried;

  while (++leftIndex < leftLength) {
    result[leftIndex] = partials[leftIndex];
  }
  while (++argsIndex < holdersLength) {
    if (isUncurried || argsIndex < argsLength) {
      result[holders[argsIndex]] = args[argsIndex];
    }
  }
  while (rangeLength--) {
    result[leftIndex++] = args[argsIndex++];
  }
  return result;
}

var _composeArgs = composeArgs$2;

/* Built-in method references for those with the same name as other `lodash` methods. */

var nativeMax$2 = Math.max;

/**
 * This function is like `composeArgs` except that the arguments composition
 * is tailored for `_.partialRight`.
 *
 * @private
 * @param {Array} args The provided arguments.
 * @param {Array} partials The arguments to append to those provided.
 * @param {Array} holders The `partials` placeholder indexes.
 * @params {boolean} [isCurried] Specify composing for a curried function.
 * @returns {Array} Returns the new array of composed arguments.
 */
function composeArgsRight$2(args, partials, holders, isCurried) {
  var argsIndex = -1,
      argsLength = args.length,
      holdersIndex = -1,
      holdersLength = holders.length,
      rightIndex = -1,
      rightLength = partials.length,
      rangeLength = nativeMax$2(argsLength - holdersLength, 0),
      result = Array(rangeLength + rightLength),
      isUncurried = !isCurried;

  while (++argsIndex < rangeLength) {
    result[argsIndex] = args[argsIndex];
  }
  var offset = argsIndex;
  while (++rightIndex < rightLength) {
    result[offset + rightIndex] = partials[rightIndex];
  }
  while (++holdersIndex < holdersLength) {
    if (isUncurried || argsIndex < argsLength) {
      result[offset + holders[holdersIndex]] = args[argsIndex++];
    }
  }
  return result;
}

var _composeArgsRight = composeArgsRight$2;

/**
 * Gets the number of `placeholder` occurrences in `array`.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} placeholder The placeholder to search for.
 * @returns {number} Returns the placeholder count.
 */

function countHolders$1(array, placeholder) {
  var length = array.length,
      result = 0;

  while (length--) {
    if (array[length] === placeholder) {
      ++result;
    }
  }
  return result;
}

var _countHolders = countHolders$1;

/**
 * The function whose prototype chain sequence wrappers inherit from.
 *
 * @private
 */

function baseLodash$3() {
  // No operation performed.
}

var _baseLodash = baseLodash$3;

var baseCreate$2 = _baseCreate,
    baseLodash$2 = _baseLodash;

/** Used as references for the maximum length and index of an array. */
var MAX_ARRAY_LENGTH = 4294967295;

/**
 * Creates a lazy wrapper object which wraps `value` to enable lazy evaluation.
 *
 * @private
 * @constructor
 * @param {*} value The value to wrap.
 */
function LazyWrapper$3(value) {
  this.__wrapped__ = value;
  this.__actions__ = [];
  this.__dir__ = 1;
  this.__filtered__ = false;
  this.__iteratees__ = [];
  this.__takeCount__ = MAX_ARRAY_LENGTH;
  this.__views__ = [];
}

// Ensure `LazyWrapper` is an instance of `baseLodash`.
LazyWrapper$3.prototype = baseCreate$2(baseLodash$2.prototype);
LazyWrapper$3.prototype.constructor = LazyWrapper$3;

var _LazyWrapper = LazyWrapper$3;

/**
 * This method returns `undefined`.
 *
 * @static
 * @memberOf _
 * @since 2.3.0
 * @category Util
 * @example
 *
 * _.times(2, _.noop);
 * // => [undefined, undefined]
 */

function noop$2() {
  // No operation performed.
}

var noop_1 = noop$2;

var metaMap = _metaMap,
    noop$1 = noop_1;

/**
 * Gets metadata for `func`.
 *
 * @private
 * @param {Function} func The function to query.
 * @returns {*} Returns the metadata for `func`.
 */
var getData$3 = !metaMap ? noop$1 : function(func) {
  return metaMap.get(func);
};

var _getData = getData$3;

/** Used to lookup unminified function names. */

var realNames$1 = {};

var _realNames = realNames$1;

var realNames = _realNames;

/** Used for built-in method references. */
var objectProto$e = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$c = objectProto$e.hasOwnProperty;

/**
 * Gets the name of `func`.
 *
 * @private
 * @param {Function} func The function to query.
 * @returns {string} Returns the function name.
 */
function getFuncName$2(func) {
  var result = (func.name + ''),
      array = realNames[result],
      length = hasOwnProperty$c.call(realNames, result) ? array.length : 0;

  while (length--) {
    var data = array[length],
        otherFunc = data.func;
    if (otherFunc == null || otherFunc == func) {
      return data.name;
    }
  }
  return result;
}

var _getFuncName = getFuncName$2;

var baseCreate$1 = _baseCreate,
    baseLodash$1 = _baseLodash;

/**
 * The base constructor for creating `lodash` wrapper objects.
 *
 * @private
 * @param {*} value The value to wrap.
 * @param {boolean} [chainAll] Enable explicit method chain sequences.
 */
function LodashWrapper$3(value, chainAll) {
  this.__wrapped__ = value;
  this.__actions__ = [];
  this.__chain__ = !!chainAll;
  this.__index__ = 0;
  this.__values__ = undefined;
}

LodashWrapper$3.prototype = baseCreate$1(baseLodash$1.prototype);
LodashWrapper$3.prototype.constructor = LodashWrapper$3;

var _LodashWrapper = LodashWrapper$3;

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */

var isArray$f = Array.isArray;

var isArray_1 = isArray$f;

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */

function isObjectLike$c(value) {
  return value != null && typeof value == 'object';
}

var isObjectLike_1 = isObjectLike$c;

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */

function copyArray$5(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

var _copyArray = copyArray$5;

var LazyWrapper$2 = _LazyWrapper,
    LodashWrapper$2 = _LodashWrapper,
    copyArray$4 = _copyArray;

/**
 * Creates a clone of `wrapper`.
 *
 * @private
 * @param {Object} wrapper The wrapper to clone.
 * @returns {Object} Returns the cloned wrapper.
 */
function wrapperClone$1(wrapper) {
  if (wrapper instanceof LazyWrapper$2) {
    return wrapper.clone();
  }
  var result = new LodashWrapper$2(wrapper.__wrapped__, wrapper.__chain__);
  result.__actions__ = copyArray$4(wrapper.__actions__);
  result.__index__  = wrapper.__index__;
  result.__values__ = wrapper.__values__;
  return result;
}

var _wrapperClone = wrapperClone$1;

var LazyWrapper$1 = _LazyWrapper,
    LodashWrapper$1 = _LodashWrapper,
    baseLodash = _baseLodash,
    isArray$e = isArray_1,
    isObjectLike$b = isObjectLike_1,
    wrapperClone = _wrapperClone;

/** Used for built-in method references. */
var objectProto$d = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$b = objectProto$d.hasOwnProperty;

/**
 * Creates a `lodash` object which wraps `value` to enable implicit method
 * chain sequences. Methods that operate on and return arrays, collections,
 * and functions can be chained together. Methods that retrieve a single value
 * or may return a primitive value will automatically end the chain sequence
 * and return the unwrapped value. Otherwise, the value must be unwrapped
 * with `_#value`.
 *
 * Explicit chain sequences, which must be unwrapped with `_#value`, may be
 * enabled using `_.chain`.
 *
 * The execution of chained methods is lazy, that is, it's deferred until
 * `_#value` is implicitly or explicitly called.
 *
 * Lazy evaluation allows several methods to support shortcut fusion.
 * Shortcut fusion is an optimization to merge iteratee calls; this avoids
 * the creation of intermediate arrays and can greatly reduce the number of
 * iteratee executions. Sections of a chain sequence qualify for shortcut
 * fusion if the section is applied to an array and iteratees accept only
 * one argument. The heuristic for whether a section qualifies for shortcut
 * fusion is subject to change.
 *
 * Chaining is supported in custom builds as long as the `_#value` method is
 * directly or indirectly included in the build.
 *
 * In addition to lodash methods, wrappers have `Array` and `String` methods.
 *
 * The wrapper `Array` methods are:
 * `concat`, `join`, `pop`, `push`, `shift`, `sort`, `splice`, and `unshift`
 *
 * The wrapper `String` methods are:
 * `replace` and `split`
 *
 * The wrapper methods that support shortcut fusion are:
 * `at`, `compact`, `drop`, `dropRight`, `dropWhile`, `filter`, `find`,
 * `findLast`, `head`, `initial`, `last`, `map`, `reject`, `reverse`, `slice`,
 * `tail`, `take`, `takeRight`, `takeRightWhile`, `takeWhile`, and `toArray`
 *
 * The chainable wrapper methods are:
 * `after`, `ary`, `assign`, `assignIn`, `assignInWith`, `assignWith`, `at`,
 * `before`, `bind`, `bindAll`, `bindKey`, `castArray`, `chain`, `chunk`,
 * `commit`, `compact`, `concat`, `conforms`, `constant`, `countBy`, `create`,
 * `curry`, `debounce`, `defaults`, `defaultsDeep`, `defer`, `delay`,
 * `difference`, `differenceBy`, `differenceWith`, `drop`, `dropRight`,
 * `dropRightWhile`, `dropWhile`, `extend`, `extendWith`, `fill`, `filter`,
 * `flatMap`, `flatMapDeep`, `flatMapDepth`, `flatten`, `flattenDeep`,
 * `flattenDepth`, `flip`, `flow`, `flowRight`, `fromPairs`, `functions`,
 * `functionsIn`, `groupBy`, `initial`, `intersection`, `intersectionBy`,
 * `intersectionWith`, `invert`, `invertBy`, `invokeMap`, `iteratee`, `keyBy`,
 * `keys`, `keysIn`, `map`, `mapKeys`, `mapValues`, `matches`, `matchesProperty`,
 * `memoize`, `merge`, `mergeWith`, `method`, `methodOf`, `mixin`, `negate`,
 * `nthArg`, `omit`, `omitBy`, `once`, `orderBy`, `over`, `overArgs`,
 * `overEvery`, `overSome`, `partial`, `partialRight`, `partition`, `pick`,
 * `pickBy`, `plant`, `property`, `propertyOf`, `pull`, `pullAll`, `pullAllBy`,
 * `pullAllWith`, `pullAt`, `push`, `range`, `rangeRight`, `rearg`, `reject`,
 * `remove`, `rest`, `reverse`, `sampleSize`, `set`, `setWith`, `shuffle`,
 * `slice`, `sort`, `sortBy`, `splice`, `spread`, `tail`, `take`, `takeRight`,
 * `takeRightWhile`, `takeWhile`, `tap`, `throttle`, `thru`, `toArray`,
 * `toPairs`, `toPairsIn`, `toPath`, `toPlainObject`, `transform`, `unary`,
 * `union`, `unionBy`, `unionWith`, `uniq`, `uniqBy`, `uniqWith`, `unset`,
 * `unshift`, `unzip`, `unzipWith`, `update`, `updateWith`, `values`,
 * `valuesIn`, `without`, `wrap`, `xor`, `xorBy`, `xorWith`, `zip`,
 * `zipObject`, `zipObjectDeep`, and `zipWith`
 *
 * The wrapper methods that are **not** chainable by default are:
 * `add`, `attempt`, `camelCase`, `capitalize`, `ceil`, `clamp`, `clone`,
 * `cloneDeep`, `cloneDeepWith`, `cloneWith`, `conformsTo`, `deburr`,
 * `defaultTo`, `divide`, `each`, `eachRight`, `endsWith`, `eq`, `escape`,
 * `escapeRegExp`, `every`, `find`, `findIndex`, `findKey`, `findLast`,
 * `findLastIndex`, `findLastKey`, `first`, `floor`, `forEach`, `forEachRight`,
 * `forIn`, `forInRight`, `forOwn`, `forOwnRight`, `get`, `gt`, `gte`, `has`,
 * `hasIn`, `head`, `identity`, `includes`, `indexOf`, `inRange`, `invoke`,
 * `isArguments`, `isArray`, `isArrayBuffer`, `isArrayLike`, `isArrayLikeObject`,
 * `isBoolean`, `isBuffer`, `isDate`, `isElement`, `isEmpty`, `isEqual`,
 * `isEqualWith`, `isError`, `isFinite`, `isFunction`, `isInteger`, `isLength`,
 * `isMap`, `isMatch`, `isMatchWith`, `isNaN`, `isNative`, `isNil`, `isNull`,
 * `isNumber`, `isObject`, `isObjectLike`, `isPlainObject`, `isRegExp`,
 * `isSafeInteger`, `isSet`, `isString`, `isUndefined`, `isTypedArray`,
 * `isWeakMap`, `isWeakSet`, `join`, `kebabCase`, `last`, `lastIndexOf`,
 * `lowerCase`, `lowerFirst`, `lt`, `lte`, `max`, `maxBy`, `mean`, `meanBy`,
 * `min`, `minBy`, `multiply`, `noConflict`, `noop`, `now`, `nth`, `pad`,
 * `padEnd`, `padStart`, `parseInt`, `pop`, `random`, `reduce`, `reduceRight`,
 * `repeat`, `result`, `round`, `runInContext`, `sample`, `shift`, `size`,
 * `snakeCase`, `some`, `sortedIndex`, `sortedIndexBy`, `sortedLastIndex`,
 * `sortedLastIndexBy`, `startCase`, `startsWith`, `stubArray`, `stubFalse`,
 * `stubObject`, `stubString`, `stubTrue`, `subtract`, `sum`, `sumBy`,
 * `template`, `times`, `toFinite`, `toInteger`, `toJSON`, `toLength`,
 * `toLower`, `toNumber`, `toSafeInteger`, `toString`, `toUpper`, `trim`,
 * `trimEnd`, `trimStart`, `truncate`, `unescape`, `uniqueId`, `upperCase`,
 * `upperFirst`, `value`, and `words`
 *
 * @name _
 * @constructor
 * @category Seq
 * @param {*} value The value to wrap in a `lodash` instance.
 * @returns {Object} Returns the new `lodash` wrapper instance.
 * @example
 *
 * function square(n) {
 *   return n * n;
 * }
 *
 * var wrapped = _([1, 2, 3]);
 *
 * // Returns an unwrapped value.
 * wrapped.reduce(_.add);
 * // => 6
 *
 * // Returns a wrapped value.
 * var squares = wrapped.map(square);
 *
 * _.isArray(squares);
 * // => false
 *
 * _.isArray(squares.value());
 * // => true
 */
function lodash$1(value) {
  if (isObjectLike$b(value) && !isArray$e(value) && !(value instanceof LazyWrapper$1)) {
    if (value instanceof LodashWrapper$1) {
      return value;
    }
    if (hasOwnProperty$b.call(value, '__wrapped__')) {
      return wrapperClone(value);
    }
  }
  return new LodashWrapper$1(value);
}

// Ensure wrappers are instances of `baseLodash`.
lodash$1.prototype = baseLodash.prototype;
lodash$1.prototype.constructor = lodash$1;

var wrapperLodash = lodash$1;

var LazyWrapper = _LazyWrapper,
    getData$2 = _getData,
    getFuncName$1 = _getFuncName,
    lodash = wrapperLodash;

/**
 * Checks if `func` has a lazy counterpart.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` has a lazy counterpart,
 *  else `false`.
 */
function isLaziable$2(func) {
  var funcName = getFuncName$1(func),
      other = lodash[funcName];

  if (typeof other != 'function' || !(funcName in LazyWrapper.prototype)) {
    return false;
  }
  if (func === other) {
    return true;
  }
  var data = getData$2(other);
  return !!data && func === data[0];
}

var _isLaziable = isLaziable$2;

/** Used to detect hot functions by number of calls within a span of milliseconds. */

var HOT_COUNT = 800,
    HOT_SPAN = 16;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut$2(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

var _shortOut = shortOut$2;

var baseSetData$1 = _baseSetData,
    shortOut$1 = _shortOut;

/**
 * Sets metadata for `func`.
 *
 * **Note:** If this function becomes hot, i.e. is invoked a lot in a short
 * period of time, it will trip its breaker and transition to an identity
 * function to avoid garbage collection pauses in V8. See
 * [V8 issue 2070](https://bugs.chromium.org/p/v8/issues/detail?id=2070)
 * for more details.
 *
 * @private
 * @param {Function} func The function to associate metadata with.
 * @param {*} data The metadata.
 * @returns {Function} Returns `func`.
 */
var setData$2 = shortOut$1(baseSetData$1);

var _setData = setData$2;

/** Used to match wrap detail comments. */

var reWrapDetails = /\{\n\/\* \[wrapped with (.+)\] \*/,
    reSplitDetails = /,? & /;

/**
 * Extracts wrapper details from the `source` body comment.
 *
 * @private
 * @param {string} source The source to inspect.
 * @returns {Array} Returns the wrapper details.
 */
function getWrapDetails$1(source) {
  var match = source.match(reWrapDetails);
  return match ? match[1].split(reSplitDetails) : [];
}

var _getWrapDetails = getWrapDetails$1;

/** Used to match wrap detail comments. */

var reWrapComment = /\{(?:\n\/\* \[wrapped with .+\] \*\/)?\n?/;

/**
 * Inserts wrapper `details` in a comment at the top of the `source` body.
 *
 * @private
 * @param {string} source The source to modify.
 * @returns {Array} details The details to insert.
 * @returns {string} Returns the modified source.
 */
function insertWrapDetails$1(source, details) {
  var length = details.length;
  if (!length) {
    return source;
  }
  var lastIndex = length - 1;
  details[lastIndex] = (length > 1 ? '& ' : '') + details[lastIndex];
  details = details.join(length > 2 ? ', ' : ' ');
  return source.replace(reWrapComment, '{\n/* [wrapped with ' + details + '] */\n');
}

var _insertWrapDetails = insertWrapDetails$1;

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */

function constant$1(value) {
  return function() {
    return value;
  };
}

var constant_1 = constant$1;

var getNative$5 = _getNative;

var defineProperty$2 = (function() {
  try {
    var func = getNative$5(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

var _defineProperty = defineProperty$2;

var constant = constant_1,
    defineProperty$1 = _defineProperty,
    identity$3 = identity_1;

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString$1 = !defineProperty$1 ? identity$3 : function(func, string) {
  return defineProperty$1(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant(string),
    'writable': true
  });
};

var _baseSetToString = baseSetToString$1;

var baseSetToString = _baseSetToString,
    shortOut = _shortOut;

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString$3 = shortOut(baseSetToString);

var _setToString = setToString$3;

/**
 * A specialized version of `_.forEach` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns `array`.
 */

function arrayEach$2(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }
  return array;
}

var _arrayEach = arrayEach$2;

/**
 * The base implementation of `_.findIndex` and `_.findLastIndex` without
 * support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} predicate The function invoked per iteration.
 * @param {number} fromIndex The index to search from.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */

function baseFindIndex$1(array, predicate, fromIndex, fromRight) {
  var length = array.length,
      index = fromIndex + (fromRight ? 1 : -1);

  while ((fromRight ? index-- : ++index < length)) {
    if (predicate(array[index], index, array)) {
      return index;
    }
  }
  return -1;
}

var _baseFindIndex = baseFindIndex$1;

/**
 * The base implementation of `_.isNaN` without support for number objects.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is `NaN`, else `false`.
 */

function baseIsNaN$1(value) {
  return value !== value;
}

var _baseIsNaN = baseIsNaN$1;

/**
 * A specialized version of `_.indexOf` which performs strict equality
 * comparisons of values, i.e. `===`.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */

function strictIndexOf$1(array, value, fromIndex) {
  var index = fromIndex - 1,
      length = array.length;

  while (++index < length) {
    if (array[index] === value) {
      return index;
    }
  }
  return -1;
}

var _strictIndexOf = strictIndexOf$1;

var baseFindIndex = _baseFindIndex,
    baseIsNaN = _baseIsNaN,
    strictIndexOf = _strictIndexOf;

/**
 * The base implementation of `_.indexOf` without `fromIndex` bounds checks.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} value The value to search for.
 * @param {number} fromIndex The index to search from.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function baseIndexOf$1(array, value, fromIndex) {
  return value === value
    ? strictIndexOf(array, value, fromIndex)
    : baseFindIndex(array, baseIsNaN, fromIndex);
}

var _baseIndexOf = baseIndexOf$1;

var baseIndexOf = _baseIndexOf;

/**
 * A specialized version of `_.includes` for arrays without support for
 * specifying an index to search from.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */
function arrayIncludes$2(array, value) {
  var length = array == null ? 0 : array.length;
  return !!length && baseIndexOf(array, value, 0) > -1;
}

var _arrayIncludes = arrayIncludes$2;

var arrayEach$1 = _arrayEach,
    arrayIncludes$1 = _arrayIncludes;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$5 = 1,
    WRAP_BIND_KEY_FLAG$4 = 2,
    WRAP_CURRY_FLAG$6 = 8,
    WRAP_CURRY_RIGHT_FLAG$2 = 16,
    WRAP_PARTIAL_FLAG$3 = 32,
    WRAP_PARTIAL_RIGHT_FLAG$2 = 64,
    WRAP_ARY_FLAG$4 = 128,
    WRAP_REARG_FLAG$3 = 256,
    WRAP_FLIP_FLAG$1 = 512;

/** Used to associate wrap methods with their bit flags. */
var wrapFlags = [
  ['ary', WRAP_ARY_FLAG$4],
  ['bind', WRAP_BIND_FLAG$5],
  ['bindKey', WRAP_BIND_KEY_FLAG$4],
  ['curry', WRAP_CURRY_FLAG$6],
  ['curryRight', WRAP_CURRY_RIGHT_FLAG$2],
  ['flip', WRAP_FLIP_FLAG$1],
  ['partial', WRAP_PARTIAL_FLAG$3],
  ['partialRight', WRAP_PARTIAL_RIGHT_FLAG$2],
  ['rearg', WRAP_REARG_FLAG$3]
];

/**
 * Updates wrapper `details` based on `bitmask` flags.
 *
 * @private
 * @returns {Array} details The details to modify.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @returns {Array} Returns `details`.
 */
function updateWrapDetails$1(details, bitmask) {
  arrayEach$1(wrapFlags, function(pair) {
    var value = '_.' + pair[0];
    if ((bitmask & pair[1]) && !arrayIncludes$1(details, value)) {
      details.push(value);
    }
  });
  return details.sort();
}

var _updateWrapDetails = updateWrapDetails$1;

var getWrapDetails = _getWrapDetails,
    insertWrapDetails = _insertWrapDetails,
    setToString$2 = _setToString,
    updateWrapDetails = _updateWrapDetails;

/**
 * Sets the `toString` method of `wrapper` to mimic the source of `reference`
 * with wrapper details in a comment at the top of the source body.
 *
 * @private
 * @param {Function} wrapper The function to modify.
 * @param {Function} reference The reference function.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @returns {Function} Returns `wrapper`.
 */
function setWrapToString$2(wrapper, reference, bitmask) {
  var source = (reference + '');
  return setToString$2(wrapper, insertWrapDetails(source, updateWrapDetails(getWrapDetails(source), bitmask)));
}

var _setWrapToString = setWrapToString$2;

var isLaziable$1 = _isLaziable,
    setData$1 = _setData,
    setWrapToString$1 = _setWrapToString;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$4 = 1,
    WRAP_BIND_KEY_FLAG$3 = 2,
    WRAP_CURRY_BOUND_FLAG$1 = 4,
    WRAP_CURRY_FLAG$5 = 8,
    WRAP_PARTIAL_FLAG$2 = 32,
    WRAP_PARTIAL_RIGHT_FLAG$1 = 64;

/**
 * Creates a function that wraps `func` to continue currying.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {Function} wrapFunc The function to create the `func` wrapper.
 * @param {*} placeholder The placeholder value.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {Array} [partials] The arguments to prepend to those provided to
 *  the new function.
 * @param {Array} [holders] The `partials` placeholder indexes.
 * @param {Array} [argPos] The argument positions of the new function.
 * @param {number} [ary] The arity cap of `func`.
 * @param {number} [arity] The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createRecurry$2(func, bitmask, wrapFunc, placeholder, thisArg, partials, holders, argPos, ary, arity) {
  var isCurry = bitmask & WRAP_CURRY_FLAG$5,
      newHolders = isCurry ? holders : undefined,
      newHoldersRight = isCurry ? undefined : holders,
      newPartials = isCurry ? partials : undefined,
      newPartialsRight = isCurry ? undefined : partials;

  bitmask |= (isCurry ? WRAP_PARTIAL_FLAG$2 : WRAP_PARTIAL_RIGHT_FLAG$1);
  bitmask &= ~(isCurry ? WRAP_PARTIAL_RIGHT_FLAG$1 : WRAP_PARTIAL_FLAG$2);

  if (!(bitmask & WRAP_CURRY_BOUND_FLAG$1)) {
    bitmask &= ~(WRAP_BIND_FLAG$4 | WRAP_BIND_KEY_FLAG$3);
  }
  var newData = [
    func, bitmask, thisArg, newPartials, newHolders, newPartialsRight,
    newHoldersRight, argPos, ary, arity
  ];

  var result = wrapFunc.apply(undefined, newData);
  if (isLaziable$1(func)) {
    setData$1(result, newData);
  }
  result.placeholder = placeholder;
  return setWrapToString$1(result, func, bitmask);
}

var _createRecurry = createRecurry$2;

/**
 * Gets the argument placeholder value for `func`.
 *
 * @private
 * @param {Function} func The function to inspect.
 * @returns {*} Returns the placeholder value.
 */

function getHolder$2(func) {
  var object = func;
  return object.placeholder;
}

var _getHolder = getHolder$2;

/** Used as references for various `Number` constants. */

var MAX_SAFE_INTEGER$1 = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex$4(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER$1 : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

var _isIndex = isIndex$4;

var copyArray$3 = _copyArray,
    isIndex$3 = _isIndex;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMin$1 = Math.min;

/**
 * Reorder `array` according to the specified indexes where the element at
 * the first index is assigned as the first element, the element at
 * the second index is assigned as the second element, and so on.
 *
 * @private
 * @param {Array} array The array to reorder.
 * @param {Array} indexes The arranged array indexes.
 * @returns {Array} Returns `array`.
 */
function reorder$1(array, indexes) {
  var arrLength = array.length,
      length = nativeMin$1(indexes.length, arrLength),
      oldArray = copyArray$3(array);

  while (length--) {
    var index = indexes[length];
    array[length] = isIndex$3(index, arrLength) ? oldArray[index] : undefined;
  }
  return array;
}

var _reorder = reorder$1;

/** Used as the internal argument placeholder. */

var PLACEHOLDER$1 = '__lodash_placeholder__';

/**
 * Replaces all `placeholder` elements in `array` with an internal placeholder
 * and returns an array of their indexes.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {*} placeholder The placeholder to replace.
 * @returns {Array} Returns the new array of placeholder indexes.
 */
function replaceHolders$3(array, placeholder) {
  var index = -1,
      length = array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (value === placeholder || value === PLACEHOLDER$1) {
      array[index] = PLACEHOLDER$1;
      result[resIndex++] = index;
    }
  }
  return result;
}

var _replaceHolders = replaceHolders$3;

var composeArgs$1 = _composeArgs,
    composeArgsRight$1 = _composeArgsRight,
    countHolders = _countHolders,
    createCtor$2 = _createCtor,
    createRecurry$1 = _createRecurry,
    getHolder$1 = _getHolder,
    reorder = _reorder,
    replaceHolders$2 = _replaceHolders,
    root$7 = _root;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$3 = 1,
    WRAP_BIND_KEY_FLAG$2 = 2,
    WRAP_CURRY_FLAG$4 = 8,
    WRAP_CURRY_RIGHT_FLAG$1 = 16,
    WRAP_ARY_FLAG$3 = 128,
    WRAP_FLIP_FLAG = 512;

/**
 * Creates a function that wraps `func` to invoke it with optional `this`
 * binding of `thisArg`, partial application, and currying.
 *
 * @private
 * @param {Function|string} func The function or method name to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {Array} [partials] The arguments to prepend to those provided to
 *  the new function.
 * @param {Array} [holders] The `partials` placeholder indexes.
 * @param {Array} [partialsRight] The arguments to append to those provided
 *  to the new function.
 * @param {Array} [holdersRight] The `partialsRight` placeholder indexes.
 * @param {Array} [argPos] The argument positions of the new function.
 * @param {number} [ary] The arity cap of `func`.
 * @param {number} [arity] The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createHybrid$2(func, bitmask, thisArg, partials, holders, partialsRight, holdersRight, argPos, ary, arity) {
  var isAry = bitmask & WRAP_ARY_FLAG$3,
      isBind = bitmask & WRAP_BIND_FLAG$3,
      isBindKey = bitmask & WRAP_BIND_KEY_FLAG$2,
      isCurried = bitmask & (WRAP_CURRY_FLAG$4 | WRAP_CURRY_RIGHT_FLAG$1),
      isFlip = bitmask & WRAP_FLIP_FLAG,
      Ctor = isBindKey ? undefined : createCtor$2(func);

  function wrapper() {
    var length = arguments.length,
        args = Array(length),
        index = length;

    while (index--) {
      args[index] = arguments[index];
    }
    if (isCurried) {
      var placeholder = getHolder$1(wrapper),
          holdersCount = countHolders(args, placeholder);
    }
    if (partials) {
      args = composeArgs$1(args, partials, holders, isCurried);
    }
    if (partialsRight) {
      args = composeArgsRight$1(args, partialsRight, holdersRight, isCurried);
    }
    length -= holdersCount;
    if (isCurried && length < arity) {
      var newHolders = replaceHolders$2(args, placeholder);
      return createRecurry$1(
        func, bitmask, createHybrid$2, wrapper.placeholder, thisArg,
        args, newHolders, argPos, ary, arity - length
      );
    }
    var thisBinding = isBind ? thisArg : this,
        fn = isBindKey ? thisBinding[func] : func;

    length = args.length;
    if (argPos) {
      args = reorder(args, argPos);
    } else if (isFlip && length > 1) {
      args.reverse();
    }
    if (isAry && ary < length) {
      args.length = ary;
    }
    if (this && this !== root$7 && this instanceof wrapper) {
      fn = Ctor || createCtor$2(fn);
    }
    return fn.apply(thisBinding, args);
  }
  return wrapper;
}

var _createHybrid = createHybrid$2;

var apply$2 = _apply,
    createCtor$1 = _createCtor,
    createHybrid$1 = _createHybrid,
    createRecurry = _createRecurry,
    getHolder = _getHolder,
    replaceHolders$1 = _replaceHolders,
    root$6 = _root;

/**
 * Creates a function that wraps `func` to enable currying.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {number} arity The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createCurry$1(func, bitmask, arity) {
  var Ctor = createCtor$1(func);

  function wrapper() {
    var length = arguments.length,
        args = Array(length),
        index = length,
        placeholder = getHolder(wrapper);

    while (index--) {
      args[index] = arguments[index];
    }
    var holders = (length < 3 && args[0] !== placeholder && args[length - 1] !== placeholder)
      ? []
      : replaceHolders$1(args, placeholder);

    length -= holders.length;
    if (length < arity) {
      return createRecurry(
        func, bitmask, createHybrid$1, wrapper.placeholder, undefined,
        args, holders, undefined, undefined, arity - length);
    }
    var fn = (this && this !== root$6 && this instanceof wrapper) ? Ctor : func;
    return apply$2(fn, this, args);
  }
  return wrapper;
}

var _createCurry = createCurry$1;

var apply$1 = _apply,
    createCtor = _createCtor,
    root$5 = _root;

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$2 = 1;

/**
 * Creates a function that wraps `func` to invoke it with the `this` binding
 * of `thisArg` and `partials` prepended to the arguments it receives.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {number} bitmask The bitmask flags. See `createWrap` for more details.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} partials The arguments to prepend to those provided to
 *  the new function.
 * @returns {Function} Returns the new wrapped function.
 */
function createPartial$1(func, bitmask, thisArg, partials) {
  var isBind = bitmask & WRAP_BIND_FLAG$2,
      Ctor = createCtor(func);

  function wrapper() {
    var argsIndex = -1,
        argsLength = arguments.length,
        leftIndex = -1,
        leftLength = partials.length,
        args = Array(leftLength + argsLength),
        fn = (this && this !== root$5 && this instanceof wrapper) ? Ctor : func;

    while (++leftIndex < leftLength) {
      args[leftIndex] = partials[leftIndex];
    }
    while (argsLength--) {
      args[leftIndex++] = arguments[++argsIndex];
    }
    return apply$1(fn, isBind ? thisArg : this, args);
  }
  return wrapper;
}

var _createPartial = createPartial$1;

var composeArgs = _composeArgs,
    composeArgsRight = _composeArgsRight,
    replaceHolders = _replaceHolders;

/** Used as the internal argument placeholder. */
var PLACEHOLDER = '__lodash_placeholder__';

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG$1 = 1,
    WRAP_BIND_KEY_FLAG$1 = 2,
    WRAP_CURRY_BOUND_FLAG = 4,
    WRAP_CURRY_FLAG$3 = 8,
    WRAP_ARY_FLAG$2 = 128,
    WRAP_REARG_FLAG$2 = 256;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMin = Math.min;

/**
 * Merges the function metadata of `source` into `data`.
 *
 * Merging metadata reduces the number of wrappers used to invoke a function.
 * This is possible because methods like `_.bind`, `_.curry`, and `_.partial`
 * may be applied regardless of execution order. Methods like `_.ary` and
 * `_.rearg` modify function arguments, making the order in which they are
 * executed important, preventing the merging of metadata. However, we make
 * an exception for a safe combined case where curried functions have `_.ary`
 * and or `_.rearg` applied.
 *
 * @private
 * @param {Array} data The destination metadata.
 * @param {Array} source The source metadata.
 * @returns {Array} Returns `data`.
 */
function mergeData$1(data, source) {
  var bitmask = data[1],
      srcBitmask = source[1],
      newBitmask = bitmask | srcBitmask,
      isCommon = newBitmask < (WRAP_BIND_FLAG$1 | WRAP_BIND_KEY_FLAG$1 | WRAP_ARY_FLAG$2);

  var isCombo =
    ((srcBitmask == WRAP_ARY_FLAG$2) && (bitmask == WRAP_CURRY_FLAG$3)) ||
    ((srcBitmask == WRAP_ARY_FLAG$2) && (bitmask == WRAP_REARG_FLAG$2) && (data[7].length <= source[8])) ||
    ((srcBitmask == (WRAP_ARY_FLAG$2 | WRAP_REARG_FLAG$2)) && (source[7].length <= source[8]) && (bitmask == WRAP_CURRY_FLAG$3));

  // Exit early if metadata can't be merged.
  if (!(isCommon || isCombo)) {
    return data;
  }
  // Use source `thisArg` if available.
  if (srcBitmask & WRAP_BIND_FLAG$1) {
    data[2] = source[2];
    // Set when currying a bound function.
    newBitmask |= bitmask & WRAP_BIND_FLAG$1 ? 0 : WRAP_CURRY_BOUND_FLAG;
  }
  // Compose partial arguments.
  var value = source[3];
  if (value) {
    var partials = data[3];
    data[3] = partials ? composeArgs(partials, value, source[4]) : value;
    data[4] = partials ? replaceHolders(data[3], PLACEHOLDER) : source[4];
  }
  // Compose partial right arguments.
  value = source[5];
  if (value) {
    partials = data[5];
    data[5] = partials ? composeArgsRight(partials, value, source[6]) : value;
    data[6] = partials ? replaceHolders(data[5], PLACEHOLDER) : source[6];
  }
  // Use source `argPos` if available.
  value = source[7];
  if (value) {
    data[7] = value;
  }
  // Use source `ary` if it's smaller.
  if (srcBitmask & WRAP_ARY_FLAG$2) {
    data[8] = data[8] == null ? source[8] : nativeMin(data[8], source[8]);
  }
  // Use source `arity` if one is not provided.
  if (data[9] == null) {
    data[9] = source[9];
  }
  // Use source `func` and merge bitmasks.
  data[0] = source[0];
  data[1] = newBitmask;

  return data;
}

var _mergeData = mergeData$1;

/** Used to match a single whitespace character. */

var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex$1(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

var _trimmedEndIndex = trimmedEndIndex$1;

var trimmedEndIndex = _trimmedEndIndex;

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim$1(string) {
  return string
    ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

var _baseTrim = baseTrim$1;

var baseGetTag$5 = _baseGetTag,
    isObjectLike$a = isObjectLike_1;

/** `Object#toString` result references. */
var symbolTag$3 = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol$6(value) {
  return typeof value == 'symbol' ||
    (isObjectLike$a(value) && baseGetTag$5(value) == symbolTag$3);
}

var isSymbol_1 = isSymbol$6;

var baseTrim = _baseTrim,
    isObject$6 = isObject_1,
    isSymbol$5 = isSymbol_1;

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber$1(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol$5(value)) {
    return NAN;
  }
  if (isObject$6(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject$6(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

var toNumber_1 = toNumber$1;

var toNumber = toNumber_1;

/** Used as references for various `Number` constants. */
var INFINITY$3 = 1 / 0,
    MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite$1(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber(value);
  if (value === INFINITY$3 || value === -INFINITY$3) {
    var sign = (value < 0 ? -1 : 1);
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}

var toFinite_1 = toFinite$1;

var toFinite = toFinite_1;

/**
 * Converts `value` to an integer.
 *
 * **Note:** This method is loosely based on
 * [`ToInteger`](http://www.ecma-international.org/ecma-262/7.0/#sec-tointeger).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted integer.
 * @example
 *
 * _.toInteger(3.2);
 * // => 3
 *
 * _.toInteger(Number.MIN_VALUE);
 * // => 0
 *
 * _.toInteger(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toInteger('3.2');
 * // => 3
 */
function toInteger$2(value) {
  var result = toFinite(value),
      remainder = result % 1;

  return result === result ? (remainder ? result - remainder : result) : 0;
}

var toInteger_1 = toInteger$2;

var baseSetData = _baseSetData,
    createBind = _createBind,
    createCurry = _createCurry,
    createHybrid = _createHybrid,
    createPartial = _createPartial,
    getData$1 = _getData,
    mergeData = _mergeData,
    setData = _setData,
    setWrapToString = _setWrapToString,
    toInteger$1 = toInteger_1;

/** Error message constants. */
var FUNC_ERROR_TEXT$2 = 'Expected a function';

/** Used to compose bitmasks for function metadata. */
var WRAP_BIND_FLAG = 1,
    WRAP_BIND_KEY_FLAG = 2,
    WRAP_CURRY_FLAG$2 = 8,
    WRAP_CURRY_RIGHT_FLAG = 16,
    WRAP_PARTIAL_FLAG$1 = 32,
    WRAP_PARTIAL_RIGHT_FLAG = 64;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$1 = Math.max;

/**
 * Creates a function that either curries or invokes `func` with optional
 * `this` binding and partially applied arguments.
 *
 * @private
 * @param {Function|string} func The function or method name to wrap.
 * @param {number} bitmask The bitmask flags.
 *    1 - `_.bind`
 *    2 - `_.bindKey`
 *    4 - `_.curry` or `_.curryRight` of a bound function
 *    8 - `_.curry`
 *   16 - `_.curryRight`
 *   32 - `_.partial`
 *   64 - `_.partialRight`
 *  128 - `_.rearg`
 *  256 - `_.ary`
 *  512 - `_.flip`
 * @param {*} [thisArg] The `this` binding of `func`.
 * @param {Array} [partials] The arguments to be partially applied.
 * @param {Array} [holders] The `partials` placeholder indexes.
 * @param {Array} [argPos] The argument positions of the new function.
 * @param {number} [ary] The arity cap of `func`.
 * @param {number} [arity] The arity of `func`.
 * @returns {Function} Returns the new wrapped function.
 */
function createWrap$3(func, bitmask, thisArg, partials, holders, argPos, ary, arity) {
  var isBindKey = bitmask & WRAP_BIND_KEY_FLAG;
  if (!isBindKey && typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$2);
  }
  var length = partials ? partials.length : 0;
  if (!length) {
    bitmask &= ~(WRAP_PARTIAL_FLAG$1 | WRAP_PARTIAL_RIGHT_FLAG);
    partials = holders = undefined;
  }
  ary = ary === undefined ? ary : nativeMax$1(toInteger$1(ary), 0);
  arity = arity === undefined ? arity : toInteger$1(arity);
  length -= holders ? holders.length : 0;

  if (bitmask & WRAP_PARTIAL_RIGHT_FLAG) {
    var partialsRight = partials,
        holdersRight = holders;

    partials = holders = undefined;
  }
  var data = isBindKey ? undefined : getData$1(func);

  var newData = [
    func, bitmask, thisArg, partials, holders, partialsRight, holdersRight,
    argPos, ary, arity
  ];

  if (data) {
    mergeData(newData, data);
  }
  func = newData[0];
  bitmask = newData[1];
  thisArg = newData[2];
  partials = newData[3];
  holders = newData[4];
  arity = newData[9] = newData[9] === undefined
    ? (isBindKey ? 0 : func.length)
    : nativeMax$1(newData[9] - length, 0);

  if (!arity && bitmask & (WRAP_CURRY_FLAG$2 | WRAP_CURRY_RIGHT_FLAG)) {
    bitmask &= ~(WRAP_CURRY_FLAG$2 | WRAP_CURRY_RIGHT_FLAG);
  }
  if (!bitmask || bitmask == WRAP_BIND_FLAG) {
    var result = createBind(func, bitmask, thisArg);
  } else if (bitmask == WRAP_CURRY_FLAG$2 || bitmask == WRAP_CURRY_RIGHT_FLAG) {
    result = createCurry(func, bitmask, arity);
  } else if ((bitmask == WRAP_PARTIAL_FLAG$1 || bitmask == (WRAP_BIND_FLAG | WRAP_PARTIAL_FLAG$1)) && !holders.length) {
    result = createPartial(func, bitmask, thisArg, partials);
  } else {
    result = createHybrid.apply(undefined, newData);
  }
  var setter = data ? baseSetData : setData;
  return setWrapToString(setter(result, newData), func, bitmask);
}

var _createWrap = createWrap$3;

var createWrap$2 = _createWrap;

/** Used to compose bitmasks for function metadata. */
var WRAP_ARY_FLAG$1 = 128;

/**
 * Creates a function that invokes `func`, with up to `n` arguments,
 * ignoring any additional arguments.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Function
 * @param {Function} func The function to cap arguments for.
 * @param {number} [n=func.length] The arity cap.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Function} Returns the new capped function.
 * @example
 *
 * _.map(['6', '8', '10'], _.ary(parseInt, 1));
 * // => [6, 8, 10]
 */
function ary(func, n, guard) {
  n = guard ? undefined : n;
  n = (func && n == null) ? func.length : n;
  return createWrap$2(func, WRAP_ARY_FLAG$1, undefined, undefined, undefined, undefined, n);
}

var ary_1 = ary;

var defineProperty = _defineProperty;

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue$3(object, key, value) {
  if (key == '__proto__' && defineProperty) {
    defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

var _baseAssignValue = baseAssignValue$3;

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */

function eq$5(value, other) {
  return value === other || (value !== value && other !== other);
}

var eq_1 = eq$5;

var baseAssignValue$2 = _baseAssignValue,
    eq$4 = eq_1;

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$a = objectProto$c.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue$2(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$a.call(object, key) && eq$4(objValue, value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue$2(object, key, value);
  }
}

var _assignValue = assignValue$2;

var assignValue$1 = _assignValue,
    baseAssignValue$1 = _baseAssignValue;

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject$5(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = customizer
      ? customizer(object[key], source[key], key, object, source)
      : undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      baseAssignValue$1(object, key, newValue);
    } else {
      assignValue$1(object, key, newValue);
    }
  }
  return object;
}

var _copyObject = copyObject$5;

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */

function baseTimes$1(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

var _baseTimes = baseTimes$1;

var baseGetTag$4 = _baseGetTag,
    isObjectLike$9 = isObjectLike_1;

/** `Object#toString` result references. */
var argsTag$3 = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments$1(value) {
  return isObjectLike$9(value) && baseGetTag$4(value) == argsTag$3;
}

var _baseIsArguments = baseIsArguments$1;

var baseIsArguments = _baseIsArguments,
    isObjectLike$8 = isObjectLike_1;

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$b.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$b.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments$4 = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
  return isObjectLike$8(value) && hasOwnProperty$9.call(value, 'callee') &&
    !propertyIsEnumerable$1.call(value, 'callee');
};

var isArguments_1 = isArguments$4;

var isBuffer$4 = {exports: {}};

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */

function stubFalse() {
  return false;
}

var stubFalse_1 = stubFalse;

(function (module, exports) {
var root = _root,
    stubFalse = stubFalse_1;

/** Detect free variable `exports`. */
var freeExports = exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer ? Buffer.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer = nativeIsBuffer || stubFalse;

module.exports = isBuffer;
}(isBuffer$4, isBuffer$4.exports));

/** Used as references for various `Number` constants. */

var MAX_SAFE_INTEGER = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength$3(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
}

var isLength_1 = isLength$3;

var baseGetTag$3 = _baseGetTag,
    isLength$2 = isLength_1,
    isObjectLike$7 = isObjectLike_1;

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$2 = '[object Array]',
    boolTag$3 = '[object Boolean]',
    dateTag$3 = '[object Date]',
    errorTag$3 = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag$5 = '[object Map]',
    numberTag$3 = '[object Number]',
    objectTag$4 = '[object Object]',
    regexpTag$3 = '[object RegExp]',
    setTag$5 = '[object Set]',
    stringTag$3 = '[object String]',
    weakMapTag$3 = '[object WeakMap]';

var arrayBufferTag$3 = '[object ArrayBuffer]',
    dataViewTag$4 = '[object DataView]',
    float32Tag$2 = '[object Float32Array]',
    float64Tag$2 = '[object Float64Array]',
    int8Tag$2 = '[object Int8Array]',
    int16Tag$2 = '[object Int16Array]',
    int32Tag$2 = '[object Int32Array]',
    uint8Tag$2 = '[object Uint8Array]',
    uint8ClampedTag$2 = '[object Uint8ClampedArray]',
    uint16Tag$2 = '[object Uint16Array]',
    uint32Tag$2 = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag$2] = typedArrayTags[float64Tag$2] =
typedArrayTags[int8Tag$2] = typedArrayTags[int16Tag$2] =
typedArrayTags[int32Tag$2] = typedArrayTags[uint8Tag$2] =
typedArrayTags[uint8ClampedTag$2] = typedArrayTags[uint16Tag$2] =
typedArrayTags[uint32Tag$2] = true;
typedArrayTags[argsTag$2] = typedArrayTags[arrayTag$2] =
typedArrayTags[arrayBufferTag$3] = typedArrayTags[boolTag$3] =
typedArrayTags[dataViewTag$4] = typedArrayTags[dateTag$3] =
typedArrayTags[errorTag$3] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag$5] = typedArrayTags[numberTag$3] =
typedArrayTags[objectTag$4] = typedArrayTags[regexpTag$3] =
typedArrayTags[setTag$5] = typedArrayTags[stringTag$3] =
typedArrayTags[weakMapTag$3] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray$1(value) {
  return isObjectLike$7(value) &&
    isLength$2(value.length) && !!typedArrayTags[baseGetTag$3(value)];
}

var _baseIsTypedArray = baseIsTypedArray$1;

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */

function baseUnary$4(func) {
  return function(value) {
    return func(value);
  };
}

var _baseUnary = baseUnary$4;

var _nodeUtil = {exports: {}};

(function (module, exports) {
var freeGlobal = _freeGlobal;

/** Detect free variable `exports`. */
var freeExports = exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports && freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule && freeModule.require && freeModule.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

module.exports = nodeUtil;
}(_nodeUtil, _nodeUtil.exports));

var baseIsTypedArray = _baseIsTypedArray,
    baseUnary$3 = _baseUnary,
    nodeUtil$2 = _nodeUtil.exports;

/* Node.js helper references. */
var nodeIsTypedArray = nodeUtil$2 && nodeUtil$2.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray$3 = nodeIsTypedArray ? baseUnary$3(nodeIsTypedArray) : baseIsTypedArray;

var isTypedArray_1 = isTypedArray$3;

var baseTimes = _baseTimes,
    isArguments$3 = isArguments_1,
    isArray$d = isArray_1,
    isBuffer$3 = isBuffer$4.exports,
    isIndex$2 = _isIndex,
    isTypedArray$2 = isTypedArray_1;

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$a.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys$2(value, inherited) {
  var isArr = isArray$d(value),
      isArg = !isArr && isArguments$3(value),
      isBuff = !isArr && !isArg && isBuffer$3(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray$2(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$8.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           isIndex$2(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

var _arrayLikeKeys = arrayLikeKeys$2;

/** Used for built-in method references. */

var objectProto$9 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype$3(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$9;

  return value === proto;
}

var _isPrototype = isPrototype$3;

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */

function overArg$2(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

var _overArg = overArg$2;

var overArg$1 = _overArg;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys$1 = overArg$1(Object.keys, Object);

var _nativeKeys = nativeKeys$1;

var isPrototype$2 = _isPrototype,
    nativeKeys = _nativeKeys;

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$8.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys$1(object) {
  if (!isPrototype$2(object)) {
    return nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$7.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

var _baseKeys = baseKeys$1;

var isFunction$1 = isFunction_1,
    isLength$1 = isLength_1;

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike$6(value) {
  return value != null && isLength$1(value.length) && !isFunction$1(value);
}

var isArrayLike_1 = isArrayLike$6;

var arrayLikeKeys$1 = _arrayLikeKeys,
    baseKeys = _baseKeys,
    isArrayLike$5 = isArrayLike_1;

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys$5(object) {
  return isArrayLike$5(object) ? arrayLikeKeys$1(object) : baseKeys(object);
}

var keys_1 = keys$5;

var copyObject$4 = _copyObject,
    keys$4 = keys_1;

/**
 * The base implementation of `_.assign` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssign$1(object, source) {
  return object && copyObject$4(source, keys$4(source), object);
}

var _baseAssign = baseAssign$1;

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */

function listCacheClear$1() {
  this.__data__ = [];
  this.size = 0;
}

var _listCacheClear = listCacheClear$1;

var eq$3 = eq_1;

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf$4(array, key) {
  var length = array.length;
  while (length--) {
    if (eq$3(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

var _assocIndexOf = assocIndexOf$4;

var assocIndexOf$3 = _assocIndexOf;

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete$1(key) {
  var data = this.__data__,
      index = assocIndexOf$3(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

var _listCacheDelete = listCacheDelete$1;

var assocIndexOf$2 = _assocIndexOf;

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet$1(key) {
  var data = this.__data__,
      index = assocIndexOf$2(data, key);

  return index < 0 ? undefined : data[index][1];
}

var _listCacheGet = listCacheGet$1;

var assocIndexOf$1 = _assocIndexOf;

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas$1(key) {
  return assocIndexOf$1(this.__data__, key) > -1;
}

var _listCacheHas = listCacheHas$1;

var assocIndexOf = _assocIndexOf;

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet$1(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

var _listCacheSet = listCacheSet$1;

var listCacheClear = _listCacheClear,
    listCacheDelete = _listCacheDelete,
    listCacheGet = _listCacheGet,
    listCacheHas = _listCacheHas,
    listCacheSet = _listCacheSet;

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache$4(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache$4.prototype.clear = listCacheClear;
ListCache$4.prototype['delete'] = listCacheDelete;
ListCache$4.prototype.get = listCacheGet;
ListCache$4.prototype.has = listCacheHas;
ListCache$4.prototype.set = listCacheSet;

var _ListCache = ListCache$4;

var ListCache$3 = _ListCache;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear$1() {
  this.__data__ = new ListCache$3;
  this.size = 0;
}

var _stackClear = stackClear$1;

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function stackDelete$1(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

var _stackDelete = stackDelete$1;

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */

function stackGet$1(key) {
  return this.__data__.get(key);
}

var _stackGet = stackGet$1;

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function stackHas$1(key) {
  return this.__data__.has(key);
}

var _stackHas = stackHas$1;

var getNative$4 = _getNative,
    root$4 = _root;

/* Built-in method references that are verified to be native. */
var Map$4 = getNative$4(root$4, 'Map');

var _Map = Map$4;

var getNative$3 = _getNative;

/* Built-in method references that are verified to be native. */
var nativeCreate$4 = getNative$3(Object, 'create');

var _nativeCreate = nativeCreate$4;

var nativeCreate$3 = _nativeCreate;

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear$1() {
  this.__data__ = nativeCreate$3 ? nativeCreate$3(null) : {};
  this.size = 0;
}

var _hashClear = hashClear$1;

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */

function hashDelete$1(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

var _hashDelete = hashDelete$1;

var nativeCreate$2 = _nativeCreate;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$7.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet$1(key) {
  var data = this.__data__;
  if (nativeCreate$2) {
    var result = data[key];
    return result === HASH_UNDEFINED$2 ? undefined : result;
  }
  return hasOwnProperty$6.call(data, key) ? data[key] : undefined;
}

var _hashGet = hashGet$1;

var nativeCreate$1 = _nativeCreate;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$6.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas$1(key) {
  var data = this.__data__;
  return nativeCreate$1 ? (data[key] !== undefined) : hasOwnProperty$5.call(data, key);
}

var _hashHas = hashHas$1;

var nativeCreate = _nativeCreate;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet$1(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

var _hashSet = hashSet$1;

var hashClear = _hashClear,
    hashDelete = _hashDelete,
    hashGet = _hashGet,
    hashHas = _hashHas,
    hashSet = _hashSet;

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash$1(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash$1.prototype.clear = hashClear;
Hash$1.prototype['delete'] = hashDelete;
Hash$1.prototype.get = hashGet;
Hash$1.prototype.has = hashHas;
Hash$1.prototype.set = hashSet;

var _Hash = Hash$1;

var Hash = _Hash,
    ListCache$2 = _ListCache,
    Map$3 = _Map;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear$1() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map$3 || ListCache$2),
    'string': new Hash
  };
}

var _mapCacheClear = mapCacheClear$1;

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */

function isKeyable$1(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

var _isKeyable = isKeyable$1;

var isKeyable = _isKeyable;

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData$4(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

var _getMapData = getMapData$4;

var getMapData$3 = _getMapData;

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete$1(key) {
  var result = getMapData$3(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

var _mapCacheDelete = mapCacheDelete$1;

var getMapData$2 = _getMapData;

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet$1(key) {
  return getMapData$2(this, key).get(key);
}

var _mapCacheGet = mapCacheGet$1;

var getMapData$1 = _getMapData;

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas$1(key) {
  return getMapData$1(this, key).has(key);
}

var _mapCacheHas = mapCacheHas$1;

var getMapData = _getMapData;

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet$1(key, value) {
  var data = getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

var _mapCacheSet = mapCacheSet$1;

var mapCacheClear = _mapCacheClear,
    mapCacheDelete = _mapCacheDelete,
    mapCacheGet = _mapCacheGet,
    mapCacheHas = _mapCacheHas,
    mapCacheSet = _mapCacheSet;

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache$3(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache$3.prototype.clear = mapCacheClear;
MapCache$3.prototype['delete'] = mapCacheDelete;
MapCache$3.prototype.get = mapCacheGet;
MapCache$3.prototype.has = mapCacheHas;
MapCache$3.prototype.set = mapCacheSet;

var _MapCache = MapCache$3;

var ListCache$1 = _ListCache,
    Map$2 = _Map,
    MapCache$2 = _MapCache;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE$1 = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet$1(key, value) {
  var data = this.__data__;
  if (data instanceof ListCache$1) {
    var pairs = data.__data__;
    if (!Map$2 || (pairs.length < LARGE_ARRAY_SIZE$1 - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new MapCache$2(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

var _stackSet = stackSet$1;

var ListCache = _ListCache,
    stackClear = _stackClear,
    stackDelete = _stackDelete,
    stackGet = _stackGet,
    stackHas = _stackHas,
    stackSet = _stackSet;

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack$4(entries) {
  var data = this.__data__ = new ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack$4.prototype.clear = stackClear;
Stack$4.prototype['delete'] = stackDelete;
Stack$4.prototype.get = stackGet;
Stack$4.prototype.has = stackHas;
Stack$4.prototype.set = stackSet;

var _Stack = Stack$4;

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */

function nativeKeysIn$1(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

var _nativeKeysIn = nativeKeysIn$1;

var isObject$5 = isObject_1,
    isPrototype$1 = _isPrototype,
    nativeKeysIn = _nativeKeysIn;

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$5.hasOwnProperty;

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn$1(object) {
  if (!isObject$5(object)) {
    return nativeKeysIn(object);
  }
  var isProto = isPrototype$1(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$4.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

var _baseKeysIn = baseKeysIn$1;

var arrayLikeKeys = _arrayLikeKeys,
    baseKeysIn = _baseKeysIn,
    isArrayLike$4 = isArrayLike_1;

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn$5(object) {
  return isArrayLike$4(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
}

var keysIn_1 = keysIn$5;

var copyObject$3 = _copyObject,
    keysIn$4 = keysIn_1;

/**
 * The base implementation of `_.assignIn` without support for multiple sources
 * or `customizer` functions.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @returns {Object} Returns `object`.
 */
function baseAssignIn$1(object, source) {
  return object && copyObject$3(source, keysIn$4(source), object);
}

var _baseAssignIn = baseAssignIn$1;

var _cloneBuffer = {exports: {}};

(function (module, exports) {
var root = _root;

/** Detect free variable `exports`. */
var freeExports = exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && 'object' == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer = moduleExports ? root.Buffer : undefined,
    allocUnsafe = Buffer ? Buffer.allocUnsafe : undefined;

/**
 * Creates a clone of  `buffer`.
 *
 * @private
 * @param {Buffer} buffer The buffer to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Buffer} Returns the cloned buffer.
 */
function cloneBuffer(buffer, isDeep) {
  if (isDeep) {
    return buffer.slice();
  }
  var length = buffer.length,
      result = allocUnsafe ? allocUnsafe(length) : new buffer.constructor(length);

  buffer.copy(result);
  return result;
}

module.exports = cloneBuffer;
}(_cloneBuffer, _cloneBuffer.exports));

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */

function arrayFilter$1(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var _arrayFilter = arrayFilter$1;

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */

function stubArray$2() {
  return [];
}

var stubArray_1 = stubArray$2;

var arrayFilter = _arrayFilter,
    stubArray$1 = stubArray_1;

/** Used for built-in method references. */
var objectProto$4 = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$4.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols$1 = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols$3 = !nativeGetSymbols$1 ? stubArray$1 : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return arrayFilter(nativeGetSymbols$1(object), function(symbol) {
    return propertyIsEnumerable.call(object, symbol);
  });
};

var _getSymbols = getSymbols$3;

var copyObject$2 = _copyObject,
    getSymbols$2 = _getSymbols;

/**
 * Copies own symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbols$1(source, object) {
  return copyObject$2(source, getSymbols$2(source), object);
}

var _copySymbols = copySymbols$1;

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */

function arrayPush$3(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

var _arrayPush = arrayPush$3;

var overArg = _overArg;

/** Built-in value references. */
var getPrototype$3 = overArg(Object.getPrototypeOf, Object);

var _getPrototype = getPrototype$3;

var arrayPush$2 = _arrayPush,
    getPrototype$2 = _getPrototype,
    getSymbols$1 = _getSymbols,
    stubArray = stubArray_1;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own and inherited enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbolsIn$2 = !nativeGetSymbols ? stubArray : function(object) {
  var result = [];
  while (object) {
    arrayPush$2(result, getSymbols$1(object));
    object = getPrototype$2(object);
  }
  return result;
};

var _getSymbolsIn = getSymbolsIn$2;

var copyObject$1 = _copyObject,
    getSymbolsIn$1 = _getSymbolsIn;

/**
 * Copies own and inherited symbols of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy symbols from.
 * @param {Object} [object={}] The object to copy symbols to.
 * @returns {Object} Returns `object`.
 */
function copySymbolsIn$1(source, object) {
  return copyObject$1(source, getSymbolsIn$1(source), object);
}

var _copySymbolsIn = copySymbolsIn$1;

var arrayPush$1 = _arrayPush,
    isArray$c = isArray_1;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys$2(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray$c(object) ? result : arrayPush$1(result, symbolsFunc(object));
}

var _baseGetAllKeys = baseGetAllKeys$2;

var baseGetAllKeys$1 = _baseGetAllKeys,
    getSymbols = _getSymbols,
    keys$3 = keys_1;

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys$2(object) {
  return baseGetAllKeys$1(object, keys$3, getSymbols);
}

var _getAllKeys = getAllKeys$2;

var baseGetAllKeys = _baseGetAllKeys,
    getSymbolsIn = _getSymbolsIn,
    keysIn$3 = keysIn_1;

/**
 * Creates an array of own and inherited enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeysIn$1(object) {
  return baseGetAllKeys(object, keysIn$3, getSymbolsIn);
}

var _getAllKeysIn = getAllKeysIn$1;

var getNative$2 = _getNative,
    root$3 = _root;

/* Built-in method references that are verified to be native. */
var DataView$1 = getNative$2(root$3, 'DataView');

var _DataView = DataView$1;

var getNative$1 = _getNative,
    root$2 = _root;

/* Built-in method references that are verified to be native. */
var Promise$2 = getNative$1(root$2, 'Promise');

var _Promise = Promise$2;

var getNative = _getNative,
    root$1 = _root;

/* Built-in method references that are verified to be native. */
var Set$2 = getNative(root$1, 'Set');

var _Set = Set$2;

var DataView = _DataView,
    Map$1 = _Map,
    Promise$1 = _Promise,
    Set$1 = _Set,
    WeakMap$1 = _WeakMap,
    baseGetTag$2 = _baseGetTag,
    toSource = _toSource;

/** `Object#toString` result references. */
var mapTag$4 = '[object Map]',
    objectTag$3 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$4 = '[object Set]',
    weakMapTag$2 = '[object WeakMap]';

var dataViewTag$3 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = toSource(DataView),
    mapCtorString = toSource(Map$1),
    promiseCtorString = toSource(Promise$1),
    setCtorString = toSource(Set$1),
    weakMapCtorString = toSource(WeakMap$1);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag$5 = baseGetTag$2;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((DataView && getTag$5(new DataView(new ArrayBuffer(1))) != dataViewTag$3) ||
    (Map$1 && getTag$5(new Map$1) != mapTag$4) ||
    (Promise$1 && getTag$5(Promise$1.resolve()) != promiseTag) ||
    (Set$1 && getTag$5(new Set$1) != setTag$4) ||
    (WeakMap$1 && getTag$5(new WeakMap$1) != weakMapTag$2)) {
  getTag$5 = function(value) {
    var result = baseGetTag$2(value),
        Ctor = result == objectTag$3 ? value.constructor : undefined,
        ctorString = Ctor ? toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$3;
        case mapCtorString: return mapTag$4;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$4;
        case weakMapCtorString: return weakMapTag$2;
      }
    }
    return result;
  };
}

var _getTag = getTag$5;

/** Used for built-in method references. */

var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$3.hasOwnProperty;

/**
 * Initializes an array clone.
 *
 * @private
 * @param {Array} array The array to clone.
 * @returns {Array} Returns the initialized clone.
 */
function initCloneArray$1(array) {
  var length = array.length,
      result = new array.constructor(length);

  // Add properties assigned by `RegExp#exec`.
  if (length && typeof array[0] == 'string' && hasOwnProperty$3.call(array, 'index')) {
    result.index = array.index;
    result.input = array.input;
  }
  return result;
}

var _initCloneArray = initCloneArray$1;

var root = _root;

/** Built-in value references. */
var Uint8Array$3 = root.Uint8Array;

var _Uint8Array = Uint8Array$3;

var Uint8Array$2 = _Uint8Array;

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer$3(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new Uint8Array$2(result).set(new Uint8Array$2(arrayBuffer));
  return result;
}

var _cloneArrayBuffer = cloneArrayBuffer$3;

var cloneArrayBuffer$2 = _cloneArrayBuffer;

/**
 * Creates a clone of `dataView`.
 *
 * @private
 * @param {Object} dataView The data view to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned data view.
 */
function cloneDataView$1(dataView, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer$2(dataView.buffer) : dataView.buffer;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}

var _cloneDataView = cloneDataView$1;

/** Used to match `RegExp` flags from their coerced string values. */

var reFlags = /\w*$/;

/**
 * Creates a clone of `regexp`.
 *
 * @private
 * @param {Object} regexp The regexp to clone.
 * @returns {Object} Returns the cloned regexp.
 */
function cloneRegExp$1(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}

var _cloneRegExp = cloneRegExp$1;

var Symbol$4 = _Symbol;

/** Used to convert symbols to primitives and strings. */
var symbolProto$2 = Symbol$4 ? Symbol$4.prototype : undefined,
    symbolValueOf$1 = symbolProto$2 ? symbolProto$2.valueOf : undefined;

/**
 * Creates a clone of the `symbol` object.
 *
 * @private
 * @param {Object} symbol The symbol object to clone.
 * @returns {Object} Returns the cloned symbol object.
 */
function cloneSymbol$1(symbol) {
  return symbolValueOf$1 ? Object(symbolValueOf$1.call(symbol)) : {};
}

var _cloneSymbol = cloneSymbol$1;

var cloneArrayBuffer$1 = _cloneArrayBuffer;

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray$2(typedArray, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer$1(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

var _cloneTypedArray = cloneTypedArray$2;

var cloneArrayBuffer = _cloneArrayBuffer,
    cloneDataView = _cloneDataView,
    cloneRegExp = _cloneRegExp,
    cloneSymbol = _cloneSymbol,
    cloneTypedArray$1 = _cloneTypedArray;

/** `Object#toString` result references. */
var boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    mapTag$3 = '[object Map]',
    numberTag$2 = '[object Number]',
    regexpTag$2 = '[object RegExp]',
    setTag$3 = '[object Set]',
    stringTag$2 = '[object String]',
    symbolTag$2 = '[object Symbol]';

var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$2 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';

/**
 * Initializes an object clone based on its `toStringTag`.
 *
 * **Note:** This function only supports cloning values with tags of
 * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
 *
 * @private
 * @param {Object} object The object to clone.
 * @param {string} tag The `toStringTag` of the object to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneByTag$1(object, tag, isDeep) {
  var Ctor = object.constructor;
  switch (tag) {
    case arrayBufferTag$2:
      return cloneArrayBuffer(object);

    case boolTag$2:
    case dateTag$2:
      return new Ctor(+object);

    case dataViewTag$2:
      return cloneDataView(object, isDeep);

    case float32Tag$1: case float64Tag$1:
    case int8Tag$1: case int16Tag$1: case int32Tag$1:
    case uint8Tag$1: case uint8ClampedTag$1: case uint16Tag$1: case uint32Tag$1:
      return cloneTypedArray$1(object, isDeep);

    case mapTag$3:
      return new Ctor;

    case numberTag$2:
    case stringTag$2:
      return new Ctor(object);

    case regexpTag$2:
      return cloneRegExp(object);

    case setTag$3:
      return new Ctor;

    case symbolTag$2:
      return cloneSymbol(object);
  }
}

var _initCloneByTag = initCloneByTag$1;

var baseCreate = _baseCreate,
    getPrototype$1 = _getPrototype,
    isPrototype = _isPrototype;

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject$2(object) {
  return (typeof object.constructor == 'function' && !isPrototype(object))
    ? baseCreate(getPrototype$1(object))
    : {};
}

var _initCloneObject = initCloneObject$2;

var getTag$4 = _getTag,
    isObjectLike$6 = isObjectLike_1;

/** `Object#toString` result references. */
var mapTag$2 = '[object Map]';

/**
 * The base implementation of `_.isMap` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 */
function baseIsMap$1(value) {
  return isObjectLike$6(value) && getTag$4(value) == mapTag$2;
}

var _baseIsMap = baseIsMap$1;

var baseIsMap = _baseIsMap,
    baseUnary$2 = _baseUnary,
    nodeUtil$1 = _nodeUtil.exports;

/* Node.js helper references. */
var nodeIsMap = nodeUtil$1 && nodeUtil$1.isMap;

/**
 * Checks if `value` is classified as a `Map` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 * @example
 *
 * _.isMap(new Map);
 * // => true
 *
 * _.isMap(new WeakMap);
 * // => false
 */
var isMap$1 = nodeIsMap ? baseUnary$2(nodeIsMap) : baseIsMap;

var isMap_1 = isMap$1;

var getTag$3 = _getTag,
    isObjectLike$5 = isObjectLike_1;

/** `Object#toString` result references. */
var setTag$2 = '[object Set]';

/**
 * The base implementation of `_.isSet` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 */
function baseIsSet$1(value) {
  return isObjectLike$5(value) && getTag$3(value) == setTag$2;
}

var _baseIsSet = baseIsSet$1;

var baseIsSet = _baseIsSet,
    baseUnary$1 = _baseUnary,
    nodeUtil = _nodeUtil.exports;

/* Node.js helper references. */
var nodeIsSet = nodeUtil && nodeUtil.isSet;

/**
 * Checks if `value` is classified as a `Set` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 * @example
 *
 * _.isSet(new Set);
 * // => true
 *
 * _.isSet(new WeakSet);
 * // => false
 */
var isSet$1 = nodeIsSet ? baseUnary$1(nodeIsSet) : baseIsSet;

var isSet_1 = isSet$1;

var Stack$3 = _Stack,
    arrayEach = _arrayEach,
    assignValue = _assignValue,
    baseAssign = _baseAssign,
    baseAssignIn = _baseAssignIn,
    cloneBuffer$1 = _cloneBuffer.exports,
    copyArray$2 = _copyArray,
    copySymbols = _copySymbols,
    copySymbolsIn = _copySymbolsIn,
    getAllKeys$1 = _getAllKeys,
    getAllKeysIn = _getAllKeysIn,
    getTag$2 = _getTag,
    initCloneArray = _initCloneArray,
    initCloneByTag = _initCloneByTag,
    initCloneObject$1 = _initCloneObject,
    isArray$b = isArray_1,
    isBuffer$2 = isBuffer$4.exports,
    isMap = isMap_1,
    isObject$4 = isObject_1,
    isSet = isSet_1,
    keys$2 = keys_1,
    keysIn$2 = keysIn_1;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG$1 = 1,
    CLONE_FLAT_FLAG = 2,
    CLONE_SYMBOLS_FLAG$1 = 4;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    errorTag$2 = '[object Error]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    mapTag$1 = '[object Map]',
    numberTag$1 = '[object Number]',
    objectTag$2 = '[object Object]',
    regexpTag$1 = '[object RegExp]',
    setTag$1 = '[object Set]',
    stringTag$1 = '[object String]',
    symbolTag$1 = '[object Symbol]',
    weakMapTag$1 = '[object WeakMap]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$1 = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values supported by `_.clone`. */
var cloneableTags = {};
cloneableTags[argsTag$1] = cloneableTags[arrayTag$1] =
cloneableTags[arrayBufferTag$1] = cloneableTags[dataViewTag$1] =
cloneableTags[boolTag$1] = cloneableTags[dateTag$1] =
cloneableTags[float32Tag] = cloneableTags[float64Tag] =
cloneableTags[int8Tag] = cloneableTags[int16Tag] =
cloneableTags[int32Tag] = cloneableTags[mapTag$1] =
cloneableTags[numberTag$1] = cloneableTags[objectTag$2] =
cloneableTags[regexpTag$1] = cloneableTags[setTag$1] =
cloneableTags[stringTag$1] = cloneableTags[symbolTag$1] =
cloneableTags[uint8Tag] = cloneableTags[uint8ClampedTag] =
cloneableTags[uint16Tag] = cloneableTags[uint32Tag] = true;
cloneableTags[errorTag$2] = cloneableTags[funcTag] =
cloneableTags[weakMapTag$1] = false;

/**
 * The base implementation of `_.clone` and `_.cloneDeep` which tracks
 * traversed objects.
 *
 * @private
 * @param {*} value The value to clone.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Deep clone
 *  2 - Flatten inherited properties
 *  4 - Clone symbols
 * @param {Function} [customizer] The function to customize cloning.
 * @param {string} [key] The key of `value`.
 * @param {Object} [object] The parent object of `value`.
 * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
 * @returns {*} Returns the cloned value.
 */
function baseClone$2(value, bitmask, customizer, key, object, stack) {
  var result,
      isDeep = bitmask & CLONE_DEEP_FLAG$1,
      isFlat = bitmask & CLONE_FLAT_FLAG,
      isFull = bitmask & CLONE_SYMBOLS_FLAG$1;

  if (customizer) {
    result = object ? customizer(value, key, object, stack) : customizer(value);
  }
  if (result !== undefined) {
    return result;
  }
  if (!isObject$4(value)) {
    return value;
  }
  var isArr = isArray$b(value);
  if (isArr) {
    result = initCloneArray(value);
    if (!isDeep) {
      return copyArray$2(value, result);
    }
  } else {
    var tag = getTag$2(value),
        isFunc = tag == funcTag || tag == genTag;

    if (isBuffer$2(value)) {
      return cloneBuffer$1(value, isDeep);
    }
    if (tag == objectTag$2 || tag == argsTag$1 || (isFunc && !object)) {
      result = (isFlat || isFunc) ? {} : initCloneObject$1(value);
      if (!isDeep) {
        return isFlat
          ? copySymbolsIn(value, baseAssignIn(result, value))
          : copySymbols(value, baseAssign(result, value));
      }
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }
      result = initCloneByTag(value, tag, isDeep);
    }
  }
  // Check for circular references and return its corresponding clone.
  stack || (stack = new Stack$3);
  var stacked = stack.get(value);
  if (stacked) {
    return stacked;
  }
  stack.set(value, result);

  if (isSet(value)) {
    value.forEach(function(subValue) {
      result.add(baseClone$2(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap(value)) {
    value.forEach(function(subValue, key) {
      result.set(key, baseClone$2(subValue, bitmask, customizer, key, value, stack));
    });
  }

  var keysFunc = isFull
    ? (isFlat ? getAllKeysIn : getAllKeys$1)
    : (isFlat ? keysIn$2 : keys$2);

  var props = isArr ? undefined : keysFunc(value);
  arrayEach(props || value, function(subValue, key) {
    if (props) {
      key = subValue;
      subValue = value[key];
    }
    // Recursively populate clone (susceptible to call stack limits).
    assignValue(result, key, baseClone$2(subValue, bitmask, customizer, key, value, stack));
  });
  return result;
}

var _baseClone = baseClone$2;

var baseClone$1 = _baseClone;

/** Used to compose bitmasks for cloning. */
var CLONE_SYMBOLS_FLAG = 4;

/**
 * Creates a shallow clone of `value`.
 *
 * **Note:** This method is loosely based on the
 * [structured clone algorithm](https://mdn.io/Structured_clone_algorithm)
 * and supports cloning arrays, array buffers, booleans, date objects, maps,
 * numbers, `Object` objects, regexes, sets, strings, symbols, and typed
 * arrays. The own enumerable properties of `arguments` objects are cloned
 * as plain objects. An empty object is returned for uncloneable values such
 * as error objects, functions, DOM nodes, and WeakMaps.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to clone.
 * @returns {*} Returns the cloned value.
 * @see _.cloneDeep
 * @example
 *
 * var objects = [{ 'a': 1 }, { 'b': 2 }];
 *
 * var shallow = _.clone(objects);
 * console.log(shallow[0] === objects[0]);
 * // => true
 */
function clone(value) {
  return baseClone$1(value, CLONE_SYMBOLS_FLAG);
}

var clone_1 = clone;

var createWrap$1 = _createWrap;

/** Used to compose bitmasks for function metadata. */
var WRAP_CURRY_FLAG$1 = 8;

/**
 * Creates a function that accepts arguments of `func` and either invokes
 * `func` returning its result, if at least `arity` number of arguments have
 * been provided, or returns a function that accepts the remaining `func`
 * arguments, and so on. The arity of `func` may be specified if `func.length`
 * is not sufficient.
 *
 * The `_.curry.placeholder` value, which defaults to `_` in monolithic builds,
 * may be used as a placeholder for provided arguments.
 *
 * **Note:** This method doesn't set the "length" property of curried functions.
 *
 * @static
 * @memberOf _
 * @since 2.0.0
 * @category Function
 * @param {Function} func The function to curry.
 * @param {number} [arity=func.length] The arity of `func`.
 * @param- {Object} [guard] Enables use as an iteratee for methods like `_.map`.
 * @returns {Function} Returns the new curried function.
 * @example
 *
 * var abc = function(a, b, c) {
 *   return [a, b, c];
 * };
 *
 * var curried = _.curry(abc);
 *
 * curried(1)(2)(3);
 * // => [1, 2, 3]
 *
 * curried(1, 2)(3);
 * // => [1, 2, 3]
 *
 * curried(1, 2, 3);
 * // => [1, 2, 3]
 *
 * // Curried with placeholders.
 * curried(1)(_, 3)(2);
 * // => [1, 2, 3]
 */
function curry(func, arity, guard) {
  arity = guard ? undefined : arity;
  var result = createWrap$1(func, WRAP_CURRY_FLAG$1, undefined, undefined, undefined, undefined, undefined, arity);
  result.placeholder = curry.placeholder;
  return result;
}

// Assign default placeholders.
curry.placeholder = {};

var curry_1 = curry;

var baseGetTag$1 = _baseGetTag,
    getPrototype = _getPrototype,
    isObjectLike$4 = isObjectLike_1;

/** `Object#toString` result references. */
var objectTag$1 = '[object Object]';

/** Used for built-in method references. */
var funcProto = Function.prototype,
    objectProto$2 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$2.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject$2(value) {
  if (!isObjectLike$4(value) || baseGetTag$1(value) != objectTag$1) {
    return false;
  }
  var proto = getPrototype(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty$2.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString.call(Ctor) == objectCtorString;
}

var isPlainObject_1 = isPlainObject$2;

var baseGetTag = _baseGetTag,
    isObjectLike$3 = isObjectLike_1,
    isPlainObject$1 = isPlainObject_1;

/** `Object#toString` result references. */
var domExcTag = '[object DOMException]',
    errorTag$1 = '[object Error]';

/**
 * Checks if `value` is an `Error`, `EvalError`, `RangeError`, `ReferenceError`,
 * `SyntaxError`, `TypeError`, or `URIError` object.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an error object, else `false`.
 * @example
 *
 * _.isError(new Error);
 * // => true
 *
 * _.isError(Error);
 * // => false
 */
function isError(value) {
  if (!isObjectLike$3(value)) {
    return false;
  }
  var tag = baseGetTag(value);
  return tag == errorTag$1 || tag == domExcTag ||
    (typeof value.message == 'string' && typeof value.name == 'string' && !isPlainObject$1(value));
}

var isError_1 = isError;

var getTag$1 = _getTag,
    isObjectLike$2 = isObjectLike_1;

/** `Object#toString` result references. */
var weakMapTag = '[object WeakMap]';

/**
 * Checks if `value` is classified as a `WeakMap` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a weak map, else `false`.
 * @example
 *
 * _.isWeakMap(new WeakMap);
 * // => true
 *
 * _.isWeakMap(new Map);
 * // => false
 */
function isWeakMap(value) {
  return isObjectLike$2(value) && getTag$1(value) == weakMapTag;
}

var isWeakMap_1 = isWeakMap;

/** Used to stand-in for `undefined` hash values. */

var HASH_UNDEFINED = '__lodash_hash_undefined__';

/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */
function setCacheAdd$1(value) {
  this.__data__.set(value, HASH_UNDEFINED);
  return this;
}

var _setCacheAdd = setCacheAdd$1;

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */

function setCacheHas$1(value) {
  return this.__data__.has(value);
}

var _setCacheHas = setCacheHas$1;

var MapCache$1 = _MapCache,
    setCacheAdd = _setCacheAdd,
    setCacheHas = _setCacheHas;

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */
function SetCache$2(values) {
  var index = -1,
      length = values == null ? 0 : values.length;

  this.__data__ = new MapCache$1;
  while (++index < length) {
    this.add(values[index]);
  }
}

// Add methods to `SetCache`.
SetCache$2.prototype.add = SetCache$2.prototype.push = setCacheAdd;
SetCache$2.prototype.has = setCacheHas;

var _SetCache = SetCache$2;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */

function arraySome$1(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}

var _arraySome = arraySome$1;

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */

function cacheHas$2(cache, key) {
  return cache.has(key);
}

var _cacheHas = cacheHas$2;

var SetCache$1 = _SetCache,
    arraySome = _arraySome,
    cacheHas$1 = _cacheHas;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$5 = 1,
    COMPARE_UNORDERED_FLAG$3 = 2;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays$2(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$5,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  }
  // Check that cyclic values are equal.
  var arrStacked = stack.get(array);
  var othStacked = stack.get(other);
  if (arrStacked && othStacked) {
    return arrStacked == other && othStacked == array;
  }
  var index = -1,
      result = true,
      seen = (bitmask & COMPARE_UNORDERED_FLAG$3) ? new SetCache$1 : undefined;

  stack.set(array, other);
  stack.set(other, array);

  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, arrValue, index, other, array, stack)
        : customizer(arrValue, othValue, index, array, other, stack);
    }
    if (compared !== undefined) {
      if (compared) {
        continue;
      }
      result = false;
      break;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (seen) {
      if (!arraySome(other, function(othValue, othIndex) {
            if (!cacheHas$1(seen, othIndex) &&
                (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
              return seen.push(othIndex);
            }
          })) {
        result = false;
        break;
      }
    } else if (!(
          arrValue === othValue ||
            equalFunc(arrValue, othValue, bitmask, customizer, stack)
        )) {
      result = false;
      break;
    }
  }
  stack['delete'](array);
  stack['delete'](other);
  return result;
}

var _equalArrays = equalArrays$2;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */

function mapToArray$1(map) {
  var index = -1,
      result = Array(map.size);

  map.forEach(function(value, key) {
    result[++index] = [key, value];
  });
  return result;
}

var _mapToArray = mapToArray$1;

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */

function setToArray$3(set) {
  var index = -1,
      result = Array(set.size);

  set.forEach(function(value) {
    result[++index] = value;
  });
  return result;
}

var _setToArray = setToArray$3;

var Symbol$3 = _Symbol,
    Uint8Array$1 = _Uint8Array,
    eq$2 = eq_1,
    equalArrays$1 = _equalArrays,
    mapToArray = _mapToArray,
    setToArray$2 = _setToArray;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$4 = 1,
    COMPARE_UNORDERED_FLAG$2 = 2;

/** `Object#toString` result references. */
var boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    mapTag = '[object Map]',
    numberTag = '[object Number]',
    regexpTag = '[object RegExp]',
    setTag = '[object Set]',
    stringTag = '[object String]',
    symbolTag = '[object Symbol]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]';

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = Symbol$3 ? Symbol$3.prototype : undefined,
    symbolValueOf = symbolProto$1 ? symbolProto$1.valueOf : undefined;

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag$1(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag:
      if ((object.byteLength != other.byteLength) ||
          (object.byteOffset != other.byteOffset)) {
        return false;
      }
      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag:
      if ((object.byteLength != other.byteLength) ||
          !equalFunc(new Uint8Array$1(object), new Uint8Array$1(other))) {
        return false;
      }
      return true;

    case boolTag:
    case dateTag:
    case numberTag:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq$2(+object, +other);

    case errorTag:
      return object.name == other.name && object.message == other.message;

    case regexpTag:
    case stringTag:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == (other + '');

    case mapTag:
      var convert = mapToArray;

    case setTag:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$4;
      convert || (convert = setToArray$2);

      if (object.size != other.size && !isPartial) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      bitmask |= COMPARE_UNORDERED_FLAG$2;

      // Recursively compare objects (susceptible to call stack limits).
      stack.set(object, other);
      var result = equalArrays$1(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag:
      if (symbolValueOf) {
        return symbolValueOf.call(object) == symbolValueOf.call(other);
      }
  }
  return false;
}

var _equalByTag = equalByTag$1;

var getAllKeys = _getAllKeys;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$3 = 1;

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto$1.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects$1(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$3,
      objProps = getAllKeys(object),
      objLength = objProps.length,
      othProps = getAllKeys(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isPartial ? key in other : hasOwnProperty$1.call(other, key))) {
      return false;
    }
  }
  // Check that cyclic values are equal.
  var objStacked = stack.get(object);
  var othStacked = stack.get(other);
  if (objStacked && othStacked) {
    return objStacked == other && othStacked == object;
  }
  var result = true;
  stack.set(object, other);
  stack.set(other, object);

  var skipCtor = isPartial;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, objValue, key, other, object, stack)
        : customizer(objValue, othValue, key, object, other, stack);
    }
    // Recursively compare objects (susceptible to call stack limits).
    if (!(compared === undefined
          ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
          : compared
        )) {
      result = false;
      break;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor &&
        ('constructor' in object && 'constructor' in other) &&
        !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
          typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }
  stack['delete'](object);
  stack['delete'](other);
  return result;
}

var _equalObjects = equalObjects$1;

var Stack$2 = _Stack,
    equalArrays = _equalArrays,
    equalByTag = _equalByTag,
    equalObjects = _equalObjects,
    getTag = _getTag,
    isArray$a = isArray_1,
    isBuffer$1 = isBuffer$4.exports,
    isTypedArray$1 = isTypedArray_1;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$2 = 1;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]',
    arrayTag = '[object Array]',
    objectTag = '[object Object]';

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty = objectProto.hasOwnProperty;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep$1(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray$a(object),
      othIsArr = isArray$a(other),
      objTag = objIsArr ? arrayTag : getTag(object),
      othTag = othIsArr ? arrayTag : getTag(other);

  objTag = objTag == argsTag ? objectTag : objTag;
  othTag = othTag == argsTag ? objectTag : othTag;

  var objIsObj = objTag == objectTag,
      othIsObj = othTag == objectTag,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer$1(object)) {
    if (!isBuffer$1(other)) {
      return false;
    }
    objIsArr = true;
    objIsObj = false;
  }
  if (isSameTag && !objIsObj) {
    stack || (stack = new Stack$2);
    return (objIsArr || isTypedArray$1(object))
      ? equalArrays(object, other, bitmask, customizer, equalFunc, stack)
      : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }
  if (!(bitmask & COMPARE_PARTIAL_FLAG$2)) {
    var objIsWrapped = objIsObj && hasOwnProperty.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;

      stack || (stack = new Stack$2);
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }
  if (!isSameTag) {
    return false;
  }
  stack || (stack = new Stack$2);
  return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

var _baseIsEqualDeep = baseIsEqualDeep$1;

var baseIsEqualDeep = _baseIsEqualDeep,
    isObjectLike$1 = isObjectLike_1;

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual$2(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || (!isObjectLike$1(value) && !isObjectLike$1(other))) {
    return value !== value && other !== other;
  }
  return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual$2, stack);
}

var _baseIsEqual = baseIsEqual$2;

var Stack$1 = _Stack,
    baseIsEqual$1 = _baseIsEqual;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;

/**
 * The base implementation of `_.isMatch` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to inspect.
 * @param {Object} source The object of property values to match.
 * @param {Array} matchData The property names, values, and compare flags to match.
 * @param {Function} [customizer] The function to customize comparisons.
 * @returns {boolean} Returns `true` if `object` is a match, else `false`.
 */
function baseIsMatch$1(object, source, matchData, customizer) {
  var index = matchData.length,
      length = index,
      noCustomizer = !customizer;

  if (object == null) {
    return !length;
  }
  object = Object(object);
  while (index--) {
    var data = matchData[index];
    if ((noCustomizer && data[2])
          ? data[1] !== object[data[0]]
          : !(data[0] in object)
        ) {
      return false;
    }
  }
  while (++index < length) {
    data = matchData[index];
    var key = data[0],
        objValue = object[key],
        srcValue = data[1];

    if (noCustomizer && data[2]) {
      if (objValue === undefined && !(key in object)) {
        return false;
      }
    } else {
      var stack = new Stack$1;
      if (customizer) {
        var result = customizer(objValue, srcValue, key, object, source, stack);
      }
      if (!(result === undefined
            ? baseIsEqual$1(srcValue, objValue, COMPARE_PARTIAL_FLAG$1 | COMPARE_UNORDERED_FLAG$1, customizer, stack)
            : result
          )) {
        return false;
      }
    }
  }
  return true;
}

var _baseIsMatch = baseIsMatch$1;

var isObject$3 = isObject_1;

/**
 * Checks if `value` is suitable for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` if suitable for strict
 *  equality comparisons, else `false`.
 */
function isStrictComparable$2(value) {
  return value === value && !isObject$3(value);
}

var _isStrictComparable = isStrictComparable$2;

var isStrictComparable$1 = _isStrictComparable,
    keys$1 = keys_1;

/**
 * Gets the property names, values, and compare flags of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the match data of `object`.
 */
function getMatchData$1(object) {
  var result = keys$1(object),
      length = result.length;

  while (length--) {
    var key = result[length],
        value = object[key];

    result[length] = [key, value, isStrictComparable$1(value)];
  }
  return result;
}

var _getMatchData = getMatchData$1;

/**
 * A specialized version of `matchesProperty` for source values suitable
 * for strict equality comparisons, i.e. `===`.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */

function matchesStrictComparable$2(key, srcValue) {
  return function(object) {
    if (object == null) {
      return false;
    }
    return object[key] === srcValue &&
      (srcValue !== undefined || (key in Object(object)));
  };
}

var _matchesStrictComparable = matchesStrictComparable$2;

var baseIsMatch = _baseIsMatch,
    getMatchData = _getMatchData,
    matchesStrictComparable$1 = _matchesStrictComparable;

/**
 * The base implementation of `_.matches` which doesn't clone `source`.
 *
 * @private
 * @param {Object} source The object of property values to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatches$1(source) {
  var matchData = getMatchData(source);
  if (matchData.length == 1 && matchData[0][2]) {
    return matchesStrictComparable$1(matchData[0][0], matchData[0][1]);
  }
  return function(object) {
    return object === source || baseIsMatch(object, source, matchData);
  };
}

var _baseMatches = baseMatches$1;

var isArray$9 = isArray_1,
    isSymbol$4 = isSymbol_1;

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
    reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey$3(value, object) {
  if (isArray$9(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' ||
      value == null || isSymbol$4(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) ||
    (object != null && value in Object(object));
}

var _isKey = isKey$3;

var MapCache = _MapCache;

/** Error message constants. */
var FUNC_ERROR_TEXT$1 = 'Expected a function';

/**
 * Creates a function that memoizes the result of `func`. If `resolver` is
 * provided, it determines the cache key for storing the result based on the
 * arguments provided to the memoized function. By default, the first argument
 * provided to the memoized function is used as the map cache key. The `func`
 * is invoked with the `this` binding of the memoized function.
 *
 * **Note:** The cache is exposed as the `cache` property on the memoized
 * function. Its creation may be customized by replacing the `_.memoize.Cache`
 * constructor with one whose instances implement the
 * [`Map`](http://ecma-international.org/ecma-262/7.0/#sec-properties-of-the-map-prototype-object)
 * method interface of `clear`, `delete`, `get`, `has`, and `set`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to have its output memoized.
 * @param {Function} [resolver] The function to resolve the cache key.
 * @returns {Function} Returns the new memoized function.
 * @example
 *
 * var object = { 'a': 1, 'b': 2 };
 * var other = { 'c': 3, 'd': 4 };
 *
 * var values = _.memoize(_.values);
 * values(object);
 * // => [1, 2]
 *
 * values(other);
 * // => [3, 4]
 *
 * object.a = 2;
 * values(object);
 * // => [1, 2]
 *
 * // Modify the result cache.
 * values.cache.set(object, ['a', 'b']);
 * values(object);
 * // => ['a', 'b']
 *
 * // Replace `_.memoize.Cache`.
 * _.memoize.Cache = WeakMap;
 */
function memoize$1(func, resolver) {
  if (typeof func != 'function' || (resolver != null && typeof resolver != 'function')) {
    throw new TypeError(FUNC_ERROR_TEXT$1);
  }
  var memoized = function() {
    var args = arguments,
        key = resolver ? resolver.apply(this, args) : args[0],
        cache = memoized.cache;

    if (cache.has(key)) {
      return cache.get(key);
    }
    var result = func.apply(this, args);
    memoized.cache = cache.set(key, result) || cache;
    return result;
  };
  memoized.cache = new (memoize$1.Cache || MapCache);
  return memoized;
}

// Expose `MapCache`.
memoize$1.Cache = MapCache;

var memoize_1 = memoize$1;

var memoize = memoize_1;

/** Used as the maximum memoize cache size. */
var MAX_MEMOIZE_SIZE = 500;

/**
 * A specialized version of `_.memoize` which clears the memoized function's
 * cache when it exceeds `MAX_MEMOIZE_SIZE`.
 *
 * @private
 * @param {Function} func The function to have its output memoized.
 * @returns {Function} Returns the new memoized function.
 */
function memoizeCapped$1(func) {
  var result = memoize(func, function(key) {
    if (cache.size === MAX_MEMOIZE_SIZE) {
      cache.clear();
    }
    return key;
  });

  var cache = result.cache;
  return result;
}

var _memoizeCapped = memoizeCapped$1;

var memoizeCapped = _memoizeCapped;

/** Used to match property names within property paths. */
var rePropName = /[^.[\]]+|\[(?:(-?\d+(?:\.\d+)?)|(["'])((?:(?!\2)[^\\]|\\.)*?)\2)\]|(?=(?:\.|\[\])(?:\.|\[\]|$))/g;

/** Used to match backslashes in property paths. */
var reEscapeChar = /\\(\\)?/g;

/**
 * Converts `string` to a property path array.
 *
 * @private
 * @param {string} string The string to convert.
 * @returns {Array} Returns the property path array.
 */
var stringToPath$2 = memoizeCapped(function(string) {
  var result = [];
  if (string.charCodeAt(0) === 46 /* . */) {
    result.push('');
  }
  string.replace(rePropName, function(match, number, quote, subString) {
    result.push(quote ? subString.replace(reEscapeChar, '$1') : (number || match));
  });
  return result;
});

var _stringToPath = stringToPath$2;

/**
 * A specialized version of `_.map` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */

function arrayMap$3(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length,
      result = Array(length);

  while (++index < length) {
    result[index] = iteratee(array[index], index, array);
  }
  return result;
}

var _arrayMap = arrayMap$3;

var Symbol$2 = _Symbol,
    arrayMap$2 = _arrayMap,
    isArray$8 = isArray_1,
    isSymbol$3 = isSymbol_1;

/** Used as references for various `Number` constants. */
var INFINITY$2 = 1 / 0;

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol$2 ? Symbol$2.prototype : undefined,
    symbolToString = symbolProto ? symbolProto.toString : undefined;

/**
 * The base implementation of `_.toString` which doesn't convert nullish
 * values to empty strings.
 *
 * @private
 * @param {*} value The value to process.
 * @returns {string} Returns the string.
 */
function baseToString$1(value) {
  // Exit early for strings to avoid a performance hit in some environments.
  if (typeof value == 'string') {
    return value;
  }
  if (isArray$8(value)) {
    // Recursively convert values (susceptible to call stack limits).
    return arrayMap$2(value, baseToString$1) + '';
  }
  if (isSymbol$3(value)) {
    return symbolToString ? symbolToString.call(value) : '';
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$2) ? '-0' : result;
}

var _baseToString = baseToString$1;

var baseToString = _baseToString;

/**
 * Converts `value` to a string. An empty string is returned for `null`
 * and `undefined` values. The sign of `-0` is preserved.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 * @example
 *
 * _.toString(null);
 * // => ''
 *
 * _.toString(-0);
 * // => '-0'
 *
 * _.toString([1, 2, 3]);
 * // => '1,2,3'
 */
function toString$2(value) {
  return value == null ? '' : baseToString(value);
}

var toString_1 = toString$2;

var isArray$7 = isArray_1,
    isKey$2 = _isKey,
    stringToPath$1 = _stringToPath,
    toString$1 = toString_1;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath$2(value, object) {
  if (isArray$7(value)) {
    return value;
  }
  return isKey$2(value, object) ? [value] : stringToPath$1(toString$1(value));
}

var _castPath = castPath$2;

var isSymbol$2 = isSymbol_1;

/** Used as references for various `Number` constants. */
var INFINITY$1 = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey$5(value) {
  if (typeof value == 'string' || isSymbol$2(value)) {
    return value;
  }
  var result = (value + '');
  return (result == '0' && (1 / value) == -INFINITY$1) ? '-0' : result;
}

var _toKey = toKey$5;

var castPath$1 = _castPath,
    toKey$4 = _toKey;

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet$3(object, path) {
  path = castPath$1(path, object);

  var index = 0,
      length = path.length;

  while (object != null && index < length) {
    object = object[toKey$4(path[index++])];
  }
  return (index && index == length) ? object : undefined;
}

var _baseGet = baseGet$3;

var baseGet$2 = _baseGet;

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get$1(object, path, defaultValue) {
  var result = object == null ? undefined : baseGet$2(object, path);
  return result === undefined ? defaultValue : result;
}

var get_1 = get$1;

/**
 * The base implementation of `_.hasIn` without support for deep paths.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {Array|string} key The key to check.
 * @returns {boolean} Returns `true` if `key` exists, else `false`.
 */

function baseHasIn$1(object, key) {
  return object != null && key in Object(object);
}

var _baseHasIn = baseHasIn$1;

var castPath = _castPath,
    isArguments$2 = isArguments_1,
    isArray$6 = isArray_1,
    isIndex$1 = _isIndex,
    isLength = isLength_1,
    toKey$3 = _toKey;

/**
 * Checks if `path` exists on `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @param {Function} hasFunc The function to check properties.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 */
function hasPath$1(object, path, hasFunc) {
  path = castPath(path, object);

  var index = -1,
      length = path.length,
      result = false;

  while (++index < length) {
    var key = toKey$3(path[index]);
    if (!(result = object != null && hasFunc(object, key))) {
      break;
    }
    object = object[key];
  }
  if (result || ++index != length) {
    return result;
  }
  length = object == null ? 0 : object.length;
  return !!length && isLength(length) && isIndex$1(key, length) &&
    (isArray$6(object) || isArguments$2(object));
}

var _hasPath = hasPath$1;

var baseHasIn = _baseHasIn,
    hasPath = _hasPath;

/**
 * Checks if `path` is a direct or inherited property of `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path to check.
 * @returns {boolean} Returns `true` if `path` exists, else `false`.
 * @example
 *
 * var object = _.create({ 'a': _.create({ 'b': 2 }) });
 *
 * _.hasIn(object, 'a');
 * // => true
 *
 * _.hasIn(object, 'a.b');
 * // => true
 *
 * _.hasIn(object, ['a', 'b']);
 * // => true
 *
 * _.hasIn(object, 'b');
 * // => false
 */
function hasIn$1(object, path) {
  return object != null && hasPath(object, path, baseHasIn);
}

var hasIn_1 = hasIn$1;

var baseIsEqual = _baseIsEqual,
    get = get_1,
    hasIn = hasIn_1,
    isKey$1 = _isKey,
    isStrictComparable = _isStrictComparable,
    matchesStrictComparable = _matchesStrictComparable,
    toKey$2 = _toKey;

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1,
    COMPARE_UNORDERED_FLAG = 2;

/**
 * The base implementation of `_.matchesProperty` which doesn't clone `srcValue`.
 *
 * @private
 * @param {string} path The path of the property to get.
 * @param {*} srcValue The value to match.
 * @returns {Function} Returns the new spec function.
 */
function baseMatchesProperty$1(path, srcValue) {
  if (isKey$1(path) && isStrictComparable(srcValue)) {
    return matchesStrictComparable(toKey$2(path), srcValue);
  }
  return function(object) {
    var objValue = get(object, path);
    return (objValue === undefined && objValue === srcValue)
      ? hasIn(object, path)
      : baseIsEqual(srcValue, objValue, COMPARE_PARTIAL_FLAG | COMPARE_UNORDERED_FLAG);
  };
}

var _baseMatchesProperty = baseMatchesProperty$1;

/**
 * The base implementation of `_.property` without support for deep paths.
 *
 * @private
 * @param {string} key The key of the property to get.
 * @returns {Function} Returns the new accessor function.
 */

function baseProperty$1(key) {
  return function(object) {
    return object == null ? undefined : object[key];
  };
}

var _baseProperty = baseProperty$1;

var baseGet$1 = _baseGet;

/**
 * A specialized version of `baseProperty` which supports deep paths.
 *
 * @private
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 */
function basePropertyDeep$1(path) {
  return function(object) {
    return baseGet$1(object, path);
  };
}

var _basePropertyDeep = basePropertyDeep$1;

var baseProperty = _baseProperty,
    basePropertyDeep = _basePropertyDeep,
    isKey = _isKey,
    toKey$1 = _toKey;

/**
 * Creates a function that returns the value at `path` of a given object.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {Array|string} path The path of the property to get.
 * @returns {Function} Returns the new accessor function.
 * @example
 *
 * var objects = [
 *   { 'a': { 'b': 2 } },
 *   { 'a': { 'b': 1 } }
 * ];
 *
 * _.map(objects, _.property('a.b'));
 * // => [2, 1]
 *
 * _.map(_.sortBy(objects, _.property(['a', 'b'])), 'a.b');
 * // => [1, 2]
 */
function property$1(path) {
  return isKey(path) ? baseProperty(toKey$1(path)) : basePropertyDeep(path);
}

var property_1 = property$1;

var baseMatches = _baseMatches,
    baseMatchesProperty = _baseMatchesProperty,
    identity$2 = identity_1,
    isArray$5 = isArray_1,
    property = property_1;

/**
 * The base implementation of `_.iteratee`.
 *
 * @private
 * @param {*} [value=_.identity] The value to convert to an iteratee.
 * @returns {Function} Returns the iteratee.
 */
function baseIteratee$3(value) {
  // Don't store the `typeof` result in a variable to avoid a JIT bug in Safari 9.
  // See https://bugs.webkit.org/show_bug.cgi?id=156034 for more details.
  if (typeof value == 'function') {
    return value;
  }
  if (value == null) {
    return identity$2;
  }
  if (typeof value == 'object') {
    return isArray$5(value)
      ? baseMatchesProperty(value[0], value[1])
      : baseMatches(value);
  }
  return property(value);
}

var _baseIteratee = baseIteratee$3;

var baseClone = _baseClone,
    baseIteratee$2 = _baseIteratee;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG = 1;

/**
 * Creates a function that invokes `func` with the arguments of the created
 * function. If `func` is a property name, the created function returns the
 * property value for a given element. If `func` is an array or object, the
 * created function returns `true` for elements that contain the equivalent
 * source properties, otherwise it returns `false`.
 *
 * @static
 * @since 4.0.0
 * @memberOf _
 * @category Util
 * @param {*} [func=_.identity] The value to convert to a callback.
 * @returns {Function} Returns the callback.
 * @example
 *
 * var users = [
 *   { 'user': 'barney', 'age': 36, 'active': true },
 *   { 'user': 'fred',   'age': 40, 'active': false }
 * ];
 *
 * // The `_.matches` iteratee shorthand.
 * _.filter(users, _.iteratee({ 'user': 'barney', 'active': true }));
 * // => [{ 'user': 'barney', 'age': 36, 'active': true }]
 *
 * // The `_.matchesProperty` iteratee shorthand.
 * _.filter(users, _.iteratee(['user', 'fred']));
 * // => [{ 'user': 'fred', 'age': 40 }]
 *
 * // The `_.property` iteratee shorthand.
 * _.map(users, _.iteratee('user'));
 * // => ['barney', 'fred']
 *
 * // Create custom iteratee shorthands.
 * _.iteratee = _.wrap(_.iteratee, function(iteratee, func) {
 *   return !_.isRegExp(func) ? iteratee(func) : function(string) {
 *     return func.test(string);
 *   };
 * });
 *
 * _.filter(['abc', 'def'], /ef/);
 * // => ['def']
 */
function iteratee(func) {
  return baseIteratee$2(typeof func == 'function' ? func : baseClone(func, CLONE_DEEP_FLAG));
}

var iteratee_1 = iteratee;

var Symbol$1 = _Symbol,
    isArguments$1 = isArguments_1,
    isArray$4 = isArray_1;

/** Built-in value references. */
var spreadableSymbol = Symbol$1 ? Symbol$1.isConcatSpreadable : undefined;

/**
 * Checks if `value` is a flattenable `arguments` object or array.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is flattenable, else `false`.
 */
function isFlattenable$1(value) {
  return isArray$4(value) || isArguments$1(value) ||
    !!(spreadableSymbol && value && value[spreadableSymbol]);
}

var _isFlattenable = isFlattenable$1;

var arrayPush = _arrayPush,
    isFlattenable = _isFlattenable;

/**
 * The base implementation of `_.flatten` with support for restricting flattening.
 *
 * @private
 * @param {Array} array The array to flatten.
 * @param {number} depth The maximum recursion depth.
 * @param {boolean} [predicate=isFlattenable] The function invoked per iteration.
 * @param {boolean} [isStrict] Restrict to values that pass `predicate` checks.
 * @param {Array} [result=[]] The initial result value.
 * @returns {Array} Returns the new flattened array.
 */
function baseFlatten$2(array, depth, predicate, isStrict, result) {
  var index = -1,
      length = array.length;

  predicate || (predicate = isFlattenable);
  result || (result = []);

  while (++index < length) {
    var value = array[index];
    if (depth > 0 && predicate(value)) {
      if (depth > 1) {
        // Recursively flatten arrays (susceptible to call stack limits).
        baseFlatten$2(value, depth - 1, predicate, isStrict, result);
      } else {
        arrayPush(result, value);
      }
    } else if (!isStrict) {
      result[result.length] = value;
    }
  }
  return result;
}

var _baseFlatten = baseFlatten$2;

var baseFlatten$1 = _baseFlatten;

/**
 * Flattens `array` a single level deep.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to flatten.
 * @returns {Array} Returns the new flattened array.
 * @example
 *
 * _.flatten([1, [2, [3, [4]], 5]]);
 * // => [1, 2, [3, [4]], 5]
 */
function flatten$1(array) {
  var length = array == null ? 0 : array.length;
  return length ? baseFlatten$1(array, 1) : [];
}

var flatten_1 = flatten$1;

var apply = _apply;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest$2(func, start, transform) {
  start = nativeMax(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return apply(func, this, otherArgs);
  };
}

var _overRest = overRest$2;

var flatten = flatten_1,
    overRest$1 = _overRest,
    setToString$1 = _setToString;

/**
 * A specialized version of `baseRest` which flattens the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @returns {Function} Returns the new function.
 */
function flatRest$2(func) {
  return setToString$1(overRest$1(func, undefined, flatten), func + '');
}

var _flatRest = flatRest$2;

var createWrap = _createWrap,
    flatRest$1 = _flatRest;

/** Used to compose bitmasks for function metadata. */
var WRAP_REARG_FLAG$1 = 256;

/**
 * Creates a function that invokes `func` with arguments arranged according
 * to the specified `indexes` where the argument value at the first index is
 * provided as the first argument, the argument value at the second index is
 * provided as the second argument, and so on.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Function
 * @param {Function} func The function to rearrange arguments for.
 * @param {...(number|number[])} indexes The arranged argument indexes.
 * @returns {Function} Returns the new function.
 * @example
 *
 * var rearged = _.rearg(function(a, b, c) {
 *   return [a, b, c];
 * }, [2, 0, 1]);
 *
 * rearged('b', 'c', 'a')
 * // => ['a', 'b', 'c']
 */
var rearg = flatRest$1(function(func, indexes) {
  return createWrap(func, WRAP_REARG_FLAG$1, undefined, undefined, undefined, indexes);
});

var rearg_1 = rearg;

var arrayMap$1 = _arrayMap,
    copyArray$1 = _copyArray,
    isArray$3 = isArray_1,
    isSymbol$1 = isSymbol_1,
    stringToPath = _stringToPath,
    toKey = _toKey,
    toString = toString_1;

/**
 * Converts `value` to a property path array.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Util
 * @param {*} value The value to convert.
 * @returns {Array} Returns the new property path array.
 * @example
 *
 * _.toPath('a.b.c');
 * // => ['a', 'b', 'c']
 *
 * _.toPath('a[0].b.c');
 * // => ['a', '0', 'b', 'c']
 */
function toPath(value) {
  if (isArray$3(value)) {
    return arrayMap$1(value, toKey);
  }
  return isSymbol$1(value) ? [value] : copyArray$1(stringToPath(toString(value)));
}

var toPath_1 = toPath;

var _util = {
  'ary': ary_1,
  'assign': _baseAssign,
  'clone': clone_1,
  'curry': curry_1,
  'forEach': _arrayEach,
  'isArray': isArray_1,
  'isError': isError_1,
  'isFunction': isFunction_1,
  'isWeakMap': isWeakMap_1,
  'iteratee': iteratee_1,
  'keys': _baseKeys,
  'rearg': rearg_1,
  'toInteger': toInteger_1,
  'toPath': toPath_1
};

var baseConvert = _baseConvert,
    util = _util;

/**
 * Converts `func` of `name` to an immutable auto-curried iteratee-first data-last
 * version with conversion `options` applied. If `name` is an object its methods
 * will be converted.
 *
 * @param {string} name The name of the function to wrap.
 * @param {Function} [func] The function to wrap.
 * @param {Object} [options] The options object. See `baseConvert` for more details.
 * @returns {Function|Object} Returns the converted function or object.
 */
function convert$4(name, func, options) {
  return baseConvert(util, name, func, options);
}

var convert_1 = convert$4;

var LodashWrapper = _LodashWrapper,
    flatRest = _flatRest,
    getData = _getData,
    getFuncName = _getFuncName,
    isArray$2 = isArray_1,
    isLaziable = _isLaziable;

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/** Used to compose bitmasks for function metadata. */
var WRAP_CURRY_FLAG = 8,
    WRAP_PARTIAL_FLAG = 32,
    WRAP_ARY_FLAG = 128,
    WRAP_REARG_FLAG = 256;

/**
 * Creates a `_.flow` or `_.flowRight` function.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new flow function.
 */
function createFlow$1(fromRight) {
  return flatRest(function(funcs) {
    var length = funcs.length,
        index = length,
        prereq = LodashWrapper.prototype.thru;

    if (fromRight) {
      funcs.reverse();
    }
    while (index--) {
      var func = funcs[index];
      if (typeof func != 'function') {
        throw new TypeError(FUNC_ERROR_TEXT);
      }
      if (prereq && !wrapper && getFuncName(func) == 'wrapper') {
        var wrapper = new LodashWrapper([], true);
      }
    }
    index = wrapper ? index : length;
    while (++index < length) {
      func = funcs[index];

      var funcName = getFuncName(func),
          data = funcName == 'wrapper' ? getData(func) : undefined;

      if (data && isLaziable(data[0]) &&
            data[1] == (WRAP_ARY_FLAG | WRAP_CURRY_FLAG | WRAP_PARTIAL_FLAG | WRAP_REARG_FLAG) &&
            !data[4].length && data[9] == 1
          ) {
        wrapper = wrapper[getFuncName(data[0])].apply(wrapper, data[3]);
      } else {
        wrapper = (func.length == 1 && isLaziable(func))
          ? wrapper[funcName]()
          : wrapper.thru(func);
      }
    }
    return function() {
      var args = arguments,
          value = args[0];

      if (wrapper && args.length == 1 && isArray$2(value)) {
        return wrapper.plant(value).value();
      }
      var index = 0,
          result = length ? funcs[index].apply(this, args) : value;

      while (++index < length) {
        result = funcs[index].call(this, result);
      }
      return result;
    };
  });
}

var _createFlow = createFlow$1;

var createFlow = _createFlow;

/**
 * Creates a function that returns the result of invoking the given functions
 * with the `this` binding of the created function, where each successive
 * invocation is supplied the return value of the previous.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Util
 * @param {...(Function|Function[])} [funcs] The functions to invoke.
 * @returns {Function} Returns the new composite function.
 * @see _.flowRight
 * @example
 *
 * function square(n) {
 *   return n * n;
 * }
 *
 * var addSquare = _.flow([_.add, square]);
 * addSquare(1, 2);
 * // => 9
 */
var flow$1 = createFlow();

var flow_1 = flow$1;

var convert$3 = convert_1,
    func$3 = convert$3('flow', flow_1);

func$3.placeholder = placeholder;
var flow = func$3;

/**
 * Creates an array with all falsey values removed. The values `false`, `null`,
 * `0`, `""`, `undefined`, and `NaN` are falsey.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Array
 * @param {Array} array The array to compact.
 * @returns {Array} Returns the new array of filtered values.
 * @example
 *
 * _.compact([0, 1, false, 2, '', 3]);
 * // => [1, 2, 3]
 */

function compact$1(array) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (value) {
      result[resIndex++] = value;
    }
  }
  return result;
}

var compact_1 = compact$1;

var _falseOptions = {
  'cap': false,
  'curry': false,
  'fixed': false,
  'immutable': false,
  'rearg': false
};

var convert$2 = convert_1,
    func$2 = convert$2('compact', compact_1, _falseOptions);

func$2.placeholder = placeholder;
var compact = func$2;

/**
 * This function is like `arrayIncludes` except that it accepts a comparator.
 *
 * @private
 * @param {Array} [array] The array to inspect.
 * @param {*} target The value to search for.
 * @param {Function} comparator The comparator invoked per element.
 * @returns {boolean} Returns `true` if `target` is found, else `false`.
 */

function arrayIncludesWith$1(array, value, comparator) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (comparator(value, array[index])) {
      return true;
    }
  }
  return false;
}

var _arrayIncludesWith = arrayIncludesWith$1;

var Set = _Set,
    noop = noop_1,
    setToArray$1 = _setToArray;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/**
 * Creates a set object of `values`.
 *
 * @private
 * @param {Array} values The values to add to the set.
 * @returns {Object} Returns the new set.
 */
var createSet$1 = !(Set && (1 / setToArray$1(new Set([,-0]))[1]) == INFINITY) ? noop : function(values) {
  return new Set(values);
};

var _createSet = createSet$1;

var SetCache = _SetCache,
    arrayIncludes = _arrayIncludes,
    arrayIncludesWith = _arrayIncludesWith,
    cacheHas = _cacheHas,
    createSet = _createSet,
    setToArray = _setToArray;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * The base implementation of `_.uniqBy` without support for iteratee shorthands.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {Function} [iteratee] The iteratee invoked per element.
 * @param {Function} [comparator] The comparator invoked per element.
 * @returns {Array} Returns the new duplicate free array.
 */
function baseUniq$1(array, iteratee, comparator) {
  var index = -1,
      includes = arrayIncludes,
      length = array.length,
      isCommon = true,
      result = [],
      seen = result;

  if (comparator) {
    isCommon = false;
    includes = arrayIncludesWith;
  }
  else if (length >= LARGE_ARRAY_SIZE) {
    var set = iteratee ? null : createSet(array);
    if (set) {
      return setToArray(set);
    }
    isCommon = false;
    includes = cacheHas;
    seen = new SetCache;
  }
  else {
    seen = iteratee ? [] : result;
  }
  outer:
  while (++index < length) {
    var value = array[index],
        computed = iteratee ? iteratee(value) : value;

    value = (comparator || value !== 0) ? value : 0;
    if (isCommon && computed === computed) {
      var seenIndex = seen.length;
      while (seenIndex--) {
        if (seen[seenIndex] === computed) {
          continue outer;
        }
      }
      if (iteratee) {
        seen.push(computed);
      }
      result.push(value);
    }
    else if (!includes(seen, computed, comparator)) {
      if (seen !== result) {
        seen.push(computed);
      }
      result.push(value);
    }
  }
  return result;
}

var _baseUniq = baseUniq$1;

var baseIteratee$1 = _baseIteratee,
    baseUniq = _baseUniq;

/**
 * This method is like `_.uniq` except that it accepts `iteratee` which is
 * invoked for each element in `array` to generate the criterion by which
 * uniqueness is computed. The order of result values is determined by the
 * order they occur in the array. The iteratee is invoked with one argument:
 * (value).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Array
 * @param {Array} array The array to inspect.
 * @param {Function} [iteratee=_.identity] The iteratee invoked per element.
 * @returns {Array} Returns the new duplicate free array.
 * @example
 *
 * _.uniqBy([2.1, 1.2, 2.3], Math.floor);
 * // => [2.1, 1.2]
 *
 * // The `_.property` iteratee shorthand.
 * _.uniqBy([{ 'x': 1 }, { 'x': 2 }, { 'x': 1 }], 'x');
 * // => [{ 'x': 1 }, { 'x': 2 }]
 */
function uniqBy$1(array, iteratee) {
  return (array && array.length) ? baseUniq(array, baseIteratee$1(iteratee)) : [];
}

var uniqBy_1 = uniqBy$1;

var convert$1 = convert_1,
    func$1 = convert$1('uniqBy', uniqBy_1);

func$1.placeholder = placeholder;
var uniqBy = func$1;

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */

function createBaseFor$1(fromRight) {
  return function(object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[fromRight ? length : ++index];
      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }
    return object;
  };
}

var _createBaseFor = createBaseFor$1;

var createBaseFor = _createBaseFor;

/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */
var baseFor$2 = createBaseFor();

var _baseFor = baseFor$2;

var baseFor$1 = _baseFor,
    keys = keys_1;

/**
 * The base implementation of `_.forOwn` without support for iteratee shorthands.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Object} Returns `object`.
 */
function baseForOwn$1(object, iteratee) {
  return object && baseFor$1(object, iteratee, keys);
}

var _baseForOwn = baseForOwn$1;

var isArrayLike$3 = isArrayLike_1;

/**
 * Creates a `baseEach` or `baseEachRight` function.
 *
 * @private
 * @param {Function} eachFunc The function to iterate over a collection.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseEach$1(eachFunc, fromRight) {
  return function(collection, iteratee) {
    if (collection == null) {
      return collection;
    }
    if (!isArrayLike$3(collection)) {
      return eachFunc(collection, iteratee);
    }
    var length = collection.length,
        index = fromRight ? length : -1,
        iterable = Object(collection);

    while ((fromRight ? index-- : ++index < length)) {
      if (iteratee(iterable[index], index, iterable) === false) {
        break;
      }
    }
    return collection;
  };
}

var _createBaseEach = createBaseEach$1;

var baseForOwn = _baseForOwn,
    createBaseEach = _createBaseEach;

/**
 * The base implementation of `_.forEach` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array|Object} Returns `collection`.
 */
var baseEach$1 = createBaseEach(baseForOwn);

var _baseEach = baseEach$1;

var baseEach = _baseEach,
    isArrayLike$2 = isArrayLike_1;

/**
 * The base implementation of `_.map` without support for iteratee shorthands.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the new mapped array.
 */
function baseMap$1(collection, iteratee) {
  var index = -1,
      result = isArrayLike$2(collection) ? Array(collection.length) : [];

  baseEach(collection, function(value, key, collection) {
    result[++index] = iteratee(value, key, collection);
  });
  return result;
}

var _baseMap = baseMap$1;

/**
 * The base implementation of `_.sortBy` which uses `comparer` to define the
 * sort order of `array` and replaces criteria objects with their corresponding
 * values.
 *
 * @private
 * @param {Array} array The array to sort.
 * @param {Function} comparer The function to define sort order.
 * @returns {Array} Returns `array`.
 */

function baseSortBy$1(array, comparer) {
  var length = array.length;

  array.sort(comparer);
  while (length--) {
    array[length] = array[length].value;
  }
  return array;
}

var _baseSortBy = baseSortBy$1;

var isSymbol = isSymbol_1;

/**
 * Compares values to sort them in ascending order.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {number} Returns the sort order indicator for `value`.
 */
function compareAscending$1(value, other) {
  if (value !== other) {
    var valIsDefined = value !== undefined,
        valIsNull = value === null,
        valIsReflexive = value === value,
        valIsSymbol = isSymbol(value);

    var othIsDefined = other !== undefined,
        othIsNull = other === null,
        othIsReflexive = other === other,
        othIsSymbol = isSymbol(other);

    if ((!othIsNull && !othIsSymbol && !valIsSymbol && value > other) ||
        (valIsSymbol && othIsDefined && othIsReflexive && !othIsNull && !othIsSymbol) ||
        (valIsNull && othIsDefined && othIsReflexive) ||
        (!valIsDefined && othIsReflexive) ||
        !valIsReflexive) {
      return 1;
    }
    if ((!valIsNull && !valIsSymbol && !othIsSymbol && value < other) ||
        (othIsSymbol && valIsDefined && valIsReflexive && !valIsNull && !valIsSymbol) ||
        (othIsNull && valIsDefined && valIsReflexive) ||
        (!othIsDefined && valIsReflexive) ||
        !othIsReflexive) {
      return -1;
    }
  }
  return 0;
}

var _compareAscending = compareAscending$1;

var compareAscending = _compareAscending;

/**
 * Used by `_.orderBy` to compare multiple properties of a value to another
 * and stable sort them.
 *
 * If `orders` is unspecified, all values are sorted in ascending order. Otherwise,
 * specify an order of "desc" for descending or "asc" for ascending sort order
 * of corresponding values.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {boolean[]|string[]} orders The order to sort by for each property.
 * @returns {number} Returns the sort order indicator for `object`.
 */
function compareMultiple$1(object, other, orders) {
  var index = -1,
      objCriteria = object.criteria,
      othCriteria = other.criteria,
      length = objCriteria.length,
      ordersLength = orders.length;

  while (++index < length) {
    var result = compareAscending(objCriteria[index], othCriteria[index]);
    if (result) {
      if (index >= ordersLength) {
        return result;
      }
      var order = orders[index];
      return result * (order == 'desc' ? -1 : 1);
    }
  }
  // Fixes an `Array#sort` bug in the JS engine embedded in Adobe applications
  // that causes it, under certain circumstances, to provide the same value for
  // `object` and `other`. See https://github.com/jashkenas/underscore/pull/1247
  // for more details.
  //
  // This also ensures a stable sort in V8 and other engines.
  // See https://bugs.chromium.org/p/v8/issues/detail?id=90 for more details.
  return object.index - other.index;
}

var _compareMultiple = compareMultiple$1;

var arrayMap = _arrayMap,
    baseGet = _baseGet,
    baseIteratee = _baseIteratee,
    baseMap = _baseMap,
    baseSortBy = _baseSortBy,
    baseUnary = _baseUnary,
    compareMultiple = _compareMultiple,
    identity$1 = identity_1,
    isArray$1 = isArray_1;

/**
 * The base implementation of `_.orderBy` without param guards.
 *
 * @private
 * @param {Array|Object} collection The collection to iterate over.
 * @param {Function[]|Object[]|string[]} iteratees The iteratees to sort by.
 * @param {string[]} orders The sort orders of `iteratees`.
 * @returns {Array} Returns the new sorted array.
 */
function baseOrderBy$1(collection, iteratees, orders) {
  if (iteratees.length) {
    iteratees = arrayMap(iteratees, function(iteratee) {
      if (isArray$1(iteratee)) {
        return function(value) {
          return baseGet(value, iteratee.length === 1 ? iteratee[0] : iteratee);
        }
      }
      return iteratee;
    });
  } else {
    iteratees = [identity$1];
  }

  var index = -1;
  iteratees = arrayMap(iteratees, baseUnary(baseIteratee));

  var result = baseMap(collection, function(value, key, collection) {
    var criteria = arrayMap(iteratees, function(iteratee) {
      return iteratee(value);
    });
    return { 'criteria': criteria, 'index': ++index, 'value': value };
  });

  return baseSortBy(result, function(object, other) {
    return compareMultiple(object, other, orders);
  });
}

var _baseOrderBy = baseOrderBy$1;

var identity = identity_1,
    overRest = _overRest,
    setToString = _setToString;

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest$2(func, start) {
  return setToString(overRest(func, start, identity), func + '');
}

var _baseRest = baseRest$2;

var eq$1 = eq_1,
    isArrayLike$1 = isArrayLike_1,
    isIndex = _isIndex,
    isObject$2 = isObject_1;

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall$2(value, index, object) {
  if (!isObject$2(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike$1(object) && isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq$1(object[index], value);
  }
  return false;
}

var _isIterateeCall = isIterateeCall$2;

var baseFlatten = _baseFlatten,
    baseOrderBy = _baseOrderBy,
    baseRest$1 = _baseRest,
    isIterateeCall$1 = _isIterateeCall;

/**
 * Creates an array of elements, sorted in ascending order by the results of
 * running each element in a collection thru each iteratee. This method
 * performs a stable sort, that is, it preserves the original sort order of
 * equal elements. The iteratees are invoked with one argument: (value).
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Collection
 * @param {Array|Object} collection The collection to iterate over.
 * @param {...(Function|Function[])} [iteratees=[_.identity]]
 *  The iteratees to sort by.
 * @returns {Array} Returns the new sorted array.
 * @example
 *
 * var users = [
 *   { 'user': 'fred',   'age': 48 },
 *   { 'user': 'barney', 'age': 36 },
 *   { 'user': 'fred',   'age': 30 },
 *   { 'user': 'barney', 'age': 34 }
 * ];
 *
 * _.sortBy(users, [function(o) { return o.user; }]);
 * // => objects for [['barney', 36], ['barney', 34], ['fred', 48], ['fred', 30]]
 *
 * _.sortBy(users, ['user', 'age']);
 * // => objects for [['barney', 34], ['barney', 36], ['fred', 30], ['fred', 48]]
 */
var sortBy$1 = baseRest$1(function(collection, iteratees) {
  if (collection == null) {
    return [];
  }
  var length = iteratees.length;
  if (length > 1 && isIterateeCall$1(collection, iteratees[0], iteratees[1])) {
    iteratees = [];
  } else if (length > 2 && isIterateeCall$1(iteratees[0], iteratees[1], iteratees[2])) {
    iteratees = [iteratees[0]];
  }
  return baseOrderBy(collection, baseFlatten(iteratees, 1), []);
});

var sortBy_1 = sortBy$1;

var convert = convert_1,
    func = convert('sortBy', sortBy_1);

func.placeholder = placeholder;
var sortBy = func;

var browser = {exports: {}};

/**
 * Helpers.
 */

var s = 1000;
var m = s * 60;
var h = m * 60;
var d = h * 24;
var w = d * 7;
var y = d * 365.25;

/**
 * Parse or format the given `val`.
 *
 * Options:
 *
 *  - `long` verbose formatting [false]
 *
 * @param {String|Number} val
 * @param {Object} [options]
 * @throws {Error} throw an error if val is not a non-empty string or a number
 * @return {String|Number}
 * @api public
 */

var ms = function(val, options) {
  options = options || {};
  var type = typeof val;
  if (type === 'string' && val.length > 0) {
    return parse(val);
  } else if (type === 'number' && isFinite(val)) {
    return options.long ? fmtLong(val) : fmtShort(val);
  }
  throw new Error(
    'val is not a non-empty string or a valid number. val=' +
      JSON.stringify(val)
  );
};

/**
 * Parse the given `str` and return milliseconds.
 *
 * @param {String} str
 * @return {Number}
 * @api private
 */

function parse(str) {
  str = String(str);
  if (str.length > 100) {
    return;
  }
  var match = /^(-?(?:\d+)?\.?\d+) *(milliseconds?|msecs?|ms|seconds?|secs?|s|minutes?|mins?|m|hours?|hrs?|h|days?|d|weeks?|w|years?|yrs?|y)?$/i.exec(
    str
  );
  if (!match) {
    return;
  }
  var n = parseFloat(match[1]);
  var type = (match[2] || 'ms').toLowerCase();
  switch (type) {
    case 'years':
    case 'year':
    case 'yrs':
    case 'yr':
    case 'y':
      return n * y;
    case 'weeks':
    case 'week':
    case 'w':
      return n * w;
    case 'days':
    case 'day':
    case 'd':
      return n * d;
    case 'hours':
    case 'hour':
    case 'hrs':
    case 'hr':
    case 'h':
      return n * h;
    case 'minutes':
    case 'minute':
    case 'mins':
    case 'min':
    case 'm':
      return n * m;
    case 'seconds':
    case 'second':
    case 'secs':
    case 'sec':
    case 's':
      return n * s;
    case 'milliseconds':
    case 'millisecond':
    case 'msecs':
    case 'msec':
    case 'ms':
      return n;
    default:
      return undefined;
  }
}

/**
 * Short format for `ms`.
 *
 * @param {Number} ms
 * @return {String}
 * @api private
 */

function fmtShort(ms) {
  var msAbs = Math.abs(ms);
  if (msAbs >= d) {
    return Math.round(ms / d) + 'd';
  }
  if (msAbs >= h) {
    return Math.round(ms / h) + 'h';
  }
  if (msAbs >= m) {
    return Math.round(ms / m) + 'm';
  }
  if (msAbs >= s) {
    return Math.round(ms / s) + 's';
  }
  return ms + 'ms';
}

/**
 * Long format for `ms`.
 *
 * @param {Number} ms
 * @return {String}
 * @api private
 */

function fmtLong(ms) {
  var msAbs = Math.abs(ms);
  if (msAbs >= d) {
    return plural(ms, msAbs, d, 'day');
  }
  if (msAbs >= h) {
    return plural(ms, msAbs, h, 'hour');
  }
  if (msAbs >= m) {
    return plural(ms, msAbs, m, 'minute');
  }
  if (msAbs >= s) {
    return plural(ms, msAbs, s, 'second');
  }
  return ms + ' ms';
}

/**
 * Pluralization helper.
 */

function plural(ms, msAbs, n, name) {
  var isPlural = msAbs >= n * 1.5;
  return Math.round(ms / n) + ' ' + name + (isPlural ? 's' : '');
}

/**
 * This is the common logic for both the Node.js and web browser
 * implementations of `debug()`.
 */

function setup(env) {
	createDebug.debug = createDebug;
	createDebug.default = createDebug;
	createDebug.coerce = coerce;
	createDebug.disable = disable;
	createDebug.enable = enable;
	createDebug.enabled = enabled;
	createDebug.humanize = ms;
	createDebug.destroy = destroy;

	Object.keys(env).forEach(key => {
		createDebug[key] = env[key];
	});

	/**
	* The currently active debug mode names, and names to skip.
	*/

	createDebug.names = [];
	createDebug.skips = [];

	/**
	* Map of special "%n" handling functions, for the debug "format" argument.
	*
	* Valid key names are a single, lower or upper-case letter, i.e. "n" and "N".
	*/
	createDebug.formatters = {};

	/**
	* Selects a color for a debug namespace
	* @param {String} namespace The namespace string for the for the debug instance to be colored
	* @return {Number|String} An ANSI color code for the given namespace
	* @api private
	*/
	function selectColor(namespace) {
		let hash = 0;

		for (let i = 0; i < namespace.length; i++) {
			hash = ((hash << 5) - hash) + namespace.charCodeAt(i);
			hash |= 0; // Convert to 32bit integer
		}

		return createDebug.colors[Math.abs(hash) % createDebug.colors.length];
	}
	createDebug.selectColor = selectColor;

	/**
	* Create a debugger with the given `namespace`.
	*
	* @param {String} namespace
	* @return {Function}
	* @api public
	*/
	function createDebug(namespace) {
		let prevTime;
		let enableOverride = null;
		let namespacesCache;
		let enabledCache;

		function debug(...args) {
			// Disabled?
			if (!debug.enabled) {
				return;
			}

			const self = debug;

			// Set `diff` timestamp
			const curr = Number(new Date());
			const ms = curr - (prevTime || curr);
			self.diff = ms;
			self.prev = prevTime;
			self.curr = curr;
			prevTime = curr;

			args[0] = createDebug.coerce(args[0]);

			if (typeof args[0] !== 'string') {
				// Anything else let's inspect with %O
				args.unshift('%O');
			}

			// Apply any `formatters` transformations
			let index = 0;
			args[0] = args[0].replace(/%([a-zA-Z%])/g, (match, format) => {
				// If we encounter an escaped % then don't increase the array index
				if (match === '%%') {
					return '%';
				}
				index++;
				const formatter = createDebug.formatters[format];
				if (typeof formatter === 'function') {
					const val = args[index];
					match = formatter.call(self, val);

					// Now we need to remove `args[index]` since it's inlined in the `format`
					args.splice(index, 1);
					index--;
				}
				return match;
			});

			// Apply env-specific formatting (colors, etc.)
			createDebug.formatArgs.call(self, args);

			const logFn = self.log || createDebug.log;
			logFn.apply(self, args);
		}

		debug.namespace = namespace;
		debug.useColors = createDebug.useColors();
		debug.color = createDebug.selectColor(namespace);
		debug.extend = extend;
		debug.destroy = createDebug.destroy; // XXX Temporary. Will be removed in the next major release.

		Object.defineProperty(debug, 'enabled', {
			enumerable: true,
			configurable: false,
			get: () => {
				if (enableOverride !== null) {
					return enableOverride;
				}
				if (namespacesCache !== createDebug.namespaces) {
					namespacesCache = createDebug.namespaces;
					enabledCache = createDebug.enabled(namespace);
				}

				return enabledCache;
			},
			set: v => {
				enableOverride = v;
			}
		});

		// Env-specific initialization logic for debug instances
		if (typeof createDebug.init === 'function') {
			createDebug.init(debug);
		}

		return debug;
	}

	function extend(namespace, delimiter) {
		const newDebug = createDebug(this.namespace + (typeof delimiter === 'undefined' ? ':' : delimiter) + namespace);
		newDebug.log = this.log;
		return newDebug;
	}

	/**
	* Enables a debug mode by namespaces. This can include modes
	* separated by a colon and wildcards.
	*
	* @param {String} namespaces
	* @api public
	*/
	function enable(namespaces) {
		createDebug.save(namespaces);
		createDebug.namespaces = namespaces;

		createDebug.names = [];
		createDebug.skips = [];

		let i;
		const split = (typeof namespaces === 'string' ? namespaces : '').split(/[\s,]+/);
		const len = split.length;

		for (i = 0; i < len; i++) {
			if (!split[i]) {
				// ignore empty strings
				continue;
			}

			namespaces = split[i].replace(/\*/g, '.*?');

			if (namespaces[0] === '-') {
				createDebug.skips.push(new RegExp('^' + namespaces.substr(1) + '$'));
			} else {
				createDebug.names.push(new RegExp('^' + namespaces + '$'));
			}
		}
	}

	/**
	* Disable debug output.
	*
	* @return {String} namespaces
	* @api public
	*/
	function disable() {
		const namespaces = [
			...createDebug.names.map(toNamespace),
			...createDebug.skips.map(toNamespace).map(namespace => '-' + namespace)
		].join(',');
		createDebug.enable('');
		return namespaces;
	}

	/**
	* Returns true if the given mode name is enabled, false otherwise.
	*
	* @param {String} name
	* @return {Boolean}
	* @api public
	*/
	function enabled(name) {
		if (name[name.length - 1] === '*') {
			return true;
		}

		let i;
		let len;

		for (i = 0, len = createDebug.skips.length; i < len; i++) {
			if (createDebug.skips[i].test(name)) {
				return false;
			}
		}

		for (i = 0, len = createDebug.names.length; i < len; i++) {
			if (createDebug.names[i].test(name)) {
				return true;
			}
		}

		return false;
	}

	/**
	* Convert regexp to namespace
	*
	* @param {RegExp} regxep
	* @return {String} namespace
	* @api private
	*/
	function toNamespace(regexp) {
		return regexp.toString()
			.substring(2, regexp.toString().length - 2)
			.replace(/\.\*\?$/, '*');
	}

	/**
	* Coerce `val`.
	*
	* @param {Mixed} val
	* @return {Mixed}
	* @api private
	*/
	function coerce(val) {
		if (val instanceof Error) {
			return val.stack || val.message;
		}
		return val;
	}

	/**
	* XXX DO NOT USE. This is a temporary stub function.
	* XXX It WILL be removed in the next major release.
	*/
	function destroy() {
		console.warn('Instance method `debug.destroy()` is deprecated and no longer does anything. It will be removed in the next major version of `debug`.');
	}

	createDebug.enable(createDebug.load());

	return createDebug;
}

var common = setup;

/* eslint-env browser */

(function (module, exports) {
/**
 * This is the web browser implementation of `debug()`.
 */

exports.formatArgs = formatArgs;
exports.save = save;
exports.load = load;
exports.useColors = useColors;
exports.storage = localstorage();
exports.destroy = (() => {
	let warned = false;

	return () => {
		if (!warned) {
			warned = true;
			console.warn('Instance method `debug.destroy()` is deprecated and no longer does anything. It will be removed in the next major version of `debug`.');
		}
	};
})();

/**
 * Colors.
 */

exports.colors = [
	'#0000CC',
	'#0000FF',
	'#0033CC',
	'#0033FF',
	'#0066CC',
	'#0066FF',
	'#0099CC',
	'#0099FF',
	'#00CC00',
	'#00CC33',
	'#00CC66',
	'#00CC99',
	'#00CCCC',
	'#00CCFF',
	'#3300CC',
	'#3300FF',
	'#3333CC',
	'#3333FF',
	'#3366CC',
	'#3366FF',
	'#3399CC',
	'#3399FF',
	'#33CC00',
	'#33CC33',
	'#33CC66',
	'#33CC99',
	'#33CCCC',
	'#33CCFF',
	'#6600CC',
	'#6600FF',
	'#6633CC',
	'#6633FF',
	'#66CC00',
	'#66CC33',
	'#9900CC',
	'#9900FF',
	'#9933CC',
	'#9933FF',
	'#99CC00',
	'#99CC33',
	'#CC0000',
	'#CC0033',
	'#CC0066',
	'#CC0099',
	'#CC00CC',
	'#CC00FF',
	'#CC3300',
	'#CC3333',
	'#CC3366',
	'#CC3399',
	'#CC33CC',
	'#CC33FF',
	'#CC6600',
	'#CC6633',
	'#CC9900',
	'#CC9933',
	'#CCCC00',
	'#CCCC33',
	'#FF0000',
	'#FF0033',
	'#FF0066',
	'#FF0099',
	'#FF00CC',
	'#FF00FF',
	'#FF3300',
	'#FF3333',
	'#FF3366',
	'#FF3399',
	'#FF33CC',
	'#FF33FF',
	'#FF6600',
	'#FF6633',
	'#FF9900',
	'#FF9933',
	'#FFCC00',
	'#FFCC33'
];

/**
 * Currently only WebKit-based Web Inspectors, Firefox >= v31,
 * and the Firebug extension (any Firefox version) are known
 * to support "%c" CSS customizations.
 *
 * TODO: add a `localStorage` variable to explicitly enable/disable colors
 */

// eslint-disable-next-line complexity
function useColors() {
	// NB: In an Electron preload script, document will be defined but not fully
	// initialized. Since we know we're in Chrome, we'll just detect this case
	// explicitly
	if (typeof window !== 'undefined' && window.process && (window.process.type === 'renderer' || window.process.__nwjs)) {
		return true;
	}

	// Internet Explorer and Edge do not support colors.
	if (typeof navigator !== 'undefined' && navigator.userAgent && navigator.userAgent.toLowerCase().match(/(edge|trident)\/(\d+)/)) {
		return false;
	}

	// Is webkit? http://stackoverflow.com/a/16459606/376773
	// document is undefined in react-native: https://github.com/facebook/react-native/pull/1632
	return (typeof document !== 'undefined' && document.documentElement && document.documentElement.style && document.documentElement.style.WebkitAppearance) ||
		// Is firebug? http://stackoverflow.com/a/398120/376773
		(typeof window !== 'undefined' && window.console && (window.console.firebug || (window.console.exception && window.console.table))) ||
		// Is firefox >= v31?
		// https://developer.mozilla.org/en-US/docs/Tools/Web_Console#Styling_messages
		(typeof navigator !== 'undefined' && navigator.userAgent && navigator.userAgent.toLowerCase().match(/firefox\/(\d+)/) && parseInt(RegExp.$1, 10) >= 31) ||
		// Double check webkit in userAgent just in case we are in a worker
		(typeof navigator !== 'undefined' && navigator.userAgent && navigator.userAgent.toLowerCase().match(/applewebkit\/(\d+)/));
}

/**
 * Colorize log arguments if enabled.
 *
 * @api public
 */

function formatArgs(args) {
	args[0] = (this.useColors ? '%c' : '') +
		this.namespace +
		(this.useColors ? ' %c' : ' ') +
		args[0] +
		(this.useColors ? '%c ' : ' ') +
		'+' + module.exports.humanize(this.diff);

	if (!this.useColors) {
		return;
	}

	const c = 'color: ' + this.color;
	args.splice(1, 0, c, 'color: inherit');

	// The final "%c" is somewhat tricky, because there could be other
	// arguments passed either before or after the %c, so we need to
	// figure out the correct index to insert the CSS into
	let index = 0;
	let lastC = 0;
	args[0].replace(/%[a-zA-Z%]/g, match => {
		if (match === '%%') {
			return;
		}
		index++;
		if (match === '%c') {
			// We only are interested in the *last* %c
			// (the user may have provided their own)
			lastC = index;
		}
	});

	args.splice(lastC, 0, c);
}

/**
 * Invokes `console.debug()` when available.
 * No-op when `console.debug` is not a "function".
 * If `console.debug` is not available, falls back
 * to `console.log`.
 *
 * @api public
 */
exports.log = console.debug || console.log || (() => {});

/**
 * Save `namespaces`.
 *
 * @param {String} namespaces
 * @api private
 */
function save(namespaces) {
	try {
		if (namespaces) {
			exports.storage.setItem('debug', namespaces);
		} else {
			exports.storage.removeItem('debug');
		}
	} catch (error) {
		// Swallow
		// XXX (@Qix-) should we be logging these?
	}
}

/**
 * Load `namespaces`.
 *
 * @return {String} returns the previously persisted debug modes
 * @api private
 */
function load() {
	let r;
	try {
		r = exports.storage.getItem('debug');
	} catch (error) {
		// Swallow
		// XXX (@Qix-) should we be logging these?
	}

	// If debug isn't set in LS, and we're in Electron, try to load $DEBUG
	if (!r && typeof process !== 'undefined' && 'env' in process) {
		r = process.env.DEBUG;
	}

	return r;
}

/**
 * Localstorage attempts to return the localstorage.
 *
 * This is necessary because safari throws
 * when a user disables cookies/localstorage
 * and you attempt to access it.
 *
 * @return {LocalStorage}
 * @api private
 */

function localstorage() {
	try {
		// TVMLKit (Apple TV JS Runtime) does not have a window object, just localStorage in the global context
		// The Browser also has localStorage in the global context.
		return localStorage;
	} catch (error) {
		// Swallow
		// XXX (@Qix-) should we be logging these?
	}
}

module.exports = common(exports);

const {formatters} = module.exports;

/**
 * Map %j to `JSON.stringify()`, since no Web Inspectors do that by default.
 */

formatters.j = function (v) {
	try {
		return JSON.stringify(v);
	} catch (error) {
		return '[UnexpectedJSONParseError]: ' + error.message;
	}
};
}(browser, browser.exports));

var debug = browser.exports;

debug('caisdk:ImageProvenance:Assertion');
var AssertionLabel;
(function (AssertionLabel) {
    AssertionLabel["Actions"] = "c2pa.actions";
    AssertionLabel["AdobeCryptoAddresses"] = "adobe.crypto.addresses";
    AssertionLabel["AdobeDictionary"] = "adobe.dictionary";
    AssertionLabel["CreativeWork"] = "stds.schema-org.CreativeWork";
})(AssertionLabel || (AssertionLabel = {}));
class Assertion {
    constructor(claim, data) {
        Object.defineProperty(this, "claim", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "label", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "assertion", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.claim = claim;
        this.assertion = data;
        this.label = data.label;
    }
    get data() {
        return this.assertion.data;
    }
}

function _loadWasmModule (sync, filepath, src, imports) {
  function _instantiateOrCompile(source, imports, stream) {
    var instantiateFunc = stream ? WebAssembly.instantiateStreaming : WebAssembly.instantiate;
    var compileFunc = stream ? WebAssembly.compileStreaming : WebAssembly.compile;

    if (imports) {
      return instantiateFunc(source, imports)
    } else {
      return compileFunc(source)
    }
  }

  var buf = null;
  var isNode = typeof process !== 'undefined' && process.versions != null && process.versions.node != null;

  if (filepath && isNode) {
    var fs = require("fs");
    var path = require("path");

    return new Promise((resolve, reject) => {
      fs.readFile(path.resolve(__dirname, filepath), (error, buffer) => {
        if (error != null) {
          reject(error);
        }

        resolve(_instantiateOrCompile(buffer, imports, false));
      });
    });
  } else if (filepath) {
    return _instantiateOrCompile(fetch(filepath), imports, true)
  }

  if (isNode) {
    buf = Buffer.from(src, 'base64');
  } else {
    var raw = globalThis.atob(src);
    var rawLength = raw.length;
    buf = new Uint8Array(new ArrayBuffer(rawLength));
    for(var i = 0; i < rawLength; i++) {
       buf[i] = raw.charCodeAt(i);
    }
  }

  if(sync) {
    var mod = new WebAssembly.Module(buf);
    return imports ? new WebAssembly.Instance(mod, imports) : mod
  } else {
    return _instantiateOrCompile(buf, imports, false)
  }
}

function detectorWasm(imports){return _loadWasmModule(0, null, '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', imports)}

var _Validator_pool, _Validator_detectionLength;
const dbg$4 = debug('caisdk:Validator');
/**
 * Handles validation of input before processing it for C2PA metadata
 *
 * @public
 */
class Validator {
    constructor(pool, detectionLength) {
        _Validator_pool.set(this, void 0);
        _Validator_detectionLength.set(this, void 0);
        __classPrivateFieldSet(this, _Validator_pool, pool, "f");
        __classPrivateFieldSet(this, _Validator_detectionLength, detectionLength !== null && detectionLength !== void 0 ? detectionLength : Validator.DEFAULT_DETECTION_LENGTH, "f");
    }
    /**
     * Sanitizes mime type strings for proper file type checking
     *
     * @remarks
     * We need to do this since some Content-Types can coming in such as `image/jpeg; charset=utf-8`.
     *
     * @param type - The mime type of the asset
     */
    static sanitizeMimeType(type) {
        return type.split(';')[0];
    }
    /**
     * Checks if the asset has a mime type that is compatible with C2PA
     *
     * @param type - The mime type of the asset to check
     */
    static isValidMimeType(type) {
        return this.VALID_MIME_TYPES.includes(this.sanitizeMimeType(type));
    }
    /**
     * Scans an individual binary chunk for a C2PA metadata marker
     *
     * @param chunk - the chunk to check for the metadata marker
     * @param shouldTransfer - `true` to pass this chunk as a transferable object
     */
    scanChunk(chunk, shouldTransfer = false) {
        return __awaiter(this, void 0, void 0, function* () {
            const wasm = yield detectorWasm();
            dbg$4('Scanning buffer for C2PA marker with length %d', chunk.byteLength);
            // TODO: Add support for transferable objects
            const result = yield __classPrivateFieldGet(this, _Validator_pool, "f").exec('scanInput', [wasm, chunk]);
            dbg$4('Scanned buffer and got result', result);
            return result;
        });
    }
    /**
     * Scans a buffer/Blob for a C2PA metadata marker
     *
     * @remarks
     * This will automatically handle both a `ArrayBuffer` or a `Blob` as input
     * and automatically decide if it should be passed as a transferable object or not.
     * It will then pass it to `scanChunk` for the actual processing.
     *
     * @param input - The buffer/blob to scan
     */
    scanInput(input) {
        return __awaiter(this, void 0, void 0, function* () {
            let buffer = null;
            let shouldTransfer = false;
            if (input instanceof ArrayBuffer) {
                buffer = input;
            }
            else if (input instanceof Blob) {
                // Only send this as a transferable object if we are extracting an array
                // buffer from a blob, since we won't be re-using this buffer anywhere else
                const fullBuffer = yield input.arrayBuffer();
                shouldTransfer = true;
                if (__classPrivateFieldGet(this, _Validator_detectionLength, "f") > 0) {
                    buffer = fullBuffer.slice(0, __classPrivateFieldGet(this, _Validator_detectionLength, "f"));
                }
                else {
                    buffer = fullBuffer;
                }
            }
            if (!buffer) {
                throw new InvalidInputError();
            }
            return this.scanChunk(buffer, shouldTransfer);
        });
    }
}
_Validator_pool = new WeakMap(), _Validator_detectionLength = new WeakMap();
Object.defineProperty(Validator, "VALID_MIME_TYPES", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: ['image/jpeg', 'image/png']
});
Object.defineProperty(Validator, "DEFAULT_DETECTION_LENGTH", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 65535
});

var baseAssignValue = _baseAssignValue,
    eq = eq_1;

/**
 * This function is like `assignValue` except that it doesn't assign
 * `undefined` values.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignMergeValue$2(object, key, value) {
  if ((value !== undefined && !eq(object[key], value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue(object, key, value);
  }
}

var _assignMergeValue = assignMergeValue$2;

var isArrayLike = isArrayLike_1,
    isObjectLike = isObjectLike_1;

/**
 * This method is like `_.isArrayLike` except that it also checks if `value`
 * is an object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array-like object,
 *  else `false`.
 * @example
 *
 * _.isArrayLikeObject([1, 2, 3]);
 * // => true
 *
 * _.isArrayLikeObject(document.body.children);
 * // => true
 *
 * _.isArrayLikeObject('abc');
 * // => false
 *
 * _.isArrayLikeObject(_.noop);
 * // => false
 */
function isArrayLikeObject$1(value) {
  return isObjectLike(value) && isArrayLike(value);
}

var isArrayLikeObject_1 = isArrayLikeObject$1;

/**
 * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */

function safeGet$2(object, key) {
  if (key === 'constructor' && typeof object[key] === 'function') {
    return;
  }

  if (key == '__proto__') {
    return;
  }

  return object[key];
}

var _safeGet = safeGet$2;

var copyObject = _copyObject,
    keysIn$1 = keysIn_1;

/**
 * Converts `value` to a plain object flattening inherited enumerable string
 * keyed properties of `value` to own properties of the plain object.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {Object} Returns the converted plain object.
 * @example
 *
 * function Foo() {
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.assign({ 'a': 1 }, new Foo);
 * // => { 'a': 1, 'b': 2 }
 *
 * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
 * // => { 'a': 1, 'b': 2, 'c': 3 }
 */
function toPlainObject$1(value) {
  return copyObject(value, keysIn$1(value));
}

var toPlainObject_1 = toPlainObject$1;

var assignMergeValue$1 = _assignMergeValue,
    cloneBuffer = _cloneBuffer.exports,
    cloneTypedArray = _cloneTypedArray,
    copyArray = _copyArray,
    initCloneObject = _initCloneObject,
    isArguments = isArguments_1,
    isArray = isArray_1,
    isArrayLikeObject = isArrayLikeObject_1,
    isBuffer = isBuffer$4.exports,
    isFunction = isFunction_1,
    isObject$1 = isObject_1,
    isPlainObject = isPlainObject_1,
    isTypedArray = isTypedArray_1,
    safeGet$1 = _safeGet,
    toPlainObject = toPlainObject_1;

/**
 * A specialized version of `baseMerge` for arrays and objects which performs
 * deep merges and tracks traversed objects enabling objects with circular
 * references to be merged.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {string} key The key of the value to merge.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} mergeFunc The function to merge values.
 * @param {Function} [customizer] The function to customize assigned values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMergeDeep$1(object, source, key, srcIndex, mergeFunc, customizer, stack) {
  var objValue = safeGet$1(object, key),
      srcValue = safeGet$1(source, key),
      stacked = stack.get(srcValue);

  if (stacked) {
    assignMergeValue$1(object, key, stacked);
    return;
  }
  var newValue = customizer
    ? customizer(objValue, srcValue, (key + ''), object, source, stack)
    : undefined;

  var isCommon = newValue === undefined;

  if (isCommon) {
    var isArr = isArray(srcValue),
        isBuff = !isArr && isBuffer(srcValue),
        isTyped = !isArr && !isBuff && isTypedArray(srcValue);

    newValue = srcValue;
    if (isArr || isBuff || isTyped) {
      if (isArray(objValue)) {
        newValue = objValue;
      }
      else if (isArrayLikeObject(objValue)) {
        newValue = copyArray(objValue);
      }
      else if (isBuff) {
        isCommon = false;
        newValue = cloneBuffer(srcValue, true);
      }
      else if (isTyped) {
        isCommon = false;
        newValue = cloneTypedArray(srcValue, true);
      }
      else {
        newValue = [];
      }
    }
    else if (isPlainObject(srcValue) || isArguments(srcValue)) {
      newValue = objValue;
      if (isArguments(objValue)) {
        newValue = toPlainObject(objValue);
      }
      else if (!isObject$1(objValue) || isFunction(objValue)) {
        newValue = initCloneObject(srcValue);
      }
    }
    else {
      isCommon = false;
    }
  }
  if (isCommon) {
    // Recursively merge objects and arrays (susceptible to call stack limits).
    stack.set(srcValue, newValue);
    mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
    stack['delete'](srcValue);
  }
  assignMergeValue$1(object, key, newValue);
}

var _baseMergeDeep = baseMergeDeep$1;

var Stack = _Stack,
    assignMergeValue = _assignMergeValue,
    baseFor = _baseFor,
    baseMergeDeep = _baseMergeDeep,
    isObject = isObject_1,
    keysIn = keysIn_1,
    safeGet = _safeGet;

/**
 * The base implementation of `_.merge` without support for multiple sources.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} [customizer] The function to customize merged values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMerge$1(object, source, srcIndex, customizer, stack) {
  if (object === source) {
    return;
  }
  baseFor(source, function(srcValue, key) {
    stack || (stack = new Stack);
    if (isObject(srcValue)) {
      baseMergeDeep(object, source, key, srcIndex, baseMerge$1, customizer, stack);
    }
    else {
      var newValue = customizer
        ? customizer(safeGet(object, key), srcValue, (key + ''), object, source, stack)
        : undefined;

      if (newValue === undefined) {
        newValue = srcValue;
      }
      assignMergeValue(object, key, newValue);
    }
  }, keysIn);
}

var _baseMerge = baseMerge$1;

var baseRest = _baseRest,
    isIterateeCall = _isIterateeCall;

/**
 * Creates a function like `_.assign`.
 *
 * @private
 * @param {Function} assigner The function to assign values.
 * @returns {Function} Returns the new assigner function.
 */
function createAssigner$1(assigner) {
  return baseRest(function(object, sources) {
    var index = -1,
        length = sources.length,
        customizer = length > 1 ? sources[length - 1] : undefined,
        guard = length > 2 ? sources[2] : undefined;

    customizer = (assigner.length > 3 && typeof customizer == 'function')
      ? (length--, customizer)
      : undefined;

    if (guard && isIterateeCall(sources[0], sources[1], guard)) {
      customizer = length < 3 ? undefined : customizer;
      length = 1;
    }
    object = Object(object);
    while (++index < length) {
      var source = sources[index];
      if (source) {
        assigner(object, source, index, customizer);
      }
    }
    return object;
  });
}

var _createAssigner = createAssigner$1;

var baseMerge = _baseMerge,
    createAssigner = _createAssigner;

/**
 * This method is like `_.assign` except that it recursively merges own and
 * inherited enumerable string keyed properties of source objects into the
 * destination object. Source properties that resolve to `undefined` are
 * skipped if a destination value exists. Array and plain object properties
 * are merged recursively. Other objects and value types are overridden by
 * assignment. Source objects are applied from left to right. Subsequent
 * sources overwrite property assignments of previous sources.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 0.5.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} [sources] The source objects.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = {
 *   'a': [{ 'b': 2 }, { 'd': 4 }]
 * };
 *
 * var other = {
 *   'a': [{ 'c': 3 }, { 'e': 5 }]
 * };
 *
 * _.merge(object, other);
 * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
 */
var merge = createAssigner(function(object, source, srcIndex) {
  baseMerge(object, source, srcIndex);
});

var merge_1 = merge;

var _a$2, _Downloader_options, _Downloader_pool, _Downloader_validator, _Downloader_responseCache, _Downloader_defaultOptions, _Downloader_defaultFetchOptions;
const dbg$3 = debug('caisdk:Downloader');
const cacheDbg = debug('caisdk:Downloader:Cache');
/**
 * Handles downloading of any assets
 *
 * @public
 */
class Downloader {
    constructor(pool, opts = {}) {
        _Downloader_options.set(this, void 0);
        _Downloader_pool.set(this, void 0);
        _Downloader_validator.set(this, void 0);
        __classPrivateFieldSet(this, _Downloader_options, Object.assign(Object.assign({}, __classPrivateFieldGet(Downloader, _a$2, "f", _Downloader_defaultOptions)), opts), "f");
        __classPrivateFieldSet(this, _Downloader_pool, pool, "f");
        __classPrivateFieldSet(this, _Downloader_validator, new Validator(__classPrivateFieldGet(this, _Downloader_pool, "f"), __classPrivateFieldGet(this, _Downloader_options, "f").inspectSize), "f");
    }
    /**
     * Wrapper around `fetch` to download an asset
     *
     * @remarks
     * This has convenience logic for range requests
     *
     * @param url - The URL to fetch
     * @param fetchOptions - Options for this particular request
     */
    static download(url, fetchOptions = {}) {
        var _b;
        return __awaiter(this, void 0, void 0, function* () {
            dbg$3('Downloading', url);
            try {
                const defaultOpts = __classPrivateFieldGet(Downloader, _a$2, "f", _Downloader_defaultFetchOptions);
                const opts = merge_1({}, defaultOpts, fetchOptions);
                // Only use range if it is specified. If not, it may lead to CORS issues due to not being whitelisted
                const useRange = opts.rangeStart !== defaultOpts.rangeStart ||
                    opts.rangeEnd !== defaultOpts.rangeEnd;
                const range = [opts.rangeStart, (_b = opts.rangeEnd) !== null && _b !== void 0 ? _b : ''].join('-');
                const rangeHeaders = useRange
                    ? { headers: { range: `bytes=${range}` } }
                    : null;
                const res = yield fetch(url, merge_1({}, opts.fetchConfig, rangeHeaders));
                if (res.ok) {
                    return res;
                }
                else {
                    throw new UrlFetchError(url, res);
                }
            }
            catch (err) {
                throw new UrlFetchError(url, null, err);
            }
        });
    }
    /**
     * This allows us to inspect the image to see if the header contains C2PA data
     *
     * @remarks
     * We will request a download to the server requesting the first `inspectSize` bytes. From there:
     * - if the server responds with a payload less than the content-length, we will inspect that chunk and
     *   download the rest if the content-type matches and that chunk contains metadata
     * - if it responds with a payload equal to or greater than the content-length, we will inspect that the
     *   content type matches, scan the chunk, and return the data
     * - we'll return `null` if the content-type is invalid or if CAI data does not exist
     *
     * @param url - The URL to fetch
     */
    inspect(url) {
        return __awaiter(this, void 0, void 0, function* () {
            dbg$3('Inspecting', url);
            let res;
            let contentType = null;
            const shouldInspect = __classPrivateFieldGet(this, _Downloader_options, "f").inspectSize > 0;
            if (shouldInspect) {
                try {
                    res = yield Downloader.download(url, {
                        rangeEnd: __classPrivateFieldGet(this, _Downloader_options, "f").inspectSize,
                    });
                    contentType = res.headers.get('content-type');
                    if (res.status === 206) {
                        dbg$3('Successfully downloaded first part of url (supports range requests)', url, res);
                    }
                    else {
                        dbg$3('Successfully downloaded complete url (server does not support range requests)', url, res);
                    }
                }
                catch (err) {
                    dbg$3('Attempting to download with a range header failed, trying again without one', err);
                }
            }
            else {
                dbg$3('inspectSize of 0 given, downloading entire file');
            }
            if (!res) {
                try {
                    // We don't have an initial response due to not doing the initial range download
                    // Do a HEAD request to determine if we should download the entire file based on content-type
                    const headRes = yield Downloader.download(url, {
                        fetchConfig: {
                            method: 'HEAD',
                        },
                    });
                    contentType = headRes.headers.get('content-type');
                    dbg$3('Performed HEAD request and got content-type', url, contentType);
                }
                catch (err) {
                    dbg$3('HEAD request to check for content-type failed, downloading entire file');
                }
            }
            if (contentType && !Validator.isValidMimeType(contentType)) {
                dbg$3('Resource has invalid content type', contentType);
                throw new InvalidMimeTypeError(contentType);
            }
            const skipRest = contentType !== 'image/jpeg';
            if (!res || skipRest) {
                res = yield Downloader.download(url);
            }
            const blob = yield res.blob();
            if (skipRest) {
                dbg$3(`Skipping inspection since file is incompatible with this feature`);
                return blob;
            }
            if (!shouldInspect) {
                dbg$3(`Skipping inspection due to disabling the config`);
                return blob;
            }
            const buffer = yield blob.arrayBuffer();
            const { found } = yield __classPrivateFieldGet(this, _Downloader_validator, "f").scanChunk(buffer);
            if (found) {
                dbg$3('C2PA metadata found for url', url);
            }
            else {
                dbg$3('No C2PA metadata found for url', url);
                return null;
            }
            // We don't get a full content-length back from the server when doing a range
            // request, so we need to guess based on the response. We should continue if
            // we get a 206 back from the server instead of a 200, and that equals the `inspectSize`.
            // In that case, changes are the second request will yield more data, unless the image
            // size is exactly the `inspectSize`, and we get no data back.
            const shouldContinue = res.status === 206 && buffer.byteLength === __classPrivateFieldGet(this, _Downloader_options, "f").inspectSize + 1;
            if (shouldContinue) {
                dbg$3('Continuing download at offset %d', __classPrivateFieldGet(this, _Downloader_options, "f").inspectSize);
                const tailRes = yield Downloader.download(url, {
                    // We need to start the range at the next byte
                    rangeStart: __classPrivateFieldGet(this, _Downloader_options, "f").inspectSize + 1,
                });
                const tailBuffer = yield tailRes.arrayBuffer();
                dbg$3('Successfully downloaded rest of file (%d bytes)', tailBuffer.byteLength);
                return new Blob([buffer, tailBuffer], { type: blob.type });
            }
            return blob;
        });
    }
    /**
     * Fetches a JSON payload and caches it, using the requested URL as the key
     *
     * @param url - The URL to fetch and cache
     */
    static cachedGetJson(url) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!__classPrivateFieldGet(this, _a$2, "f", _Downloader_responseCache).get(url)) {
                cacheDbg('No cache found for %s', url);
                const res = yield Downloader.download(url, {
                    fetchConfig: {
                        credentials: 'omit',
                        headers: {
                            Accept: 'application/json',
                        },
                    },
                });
                const data = yield res.json();
                cacheDbg('Saving data for %s', url, data);
                __classPrivateFieldGet(this, _a$2, "f", _Downloader_responseCache).set(url, data);
            }
            cacheDbg('Returning cached data for %s', url);
            return __classPrivateFieldGet(this, _a$2, "f", _Downloader_responseCache).get(url);
        });
    }
}
_a$2 = Downloader, _Downloader_options = new WeakMap(), _Downloader_pool = new WeakMap(), _Downloader_validator = new WeakMap();
_Downloader_responseCache = { value: new Map() };
_Downloader_defaultOptions = { value: {
        inspectSize: 65535,
    } };
_Downloader_defaultFetchOptions = { value: {
        rangeStart: 0,
        rangeEnd: undefined,
        fetchConfig: {},
    } };

var _ActionsAssertion_instances, _ActionsAssertion_translateActionName, _ActionsAssertion_processCategories;
const dbg$2 = debug('caisdk:Assertions:ActionsAssertion');
var IconVariant;
(function (IconVariant) {
    IconVariant["Light"] = "light";
    IconVariant["Dark"] = "dark";
})(IconVariant || (IconVariant = {}));
class ActionsAssertion extends Assertion {
    constructor(claim, data) {
        super(claim, data);
        _ActionsAssertion_instances.add(this);
        Object.defineProperty(this, "assertion", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Pipeline to convert categories from the dictionary into a structure suitable for the
         * edits and activity web component. This also makes sure the categories are unique and sorted.
         */
        _ActionsAssertion_processCategories.set(this, flow(compact, uniqBy((category) => category.id), sortBy((category) => category.label)));
        this.assertion = data;
    }
    /**
     * Gets the list of actions from the claim's action assertion and runs them through the
     * dictionary to get information needed to render the associated categories in the
     * "Edits and activity" section.
     */
    getCategories(locale = 'en-US', iconVariant = IconVariant.Dark) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const { actions } = this.assertion.data;
            const dictionaryUrl = (_b = (_a = this.claim.assertions.get(AssertionLabel.AdobeDictionary)) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.url;
            // A dictionary and actions are both available
            if (dictionaryUrl && actions) {
                const dictionary = yield Downloader.cachedGetJson(dictionaryUrl);
                return __classPrivateFieldGet(this, _ActionsAssertion_processCategories, "f").call(this, actions.map((action) => {
                    var _a;
                    return __classPrivateFieldGet(this, _ActionsAssertion_instances, "m", _ActionsAssertion_translateActionName).call(this, dictionary, 
                    // TODO: This should be resolved once we reconcile dictionary definitions
                    (_a = action.parameters) !== null && _a !== void 0 ? _a : action.action, locale, iconVariant);
                }));
            }
            if (!dictionaryUrl && actions) {
                throw new DictionaryUrlNotFoundError();
            }
            // No actions or dictionary (this would happen if the producer opted out of this)
            return null;
        });
    }
}
_ActionsAssertion_processCategories = new WeakMap(), _ActionsAssertion_instances = new WeakSet(), _ActionsAssertion_translateActionName = function _ActionsAssertion_translateActionName(dictionary, actionId, locale, iconVariant) {
    var _a, _b, _c;
    const categoryId = (_b = (_a = dictionary.actions[actionId]) === null || _a === void 0 ? void 0 : _a.category) !== null && _b !== void 0 ? _b : ActionsAssertion.UNCATEGORIZED_ID;
    if (categoryId === ActionsAssertion.UNCATEGORIZED_ID) {
        dbg$2('Could not find category for actionId', actionId);
    }
    const category = dictionary.categories[categoryId];
    if (category) {
        return {
            id: categoryId,
            icon: (_c = category.icon) === null || _c === void 0 ? void 0 : _c.replace('{variant}', iconVariant),
            label: category.labels[locale],
            description: category.descriptions[locale],
        };
    }
    return null;
};
Object.defineProperty(ActionsAssertion, "UNCATEGORIZED_ID", {
    enumerable: true,
    configurable: true,
    writable: true,
    value: 'UNCATEGORIZED'
});

class AdobeCryptoAddressesAssertion extends Assertion {
    constructor(claim, data) {
        super(claim, data);
        Object.defineProperty(this, "assertion", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.assertion = data;
    }
}

class AdobeDictionaryAssertion extends Assertion {
    constructor(claim, data) {
        super(claim, data);
        Object.defineProperty(this, "assertion", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.assertion = data;
    }
}

class CreativeWorkAssertion extends Assertion {
    constructor(claim, data) {
        super(claim, data);
        Object.defineProperty(this, "assertion", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.assertion = data;
    }
    /**
     * Gets the author that is the producer from the CreativeWork assertion
     */
    get producer() {
        var _a;
        return (_a = this.assertion.data.author) === null || _a === void 0 ? void 0 : _a.find((x) => !x.hasOwnProperty('@id') && Array.isArray(x.credential));
    }
    /**
     * Gets any social accounts that have been added
     */
    get socialAccounts() {
        var _a;
        return (_a = this.assertion.data.author) === null || _a === void 0 ? void 0 : _a.filter((x) => x.hasOwnProperty('@id') && !x.hasOwnProperty('credential'));
    }
}

class AssertionFactory {
    constructor(claim, data) {
        Object.defineProperty(this, "claim", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "label", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "assertion", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.claim = claim;
        this.assertion = data.data;
        this.label = data.label;
    }
    get data() {
        return this.assertion.data;
    }
    static from(claim, assertion) {
        const { label } = assertion;
        if (label === AssertionLabel.Actions) {
            return new ActionsAssertion(claim, assertion);
        }
        if (label === AssertionLabel.AdobeCryptoAddresses) {
            return new AdobeCryptoAddressesAssertion(claim, assertion);
        }
        if (label === AssertionLabel.AdobeDictionary) {
            return new AdobeDictionaryAssertion(claim, assertion);
        }
        if (label === AssertionLabel.CreativeWork) {
            return new CreativeWorkAssertion(claim, assertion);
        }
        return new Assertion(claim, assertion);
    }
}

/**
 * Calculates the SHA of a buffer using WebCrypto
 */
function sha(data, algorithm = 'SHA-1') {
    return __awaiter(this, void 0, void 0, function* () {
        const hashBuf = yield crypto.subtle.digest(algorithm, data);
        const hashArr = Array.from(new Uint8Array(hashBuf));
        return hashArr.map((b) => b.toString(16).padStart(2, '0')).join('');
    });
}

var _Asset_hash;
debug('caisdk:ImageProvenance:Asset');
class Asset {
    constructor(parent, data) {
        Object.defineProperty(this, "parent", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "type", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "arrayBuffer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        _Asset_hash.set(this, void 0);
        const { thumbnail } = data, rest = __rest(data, ["thumbnail"]);
        this.parent = parent;
        this.data = rest;
        if (thumbnail) {
            this.type = thumbnail.format;
            this.arrayBuffer = Uint8Array.from(thumbnail.image);
        }
    }
    get blob() {
        if (this.arrayBuffer && this.type) {
            return new Blob([this.arrayBuffer], { type: this.type });
        }
    }
    computeHash() {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof __classPrivateFieldGet(this, _Asset_hash, "f") === 'undefined') {
                if (this.arrayBuffer) {
                    __classPrivateFieldSet(this, _Asset_hash, yield sha(this.arrayBuffer), "f");
                }
                else {
                    __classPrivateFieldSet(this, _Asset_hash, null, "f");
                }
            }
            return __classPrivateFieldGet(this, _Asset_hash, "f");
        });
    }
    /**
     * Generates a blob URL from the thumbnail data
     *
     * @returns An object with a `url` and `dispose` function
     */
    generateThumbnailUrl() {
        return __awaiter(this, void 0, void 0, function* () {
            const blob = this.blob;
            if (blob) {
                const url = URL.createObjectURL(blob);
                return {
                    url,
                    dispose: () => URL.revokeObjectURL(url),
                };
            }
            return { url: null, dispose: () => { } };
        });
    }
}
_Asset_hash = new WeakMap();

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }

  var number = Number(dirtyNumber);

  if (isNaN(number)) {
    return number;
  }

  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

var MILLISECONDS_IN_HOUR = 3600000;
var MILLISECONDS_IN_MINUTE = 60000;
var DEFAULT_ADDITIONAL_DIGITS = 2;
var patterns = {
  dateTimeDelimiter: /[T ]/,
  timeZoneDelimiter: /[Z ]/i,
  timezone: /([Z+-].*)$/
};
var dateRegex = /^-?(?:(\d{3})|(\d{2})(?:-?(\d{2}))?|W(\d{2})(?:-?(\d{1}))?|)$/;
var timeRegex = /^(\d{2}(?:[.,]\d*)?)(?::?(\d{2}(?:[.,]\d*)?))?(?::?(\d{2}(?:[.,]\d*)?))?$/;
var timezoneRegex = /^([+-])(\d{2})(?::?(\d{2}))?$/;
/**
 * @name parseISO
 * @category Common Helpers
 * @summary Parse ISO string
 *
 * @description
 * Parse the given string in ISO 8601 format and return an instance of Date.
 *
 * Function accepts complete ISO 8601 formats as well as partial implementations.
 * ISO 8601: http://en.wikipedia.org/wiki/ISO_8601
 *
 * If the argument isn't a string, the function cannot parse the string or
 * the values are invalid, it returns Invalid Date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The previous `parse` implementation was renamed to `parseISO`.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   parse('2016-01-01')
 *
 *   // v2.0.0 onward
 *   parseISO('2016-01-01')
 *   ```
 *
 * - `parseISO` now validates separate date and time values in ISO-8601 strings
 *   and returns `Invalid Date` if the date is invalid.
 *
 *   ```javascript
 *   parseISO('2018-13-32')
 *   //=> Invalid Date
 *   ```
 *
 * - `parseISO` now doesn't fall back to `new Date` constructor
 *   if it fails to parse a string argument. Instead, it returns `Invalid Date`.
 *
 * @param {String} argument - the value to convert
 * @param {Object} [options] - an object with options.
 * @param {0|1|2} [options.additionalDigits=2] - the additional number of digits in the extended year format
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 * @throws {RangeError} `options.additionalDigits` must be 0, 1 or 2
 *
 * @example
 * // Convert string '2014-02-11T11:30:30' to date:
 * var result = parseISO('2014-02-11T11:30:30')
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert string '+02014101' to date,
 * // if the additional number of digits in the extended year format is 1:
 * var result = parseISO('+02014101', { additionalDigits: 1 })
 * //=> Fri Apr 11 2014 00:00:00
 */

function parseISO(argument, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var additionalDigits = options.additionalDigits == null ? DEFAULT_ADDITIONAL_DIGITS : toInteger(options.additionalDigits);

  if (additionalDigits !== 2 && additionalDigits !== 1 && additionalDigits !== 0) {
    throw new RangeError('additionalDigits must be 0, 1 or 2');
  }

  if (!(typeof argument === 'string' || Object.prototype.toString.call(argument) === '[object String]')) {
    return new Date(NaN);
  }

  var dateStrings = splitDateString(argument);
  var date;

  if (dateStrings.date) {
    var parseYearResult = parseYear(dateStrings.date, additionalDigits);
    date = parseDate(parseYearResult.restDateString, parseYearResult.year);
  }

  if (isNaN(date) || !date) {
    return new Date(NaN);
  }

  var timestamp = date.getTime();
  var time = 0;
  var offset;

  if (dateStrings.time) {
    time = parseTime(dateStrings.time);

    if (isNaN(time) || time === null) {
      return new Date(NaN);
    }
  }

  if (dateStrings.timezone) {
    offset = parseTimezone(dateStrings.timezone);

    if (isNaN(offset)) {
      return new Date(NaN);
    }
  } else {
    var dirtyDate = new Date(timestamp + time); // js parsed string assuming it's in UTC timezone
    // but we need it to be parsed in our timezone
    // so we use utc values to build date in our timezone.
    // Year values from 0 to 99 map to the years 1900 to 1999
    // so set year explicitly with setFullYear.

    var result = new Date(0);
    result.setFullYear(dirtyDate.getUTCFullYear(), dirtyDate.getUTCMonth(), dirtyDate.getUTCDate());
    result.setHours(dirtyDate.getUTCHours(), dirtyDate.getUTCMinutes(), dirtyDate.getUTCSeconds(), dirtyDate.getUTCMilliseconds());
    return result;
  }

  return new Date(timestamp + time + offset);
}

function splitDateString(dateString) {
  var dateStrings = {};
  var array = dateString.split(patterns.dateTimeDelimiter);
  var timeString; // The regex match should only return at maximum two array elements.
  // [date], [time], or [date, time].

  if (array.length > 2) {
    return dateStrings;
  }

  if (/:/.test(array[0])) {
    dateStrings.date = null;
    timeString = array[0];
  } else {
    dateStrings.date = array[0];
    timeString = array[1];

    if (patterns.timeZoneDelimiter.test(dateStrings.date)) {
      dateStrings.date = dateString.split(patterns.timeZoneDelimiter)[0];
      timeString = dateString.substr(dateStrings.date.length, dateString.length);
    }
  }

  if (timeString) {
    var token = patterns.timezone.exec(timeString);

    if (token) {
      dateStrings.time = timeString.replace(token[1], '');
      dateStrings.timezone = token[1];
    } else {
      dateStrings.time = timeString;
    }
  }

  return dateStrings;
}

function parseYear(dateString, additionalDigits) {
  var regex = new RegExp('^(?:(\\d{4}|[+-]\\d{' + (4 + additionalDigits) + '})|(\\d{2}|[+-]\\d{' + (2 + additionalDigits) + '})$)');
  var captures = dateString.match(regex); // Invalid ISO-formatted year

  if (!captures) return {
    year: null
  };
  var year = captures[1] && parseInt(captures[1]);
  var century = captures[2] && parseInt(captures[2]);
  return {
    year: century == null ? year : century * 100,
    restDateString: dateString.slice((captures[1] || captures[2]).length)
  };
}

function parseDate(dateString, year) {
  // Invalid ISO-formatted year
  if (year === null) return null;
  var captures = dateString.match(dateRegex); // Invalid ISO-formatted string

  if (!captures) return null;
  var isWeekDate = !!captures[4];
  var dayOfYear = parseDateUnit(captures[1]);
  var month = parseDateUnit(captures[2]) - 1;
  var day = parseDateUnit(captures[3]);
  var week = parseDateUnit(captures[4]);
  var dayOfWeek = parseDateUnit(captures[5]) - 1;

  if (isWeekDate) {
    if (!validateWeekDate(year, week, dayOfWeek)) {
      return new Date(NaN);
    }

    return dayOfISOWeekYear(year, week, dayOfWeek);
  } else {
    var date = new Date(0);

    if (!validateDate(year, month, day) || !validateDayOfYearDate(year, dayOfYear)) {
      return new Date(NaN);
    }

    date.setUTCFullYear(year, month, Math.max(dayOfYear, day));
    return date;
  }
}

function parseDateUnit(value) {
  return value ? parseInt(value) : 1;
}

function parseTime(timeString) {
  var captures = timeString.match(timeRegex);
  if (!captures) return null; // Invalid ISO-formatted time

  var hours = parseTimeUnit(captures[1]);
  var minutes = parseTimeUnit(captures[2]);
  var seconds = parseTimeUnit(captures[3]);

  if (!validateTime(hours, minutes, seconds)) {
    return NaN;
  }

  return hours * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE + seconds * 1000;
}

function parseTimeUnit(value) {
  return value && parseFloat(value.replace(',', '.')) || 0;
}

function parseTimezone(timezoneString) {
  if (timezoneString === 'Z') return 0;
  var captures = timezoneString.match(timezoneRegex);
  if (!captures) return 0;
  var sign = captures[1] === '+' ? -1 : 1;
  var hours = parseInt(captures[2]);
  var minutes = captures[3] && parseInt(captures[3]) || 0;

  if (!validateTimezone(hours, minutes)) {
    return NaN;
  }

  return sign * (hours * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE);
}

function dayOfISOWeekYear(isoWeekYear, week, day) {
  var date = new Date(0);
  date.setUTCFullYear(isoWeekYear, 0, 4);
  var fourthOfJanuaryDay = date.getUTCDay() || 7;
  var diff = (week - 1) * 7 + day + 1 - fourthOfJanuaryDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
} // Validation functions
// February is null to handle the leap year (using ||)


var daysInMonths = [31, null, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYearIndex(year) {
  return year % 400 === 0 || year % 4 === 0 && year % 100;
}

function validateDate(year, month, date) {
  return month >= 0 && month <= 11 && date >= 1 && date <= (daysInMonths[month] || (isLeapYearIndex(year) ? 29 : 28));
}

function validateDayOfYearDate(year, dayOfYear) {
  return dayOfYear >= 1 && dayOfYear <= (isLeapYearIndex(year) ? 366 : 365);
}

function validateWeekDate(_year, week, day) {
  return week >= 1 && week <= 53 && day >= 0 && day <= 6;
}

function validateTime(hours, minutes, seconds) {
  if (hours === 24) {
    return minutes === 0 && seconds === 0;
  }

  return seconds >= 0 && seconds < 60 && minutes >= 0 && minutes < 60 && hours >= 0 && hours < 25;
}

function validateTimezone(_hours, minutes) {
  return minutes >= 0 && minutes <= 59;
}

debug('caisdk:ImageProvenance:Ingredient');
class Ingredient extends Asset {
    constructor(parent, id, data) {
        super(parent, data);
        /**
         * A unique identifier for this ingredient
         */
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * The ingredient data from the toolkit
         */
        Object.defineProperty(this, "data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.id = id;
        // This is intentional, we want to override the IAssetData type
        this.data = data;
    }
    get claim() {
        if (this.data.provenance) {
            return this.parent.provenance.claims.get(this.data.provenance);
        }
    }
    get asset() {
        return this;
    }
    get title() {
        return this.data.title;
    }
    /**
     * Any errors coming back from the toolkit
     */
    get errors() {
        var _a;
        return (_a = this.data.errors) !== null && _a !== void 0 ? _a : [];
    }
}

debug('caisdk:ImageProvenance:Claim');
var RecorderFormat;
(function (RecorderFormat) {
    RecorderFormat[RecorderFormat["Raw"] = 0] = "Raw";
    RecorderFormat[RecorderFormat["ProgramNameAndVersion"] = 1] = "ProgramNameAndVersion";
})(RecorderFormat || (RecorderFormat = {}));
/**
 * Class that represents a claim in the asset's provenance data.
 *
 * @see {@link https://c2pa.org/public-draft/#_overview_4}
 *
 * @public
 */
class Claim {
    /**
     * Create a new `Claim` object. Typically these will be created by the SDK.
     *
     * @param provenance - The provenance instance this claim belongs to
     * @param data - The raw data for this claim from the toolkit
     *
     * @internal
     */
    constructor(provenance, id, data) {
        Object.defineProperty(this, "provenance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * An identifier string for this claim that is unique per provenance chain
         */
        Object.defineProperty(this, "id", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * The underlying data for this claim, provided by the toolkit
         */
        Object.defineProperty(this, "data", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * The collection of assertions linked to this claim
         */
        Object.defineProperty(this, "assertions", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * The collection of ingredients that this claim includes
         */
        Object.defineProperty(this, "ingredients", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * The acquisition asset for this claim if it exists
         */
        Object.defineProperty(this, "asset", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.provenance = provenance;
        this.id = id;
        this.data = data;
        this.assertions = this.data.assertions.reduce((map, assertion) => map.set(assertion.label, AssertionFactory.from(this, assertion)), new Map());
        this.ingredients = this.data.ingredients.map((ingredient, idx) => new Ingredient(this, `${id}[${idx}]`, ingredient));
        if (this.data.asset) {
            this.asset = new Asset(this, this.data.asset);
        }
    }
    /**
     * Searches the claim's assertions for one matching the supplied label
     *
     * @param label - The label of the assertion you want to retrieve
     */
    findAssertion(label) {
        const assertion = this.assertions.get(label);
        // TODO: See if we can get this working with the TypeScript factory pattern
        return assertion !== null && assertion !== void 0 ? assertion : null;
    }
    /**
     * Gets the parent of this claim
     *
     * @remarks
     * This will give the claim that contains the ingredient that this claim belongs to
     */
    get parent() {
        const iterator = this.provenance.claims.values();
        let result = iterator.next();
        let parent;
        while (!parent && !result.done) {
            const claim = result.value;
            if (claim.id !== this.id) {
                // Look to see if this claim is listed as an ingredient of another
                const ingredient = claim.ingredients.find((x) => { var _a; return ((_a = x.claim) === null || _a === void 0 ? void 0 : _a.id) === this.id; });
                if (ingredient) {
                    parent = ingredient.parent;
                }
            }
            result = iterator.next();
        }
        return parent;
    }
    /**
     * Gets the acquisition thumbnail for this claim if it exists
     */
    get thumbnail() {
        var _a, _b;
        return (_b = (_a = this.data) === null || _a === void 0 ? void 0 : _a.asset) === null || _b === void 0 ? void 0 : _b.thumbnail;
    }
    /**
     * Returns the string used to identify the item that recorded this claim (application, device, etc.)
     */
    get recorder() {
        var _a;
        return (_a = this.data) === null || _a === void 0 ? void 0 : _a.recorder;
    }
    /**
     * Formats the `recorder` string
     *
     * @param format - The format to parse the string into
     * @returns The formatted string, if specified
     */
    formatRecorder(format = RecorderFormat.Raw) {
        var _a, _b;
        if (format === RecorderFormat.ProgramNameAndVersion) {
            return (_b = (_a = this.recorder) === null || _a === void 0 ? void 0 : _a.split('(')[0]) === null || _b === void 0 ? void 0 : _b.trim();
        }
        return this.recorder;
    }
    /**
     * Gets the claim data in a serializable/easily consumable format (useful for passing data to UIs and
     * transmitting over the wire). Returns a disposer so that generated thumbnail URLs can be cleaned up properly.
     */
    asSerializableData() {
        var _a, _b, _c, _d, _e, _f;
        return __awaiter(this, void 0, void 0, function* () {
            console.log('this', this);
            const omitAssertions = ['c2pa.hash.data'];
            const assertions = this.data.assertions.reduce((acc, { label, data }) => {
                if (!omitAssertions.includes(label)) {
                    acc[label] = data;
                }
                return acc;
            }, {});
            const thumbnailUrl = yield ((_a = this.asset) === null || _a === void 0 ? void 0 : _a.generateThumbnailUrl());
            const creativeWorkAssertion = this.findAssertion(AssertionLabel.CreativeWork);
            const socialAccounts = ((_b = creativeWorkAssertion === null || creativeWorkAssertion === void 0 ? void 0 : creativeWorkAssertion.socialAccounts) !== null && _b !== void 0 ? _b : []).map((account) => ({
                name: account.name,
                url: account['@id'],
            }));
            return {
                data: {
                    assertions,
                    isBeta: this.isBeta,
                    producer: (_d = (_c = creativeWorkAssertion === null || creativeWorkAssertion === void 0 ? void 0 : creativeWorkAssertion.producer) === null || _c === void 0 ? void 0 : _c.name) !== null && _d !== void 0 ? _d : '',
                    recorder: this.formatRecorder(RecorderFormat.ProgramNameAndVersion),
                    signatureDate: (_e = this.signature) === null || _e === void 0 ? void 0 : _e.isoDateString,
                    signer: (_f = this.signature) === null || _f === void 0 ? void 0 : _f.issuer,
                    socialAccounts,
                    thumbnailUrl: thumbnailUrl === null || thumbnailUrl === void 0 ? void 0 : thumbnailUrl.url,
                },
                dispose: () => {
                    var _a;
                    (_a = thumbnailUrl === null || thumbnailUrl === void 0 ? void 0 : thumbnailUrl.dispose) === null || _a === void 0 ? void 0 : _a.call(thumbnailUrl);
                },
            };
        });
    }
    /**
     * Gets the title of the acquisition asset for this claim if it exists (e.g. filename)
     */
    get title() {
        var _a;
        return (_a = this.asset) === null || _a === void 0 ? void 0 : _a.data.title;
    }
    /**
     * Gets the signature information (issuer, date) for this claim
     */
    get signature() {
        const sig = this.data.signature;
        return {
            issuer: sig.issuer,
            date: parseISO(sig.time),
            isoDateString: sig.time,
        };
    }
    /**
     * Any errors coming back from the toolkit
     */
    get errors() {
        var _a, _b;
        return (_b = (_a = this.data) === null || _a === void 0 ? void 0 : _a.errors) !== null && _b !== void 0 ? _b : [];
    }
    get isBeta() {
        var _a, _b;
        return !!((_b = (_a = this.findAssertion('adobe.beta')) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.version);
    }
}

var workerpool$1 = {exports: {}};

/**
 * workerpool.js
 * https://github.com/josdejong/workerpool
 *
 * Offload tasks to a pool of workers on node.js and in the browser.
 *
 * @version 6.1.5
 * @date    2021-06-17
 *
 * @license
 * Copyright (C) 2014-2020 Jos de Jong <wjosdejong@gmail.com>
 *
 * Licensed under the Apache License, Version 2.0 (the "License"); you may not
 * use this file except in compliance with the License. You may obtain a copy
 * of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied. See the
 * License for the specific language governing permissions and limitations under
 * the License.
 */

(function (module, exports) {
(function webpackUniversalModuleDefinition(root, factory) {
	module.exports = factory();
})((typeof self !== 'undefined' ? self : commonjsGlobal), function() {
return /******/ (function() { // webpackBootstrap
/******/ 	var __webpack_modules__ = ({

/***/ 345:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var Promise = __webpack_require__(219);

var WorkerHandler = __webpack_require__(751);

var environment = __webpack_require__(828);

var DebugPortAllocator = __webpack_require__(833);

var DEBUG_PORT_ALLOCATOR = new DebugPortAllocator();
/**
 * A pool to manage workers
 * @param {String} [script]   Optional worker script
 * @param {WorkerPoolOptions} [options]  See docs
 * @constructor
 */

function Pool(script, options) {
  if (typeof script === 'string') {
    this.script = script || null;
  } else {
    this.script = null;
    options = script;
  }

  this.workers = []; // queue with all workers

  this.tasks = []; // queue with tasks awaiting execution

  options = options || {};
  this.forkArgs = options.forkArgs || [];
  this.forkOpts = options.forkOpts || {};
  this.debugPortStart = options.debugPortStart || 43210;
  this.nodeWorker = options.nodeWorker;
  this.workerType = options.workerType || options.nodeWorker || 'auto';
  this.maxQueueSize = options.maxQueueSize || Infinity; // configuration

  if (options && 'maxWorkers' in options) {
    validateMaxWorkers(options.maxWorkers);
    this.maxWorkers = options.maxWorkers;
  } else {
    this.maxWorkers = Math.max((environment.cpus || 4) - 1, 1);
  }

  if (options && 'minWorkers' in options) {
    if (options.minWorkers === 'max') {
      this.minWorkers = this.maxWorkers;
    } else {
      validateMinWorkers(options.minWorkers);
      this.minWorkers = options.minWorkers;
      this.maxWorkers = Math.max(this.minWorkers, this.maxWorkers); // in case minWorkers is higher than maxWorkers
    }

    this._ensureMinWorkers();
  }

  this._boundNext = this._next.bind(this);

  if (this.workerType === 'thread') {
    WorkerHandler.ensureWorkerThreads();
  }
}
/**
 * Execute a function on a worker.
 *
 * Example usage:
 *
 *   var pool = new Pool()
 *
 *   // call a function available on the worker
 *   pool.exec('fibonacci', [6])
 *
 *   // offload a function
 *   function add(a, b) {
 *     return a + b
 *   };
 *   pool.exec(add, [2, 4])
 *       .then(function (result) {
 *         console.log(result); // outputs 6
 *       })
 *       .catch(function(error) {
 *         console.log(error);
 *       });
 *
 * @param {String | Function} method  Function name or function.
 *                                    If `method` is a string, the corresponding
 *                                    method on the worker will be executed
 *                                    If `method` is a Function, the function
 *                                    will be stringified and executed via the
 *                                    workers built-in function `run(fn, args)`.
 * @param {Array} [params]  Function arguments applied when calling the function
 * @param {ExecOptions} [options]  Options object
 * @return {Promise.<*, Error>} result
 */


Pool.prototype.exec = function (method, params, options) {
  // validate type of arguments
  if (params && !Array.isArray(params)) {
    throw new TypeError('Array expected as argument "params"');
  }

  if (typeof method === 'string') {
    var resolver = Promise.defer();

    if (this.tasks.length >= this.maxQueueSize) {
      throw new Error('Max queue size of ' + this.maxQueueSize + ' reached');
    } // add a new task to the queue


    var tasks = this.tasks;
    var task = {
      method: method,
      params: params,
      resolver: resolver,
      timeout: null,
      options: options
    };
    tasks.push(task); // replace the timeout method of the Promise with our own,
    // which starts the timer as soon as the task is actually started

    var originalTimeout = resolver.promise.timeout;

    resolver.promise.timeout = function timeout(delay) {
      if (tasks.indexOf(task) !== -1) {
        // task is still queued -> start the timer later on
        task.timeout = delay;
        return resolver.promise;
      } else {
        // task is already being executed -> start timer immediately
        return originalTimeout.call(resolver.promise, delay);
      }
    }; // trigger task execution


    this._next();

    return resolver.promise;
  } else if (typeof method === 'function') {
    // send stringified function and function arguments to worker
    return this.exec('run', [String(method), params]);
  } else {
    throw new TypeError('Function or string expected as argument "method"');
  }
};
/**
 * Create a proxy for current worker. Returns an object containing all
 * methods available on the worker. The methods always return a promise.
 *
 * @return {Promise.<Object, Error>} proxy
 */


Pool.prototype.proxy = function () {
  if (arguments.length > 0) {
    throw new Error('No arguments expected');
  }

  var pool = this;
  return this.exec('methods').then(function (methods) {
    var proxy = {};
    methods.forEach(function (method) {
      proxy[method] = function () {
        return pool.exec(method, Array.prototype.slice.call(arguments));
      };
    });
    return proxy;
  });
};
/**
 * Creates new array with the results of calling a provided callback function
 * on every element in this array.
 * @param {Array} array
 * @param {function} callback  Function taking two arguments:
 *                             `callback(currentValue, index)`
 * @return {Promise.<Array>} Returns a promise which resolves  with an Array
 *                           containing the results of the callback function
 *                           executed for each of the array elements.
 */

/* TODO: implement map
Pool.prototype.map = function (array, callback) {
};
*/

/**
 * Grab the first task from the queue, find a free worker, and assign the
 * worker to the task.
 * @protected
 */


Pool.prototype._next = function () {
  if (this.tasks.length > 0) {
    // there are tasks in the queue
    // find an available worker
    var worker = this._getWorker();

    if (worker) {
      // get the first task from the queue
      var me = this;
      var task = this.tasks.shift(); // check if the task is still pending (and not cancelled -> promise rejected)

      if (task.resolver.promise.pending) {
        // send the request to the worker
        var promise = worker.exec(task.method, task.params, task.resolver, task.options).then(me._boundNext)["catch"](function () {
          // if the worker crashed and terminated, remove it from the pool
          if (worker.terminated) {
            return me._removeWorker(worker);
          }
        }).then(function () {
          me._next(); // trigger next task in the queue

        }); // start queued timer now

        if (typeof task.timeout === 'number') {
          promise.timeout(task.timeout);
        }
      } else {
        // The task taken was already complete (either rejected or resolved), so just trigger next task in the queue
        me._next();
      }
    }
  }
};
/**
 * Get an available worker. If no worker is available and the maximum number
 * of workers isn't yet reached, a new worker will be created and returned.
 * If no worker is available and the maximum number of workers is reached,
 * null will be returned.
 *
 * @return {WorkerHandler | null} worker
 * @private
 */


Pool.prototype._getWorker = function () {
  // find a non-busy worker
  var workers = this.workers;

  for (var i = 0; i < workers.length; i++) {
    var worker = workers[i];

    if (worker.busy() === false) {
      return worker;
    }
  }

  if (workers.length < this.maxWorkers) {
    // create a new worker
    worker = this._createWorkerHandler();
    workers.push(worker);
    return worker;
  }

  return null;
};
/**
 * Remove a worker from the pool.
 * Attempts to terminate worker if not already terminated, and ensures the minimum
 * pool size is met.
 * @param {WorkerHandler} worker
 * @return {Promise<WorkerHandler>}
 * @protected
 */


Pool.prototype._removeWorker = function (worker) {
  DEBUG_PORT_ALLOCATOR.releasePort(worker.debugPort); // _removeWorker will call this, but we need it to be removed synchronously

  this._removeWorkerFromList(worker); // If minWorkers set, spin up new workers to replace the crashed ones


  this._ensureMinWorkers(); // terminate the worker (if not already terminated)


  return new Promise(function (resolve, reject) {
    worker.terminate(false, function (err) {
      if (err) {
        reject(err);
      } else {
        resolve(worker);
      }
    });
  });
};
/**
 * Remove a worker from the pool list.
 * @param {WorkerHandler} worker
 * @protected
 */


Pool.prototype._removeWorkerFromList = function (worker) {
  // remove from the list with workers
  var index = this.workers.indexOf(worker);

  if (index !== -1) {
    this.workers.splice(index, 1);
  }
};
/**
 * Close all active workers. Tasks currently being executed will be finished first.
 * @param {boolean} [force=false]   If false (default), the workers are terminated
 *                                  after finishing all tasks currently in
 *                                  progress. If true, the workers will be
 *                                  terminated immediately.
 * @param {number} [timeout]        If provided and non-zero, worker termination promise will be rejected
 *                                  after timeout if worker process has not been terminated.
 * @return {Promise.<void, Error>}
 */


Pool.prototype.terminate = function (force, timeout) {
  // cancel any pending tasks
  this.tasks.forEach(function (task) {
    task.resolver.reject(new Error('Pool terminated'));
  });
  this.tasks.length = 0;

  var f = function f(worker) {
    this._removeWorkerFromList(worker);
  };

  var removeWorker = f.bind(this);
  var promises = [];
  var workers = this.workers.slice();
  workers.forEach(function (worker) {
    var termPromise = worker.terminateAndNotify(force, timeout).then(removeWorker);
    promises.push(termPromise);
  });
  return Promise.all(promises);
};
/**
 * Retrieve statistics on tasks and workers.
 * @return {{totalWorkers: number, busyWorkers: number, idleWorkers: number, pendingTasks: number, activeTasks: number}} Returns an object with statistics
 */


Pool.prototype.stats = function () {
  var totalWorkers = this.workers.length;
  var busyWorkers = this.workers.filter(function (worker) {
    return worker.busy();
  }).length;
  return {
    totalWorkers: totalWorkers,
    busyWorkers: busyWorkers,
    idleWorkers: totalWorkers - busyWorkers,
    pendingTasks: this.tasks.length,
    activeTasks: busyWorkers
  };
};
/**
 * Ensures that a minimum of minWorkers is up and running
 * @protected
 */


Pool.prototype._ensureMinWorkers = function () {
  if (this.minWorkers) {
    for (var i = this.workers.length; i < this.minWorkers; i++) {
      this.workers.push(this._createWorkerHandler());
    }
  }
};
/**
 * Helper function to create a new WorkerHandler and pass all options.
 * @return {WorkerHandler}
 * @private
 */


Pool.prototype._createWorkerHandler = function () {
  return new WorkerHandler(this.script, {
    forkArgs: this.forkArgs,
    forkOpts: this.forkOpts,
    debugPort: DEBUG_PORT_ALLOCATOR.nextAvailableStartingAt(this.debugPortStart),
    workerType: this.workerType
  });
};
/**
 * Ensure that the maxWorkers option is an integer >= 1
 * @param {*} maxWorkers
 * @returns {boolean} returns true maxWorkers has a valid value
 */


function validateMaxWorkers(maxWorkers) {
  if (!isNumber(maxWorkers) || !isInteger(maxWorkers) || maxWorkers < 1) {
    throw new TypeError('Option maxWorkers must be an integer number >= 1');
  }
}
/**
 * Ensure that the minWorkers option is an integer >= 0
 * @param {*} minWorkers
 * @returns {boolean} returns true when minWorkers has a valid value
 */


function validateMinWorkers(minWorkers) {
  if (!isNumber(minWorkers) || !isInteger(minWorkers) || minWorkers < 0) {
    throw new TypeError('Option minWorkers must be an integer number >= 0');
  }
}
/**
 * Test whether a variable is a number
 * @param {*} value
 * @returns {boolean} returns true when value is a number
 */


function isNumber(value) {
  return typeof value === 'number';
}
/**
 * Test whether a number is an integer
 * @param {number} value
 * @returns {boolean} Returns true if value is an integer
 */


function isInteger(value) {
  return Math.round(value) == value;
}

module.exports = Pool;

/***/ }),

/***/ 219:
/***/ (function(module) {

/**
 * Promise
 *
 * Inspired by https://gist.github.com/RubaXa/8501359 from RubaXa <trash@rubaxa.org>
 *
 * @param {Function} handler   Called as handler(resolve: Function, reject: Function)
 * @param {Promise} [parent]   Parent promise for propagation of cancel and timeout
 */

function Promise(handler, parent) {
  var me = this;

  if (!(this instanceof Promise)) {
    throw new SyntaxError('Constructor must be called with the new operator');
  }

  if (typeof handler !== 'function') {
    throw new SyntaxError('Function parameter handler(resolve, reject) missing');
  }

  var _onSuccess = [];
  var _onFail = []; // status

  this.resolved = false;
  this.rejected = false;
  this.pending = true;
  /**
   * Process onSuccess and onFail callbacks: add them to the queue.
   * Once the promise is resolve, the function _promise is replace.
   * @param {Function} onSuccess
   * @param {Function} onFail
   * @private
   */

  var _process = function _process(onSuccess, onFail) {
    _onSuccess.push(onSuccess);

    _onFail.push(onFail);
  };
  /**
   * Add an onSuccess callback and optionally an onFail callback to the Promise
   * @param {Function} onSuccess
   * @param {Function} [onFail]
   * @returns {Promise} promise
   */


  this.then = function (onSuccess, onFail) {
    return new Promise(function (resolve, reject) {
      var s = onSuccess ? _then(onSuccess, resolve, reject) : resolve;
      var f = onFail ? _then(onFail, resolve, reject) : reject;

      _process(s, f);
    }, me);
  };
  /**
   * Resolve the promise
   * @param {*} result
   * @type {Function}
   */


  var _resolve2 = function _resolve(result) {
    // update status
    me.resolved = true;
    me.rejected = false;
    me.pending = false;

    _onSuccess.forEach(function (fn) {
      fn(result);
    });

    _process = function _process(onSuccess, onFail) {
      onSuccess(result);
    };

    _resolve2 = _reject2 = function _reject() {};

    return me;
  };
  /**
   * Reject the promise
   * @param {Error} error
   * @type {Function}
   */


  var _reject2 = function _reject(error) {
    // update status
    me.resolved = false;
    me.rejected = true;
    me.pending = false;

    _onFail.forEach(function (fn) {
      fn(error);
    });

    _process = function _process(onSuccess, onFail) {
      onFail(error);
    };

    _resolve2 = _reject2 = function _reject() {};

    return me;
  };
  /**
   * Cancel te promise. This will reject the promise with a CancellationError
   * @returns {Promise} self
   */


  this.cancel = function () {
    if (parent) {
      parent.cancel();
    } else {
      _reject2(new CancellationError());
    }

    return me;
  };
  /**
   * Set a timeout for the promise. If the promise is not resolved within
   * the time, the promise will be cancelled and a TimeoutError is thrown.
   * If the promise is resolved in time, the timeout is removed.
   * @param {number} delay     Delay in milliseconds
   * @returns {Promise} self
   */


  this.timeout = function (delay) {
    if (parent) {
      parent.timeout(delay);
    } else {
      var timer = setTimeout(function () {
        _reject2(new TimeoutError('Promise timed out after ' + delay + ' ms'));
      }, delay);
      me.always(function () {
        clearTimeout(timer);
      });
    }

    return me;
  }; // attach handler passing the resolve and reject functions


  handler(function (result) {
    _resolve2(result);
  }, function (error) {
    _reject2(error);
  });
}
/**
 * Execute given callback, then call resolve/reject based on the returned result
 * @param {Function} callback
 * @param {Function} resolve
 * @param {Function} reject
 * @returns {Function}
 * @private
 */


function _then(callback, resolve, reject) {
  return function (result) {
    try {
      var res = callback(result);

      if (res && typeof res.then === 'function' && typeof res['catch'] === 'function') {
        // method returned a promise
        res.then(resolve, reject);
      } else {
        resolve(res);
      }
    } catch (error) {
      reject(error);
    }
  };
}
/**
 * Add an onFail callback to the Promise
 * @param {Function} onFail
 * @returns {Promise} promise
 */


Promise.prototype['catch'] = function (onFail) {
  return this.then(null, onFail);
}; // TODO: add support for Promise.catch(Error, callback)
// TODO: add support for Promise.catch(Error, Error, callback)

/**
 * Execute given callback when the promise either resolves or rejects.
 * @param {Function} fn
 * @returns {Promise} promise
 */


Promise.prototype.always = function (fn) {
  return this.then(fn, fn);
};
/**
 * Create a promise which resolves when all provided promises are resolved,
 * and fails when any of the promises resolves.
 * @param {Promise[]} promises
 * @returns {Promise} promise
 */


Promise.all = function (promises) {
  return new Promise(function (resolve, reject) {
    var remaining = promises.length,
        results = [];

    if (remaining) {
      promises.forEach(function (p, i) {
        p.then(function (result) {
          results[i] = result;
          remaining--;

          if (remaining == 0) {
            resolve(results);
          }
        }, function (error) {
          remaining = 0;
          reject(error);
        });
      });
    } else {
      resolve(results);
    }
  });
};
/**
 * Create a promise resolver
 * @returns {{promise: Promise, resolve: Function, reject: Function}} resolver
 */


Promise.defer = function () {
  var resolver = {};
  resolver.promise = new Promise(function (resolve, reject) {
    resolver.resolve = resolve;
    resolver.reject = reject;
  });
  return resolver;
};
/**
 * Create a cancellation error
 * @param {String} [message]
 * @extends Error
 */


function CancellationError(message) {
  this.message = message || 'promise cancelled';
  this.stack = new Error().stack;
}

CancellationError.prototype = new Error();
CancellationError.prototype.constructor = Error;
CancellationError.prototype.name = 'CancellationError';
Promise.CancellationError = CancellationError;
/**
 * Create a timeout error
 * @param {String} [message]
 * @extends Error
 */

function TimeoutError(message) {
  this.message = message || 'timeout exceeded';
  this.stack = new Error().stack;
}

TimeoutError.prototype = new Error();
TimeoutError.prototype.constructor = Error;
TimeoutError.prototype.name = 'TimeoutError';
Promise.TimeoutError = TimeoutError;
module.exports = Promise;

/***/ }),

/***/ 751:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {


function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it["return"] != null) it["return"](); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

var Promise = __webpack_require__(219);

var environment = __webpack_require__(828);

var requireFoolWebpack = __webpack_require__(397);
/**
 * Special message sent by parent which causes a child process worker to terminate itself.
 * Not a "message object"; this string is the entire message.
 */


var TERMINATE_METHOD_ID = '__workerpool-terminate__';
/**
 * If sending `TERMINATE_METHOD_ID` does not cause the child process to exit in this many milliseconds,
 * force-kill the child process.
 */

var CHILD_PROCESS_EXIT_TIMEOUT = 1000;

function ensureWorkerThreads() {
  var WorkerThreads = tryRequireWorkerThreads();

  if (!WorkerThreads) {
    throw new Error('WorkerPool: workerType = \'thread\' is not supported, Node >= 11.7.0 required');
  }

  return WorkerThreads;
} // check whether Worker is supported by the browser


function ensureWebWorker() {
  // Workaround for a bug in PhantomJS (Or QtWebkit): https://github.com/ariya/phantomjs/issues/14534
  if (typeof Worker !== 'function' && ((typeof Worker === "undefined" ? "undefined" : _typeof(Worker)) !== 'object' || typeof Worker.prototype.constructor !== 'function')) {
    throw new Error('WorkerPool: Web Workers not supported');
  }
}

function tryRequireWorkerThreads() {
  try {
    return requireFoolWebpack('worker_threads');
  } catch (error) {
    if (_typeof(error) === 'object' && error !== null && error.code === 'MODULE_NOT_FOUND') {
      // no worker_threads available (old version of node.js)
      return null;
    } else {
      throw error;
    }
  }
} // get the default worker script


function getDefaultWorker() {
  if (environment.platform === 'browser') {
    // test whether the browser supports all features that we need
    if (typeof Blob === 'undefined') {
      throw new Error('Blob not supported by the browser');
    }

    if (!window.URL || typeof window.URL.createObjectURL !== 'function') {
      throw new Error('URL.createObjectURL not supported by the browser');
    } // use embedded worker.js


    var blob = new Blob([__webpack_require__(670)], {
      type: 'text/javascript'
    });
    return window.URL.createObjectURL(blob);
  } else {
    // use external worker.js in current directory
    return __dirname + '/worker.js';
  }
}

function setupWorker(script, options) {
  if (options.workerType === 'web') {
    // browser only
    ensureWebWorker();
    return setupBrowserWorker(script, Worker);
  } else if (options.workerType === 'thread') {
    // node.js only
    WorkerThreads = ensureWorkerThreads();
    return setupWorkerThreadWorker(script, WorkerThreads);
  } else if (options.workerType === 'process' || !options.workerType) {
    // node.js only
    return setupProcessWorker(script, resolveForkOptions(options), requireFoolWebpack('child_process'));
  } else {
    // options.workerType === 'auto' or undefined
    if (environment.platform === 'browser') {
      ensureWebWorker();
      return setupBrowserWorker(script, Worker);
    } else {
      // environment.platform === 'node'
      var WorkerThreads = tryRequireWorkerThreads();

      if (WorkerThreads) {
        return setupWorkerThreadWorker(script, WorkerThreads);
      } else {
        return setupProcessWorker(script, resolveForkOptions(options), requireFoolWebpack('child_process'));
      }
    }
  }
}

function setupBrowserWorker(script, Worker) {
  // create the web worker
  var worker = new Worker(script);
  worker.isBrowserWorker = true; // add node.js API to the web worker

  worker.on = function (event, callback) {
    this.addEventListener(event, function (message) {
      callback(message.data);
    });
  };

  worker.send = function (message) {
    this.postMessage(message);
  };

  return worker;
}

function setupWorkerThreadWorker(script, WorkerThreads) {
  var worker = new WorkerThreads.Worker(script, {
    stdout: false,
    // automatically pipe worker.STDOUT to process.STDOUT
    stderr: false // automatically pipe worker.STDERR to process.STDERR

  });
  worker.isWorkerThread = true; // make the worker mimic a child_process

  worker.send = function (message) {
    this.postMessage(message);
  };

  worker.kill = function () {
    this.terminate();
    return true;
  };

  worker.disconnect = function () {
    this.terminate();
  };

  return worker;
}

function setupProcessWorker(script, options, child_process) {
  // no WorkerThreads, fallback to sub-process based workers
  var worker = child_process.fork(script, options.forkArgs, options.forkOpts);
  worker.isChildProcess = true;
  return worker;
} // add debug flags to child processes if the node inspector is active


function resolveForkOptions(opts) {
  opts = opts || {};
  var processExecArgv = process.execArgv.join(' ');
  var inspectorActive = processExecArgv.indexOf('--inspect') !== -1;
  var debugBrk = processExecArgv.indexOf('--debug-brk') !== -1;
  var execArgv = [];

  if (inspectorActive) {
    execArgv.push('--inspect=' + opts.debugPort);

    if (debugBrk) {
      execArgv.push('--debug-brk');
    }
  }

  process.execArgv.forEach(function (arg) {
    if (arg.indexOf('--max-old-space-size') > -1) {
      execArgv.push(arg);
    }
  });
  return Object.assign({}, opts, {
    forkArgs: opts.forkArgs,
    forkOpts: Object.assign({}, opts.forkOpts, {
      execArgv: (opts.forkOpts && opts.forkOpts.execArgv || []).concat(execArgv)
    })
  });
}
/**
 * Converts a serialized error to Error
 * @param {Object} obj Error that has been serialized and parsed to object
 * @return {Error} The equivalent Error.
 */


function objectToError(obj) {
  var temp = new Error('');
  var props = Object.keys(obj);

  for (var i = 0; i < props.length; i++) {
    temp[props[i]] = obj[props[i]];
  }

  return temp;
}
/**
 * A WorkerHandler controls a single worker. This worker can be a child process
 * on node.js or a WebWorker in a browser environment.
 * @param {String} [script] If no script is provided, a default worker with a
 *                          function run will be created.
 * @param {WorkerPoolOptions} _options See docs
 * @constructor
 */


function WorkerHandler(script, _options) {
  var me = this;
  var options = _options || {};
  this.script = script || getDefaultWorker();
  this.worker = setupWorker(this.script, options);
  this.debugPort = options.debugPort; // The ready message is only sent if the worker.add method is called (And the default script is not used)

  if (!script) {
    this.worker.ready = true;
  } // queue for requests that are received before the worker is ready


  this.requestQueue = [];
  this.worker.on('message', function (response) {
    if (me.terminated) {
      return;
    }

    if (typeof response === 'string' && response === 'ready') {
      me.worker.ready = true;
      dispatchQueuedRequests();
    } else {
      // find the task from the processing queue, and run the tasks callback
      var id = response.id;
      var task = me.processing[id];

      if (task !== undefined) {
        if (response.isEvent) {
          if (task.options && typeof task.options.on === 'function') {
            task.options.on(response.payload);
          }
        } else {
          // remove the task from the queue
          delete me.processing[id]; // test if we need to terminate

          if (me.terminating === true) {
            // complete worker termination if all tasks are finished
            me.terminate();
          } // resolve the task's promise


          if (response.error) {
            task.resolver.reject(objectToError(response.error));
          } else {
            task.resolver.resolve(response.result);
          }
        }
      }
    }
  }); // reject all running tasks on worker error

  function onError(error) {
    me.terminated = true;

    for (var id in me.processing) {
      if (me.processing[id] !== undefined) {
        me.processing[id].resolver.reject(error);
      }
    }

    me.processing = Object.create(null);
  } // send all queued requests to worker


  function dispatchQueuedRequests() {
    var _iterator = _createForOfIteratorHelper(me.requestQueue.splice(0)),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var request = _step.value;
        me.worker.send(request);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }

  var worker = this.worker; // listen for worker messages error and exit

  this.worker.on('error', onError);
  this.worker.on('exit', function (exitCode, signalCode) {
    var message = 'Workerpool Worker terminated Unexpectedly\n';
    message += '    exitCode: `' + exitCode + '`\n';
    message += '    signalCode: `' + signalCode + '`\n';
    message += '    workerpool.script: `' + me.script + '`\n';
    message += '    spawnArgs: `' + worker.spawnargs + '`\n';
    message += '    spawnfile: `' + worker.spawnfile + '`\n';
    message += '    stdout: `' + worker.stdout + '`\n';
    message += '    stderr: `' + worker.stderr + '`\n';
    onError(new Error(message));
  });
  this.processing = Object.create(null); // queue with tasks currently in progress

  this.terminating = false;
  this.terminated = false;
  this.terminationHandler = null;
  this.lastId = 0;
}
/**
 * Get a list with methods available on the worker.
 * @return {Promise.<String[], Error>} methods
 */


WorkerHandler.prototype.methods = function () {
  return this.exec('methods');
};
/**
 * Execute a method with given parameters on the worker
 * @param {String} method
 * @param {Array} [params]
 * @param {{resolve: Function, reject: Function}} [resolver]
 * @param {ExecOptions}  [options]
 * @return {Promise.<*, Error>} result
 */


WorkerHandler.prototype.exec = function (method, params, resolver, options) {
  if (!resolver) {
    resolver = Promise.defer();
  } // generate a unique id for the task


  var id = ++this.lastId; // register a new task as being in progress

  this.processing[id] = {
    id: id,
    resolver: resolver,
    options: options
  }; // build a JSON-RPC request

  var request = {
    id: id,
    method: method,
    params: params
  };

  if (this.terminated) {
    resolver.reject(new Error('Worker is terminated'));
  } else if (this.worker.ready) {
    // send the request to the worker
    this.worker.send(request);
  } else {
    this.requestQueue.push(request);
  } // on cancellation, force the worker to terminate


  var me = this;
  return resolver.promise["catch"](function (error) {
    if (error instanceof Promise.CancellationError || error instanceof Promise.TimeoutError) {
      // remove this task from the queue. It is already rejected (hence this
      // catch event), and else it will be rejected again when terminating
      delete me.processing[id]; // terminate worker

      return me.terminateAndNotify(true).then(function () {
        throw error;
      }, function (err) {
        throw err;
      });
    } else {
      throw error;
    }
  });
};
/**
 * Test whether the worker is working or not
 * @return {boolean} Returns true if the worker is busy
 */


WorkerHandler.prototype.busy = function () {
  return Object.keys(this.processing).length > 0;
};
/**
 * Terminate the worker.
 * @param {boolean} [force=false]   If false (default), the worker is terminated
 *                                  after finishing all tasks currently in
 *                                  progress. If true, the worker will be
 *                                  terminated immediately.
 * @param {function} [callback=null] If provided, will be called when process terminates.
 */


WorkerHandler.prototype.terminate = function (force, callback) {
  var me = this;

  if (force) {
    // cancel all tasks in progress
    for (var id in this.processing) {
      if (this.processing[id] !== undefined) {
        this.processing[id].resolver.reject(new Error('Worker terminated'));
      }
    }

    this.processing = Object.create(null);
  }

  if (typeof callback === 'function') {
    this.terminationHandler = callback;
  }

  if (!this.busy()) {
    // all tasks are finished. kill the worker
    var cleanup = function cleanup(err) {
      me.terminated = true;

      if (me.worker != null && me.worker.removeAllListeners) {
        // removeAllListeners is only available for child_process
        me.worker.removeAllListeners('message');
      }

      me.worker = null;
      me.terminating = false;

      if (me.terminationHandler) {
        me.terminationHandler(err, me);
      } else if (err) {
        throw err;
      }
    };

    if (this.worker) {
      if (typeof this.worker.kill === 'function') {
        if (this.worker.killed) {
          cleanup(new Error('worker already killed!'));
          return;
        }

        if (this.worker.isChildProcess) {
          var cleanExitTimeout = setTimeout(function () {
            me.worker.kill();
          }, CHILD_PROCESS_EXIT_TIMEOUT);
          this.worker.once('exit', function () {
            clearTimeout(cleanExitTimeout);
            me.worker.killed = true;
            cleanup();
          });

          if (this.worker.ready) {
            this.worker.send(TERMINATE_METHOD_ID);
          } else {
            this.worker.requestQueue.push(TERMINATE_METHOD_ID);
          }
        } else {
          // worker_thread
          this.worker.kill();
          this.worker.killed = true;
          cleanup();
        }

        return;
      } else if (typeof this.worker.terminate === 'function') {
        this.worker.terminate(); // web worker

        this.worker.killed = true;
      } else {
        throw new Error('Failed to terminate worker');
      }
    }

    cleanup();
  } else {
    // we can't terminate immediately, there are still tasks being executed
    this.terminating = true;
  }
};
/**
 * Terminate the worker, returning a Promise that resolves when the termination has been done.
 * @param {boolean} [force=false]   If false (default), the worker is terminated
 *                                  after finishing all tasks currently in
 *                                  progress. If true, the worker will be
 *                                  terminated immediately.
 * @param {number} [timeout]        If provided and non-zero, worker termination promise will be rejected
 *                                  after timeout if worker process has not been terminated.
 * @return {Promise.<WorkerHandler, Error>}
 */


WorkerHandler.prototype.terminateAndNotify = function (force, timeout) {
  var resolver = Promise.defer();

  if (timeout) {
    resolver.promise.timeout = timeout;
  }

  this.terminate(force, function (err, worker) {
    if (err) {
      resolver.reject(err);
    } else {
      resolver.resolve(worker);
    }
  });
  return resolver.promise;
};

module.exports = WorkerHandler;
module.exports._tryRequireWorkerThreads = tryRequireWorkerThreads;
module.exports._setupProcessWorker = setupProcessWorker;
module.exports._setupBrowserWorker = setupBrowserWorker;
module.exports._setupWorkerThreadWorker = setupWorkerThreadWorker;
module.exports.ensureWorkerThreads = ensureWorkerThreads;

/***/ }),

/***/ 833:
/***/ (function(module) {


var MAX_PORTS = 65535;
module.exports = DebugPortAllocator;

function DebugPortAllocator() {
  this.ports = Object.create(null);
  this.length = 0;
}

DebugPortAllocator.prototype.nextAvailableStartingAt = function (starting) {
  while (this.ports[starting] === true) {
    starting++;
  }

  if (starting >= MAX_PORTS) {
    throw new Error('WorkerPool debug port limit reached: ' + starting + '>= ' + MAX_PORTS);
  }

  this.ports[starting] = true;
  this.length++;
  return starting;
};

DebugPortAllocator.prototype.releasePort = function (port) {
  delete this.ports[port];
  this.length--;
};

/***/ }),

/***/ 828:
/***/ (function(module, __unused_webpack_exports, __webpack_require__) {

var requireFoolWebpack = __webpack_require__(397); // source: https://github.com/flexdinesh/browser-or-node


var isNode = function isNode(nodeProcess) {
  return typeof nodeProcess !== 'undefined' && nodeProcess.versions != null && nodeProcess.versions.node != null;
};

module.exports.isNode = isNode; // determines the JavaScript platform: browser or node

module.exports.platform = typeof process !== 'undefined' && isNode(process) ? 'node' : 'browser'; // determines whether the code is running in main thread or not
// note that in node.js we have to check both worker_thread and child_process

var worker_threads = tryRequireFoolWebpack('worker_threads');
module.exports.isMainThread = module.exports.platform === 'node' ? (!worker_threads || worker_threads.isMainThread) && !process.connected : typeof Window !== 'undefined'; // determines the number of cpus available

module.exports.cpus = module.exports.platform === 'browser' ? self.navigator.hardwareConcurrency : requireFoolWebpack('os').cpus().length;

function tryRequireFoolWebpack(module) {
  try {
    return requireFoolWebpack(module);
  } catch (err) {
    return null;
  }
}

/***/ }),

/***/ 670:
/***/ (function(module) {

/**
 * embeddedWorker.js contains an embedded version of worker.js.
 * This file is automatically generated,
 * changes made in this file will be overwritten.
 */
module.exports = "!function(){var __webpack_exports__={};!function(){var exports=__webpack_exports__,__webpack_unused_export__;function _typeof(r){return(_typeof=\"function\"==typeof Symbol&&\"symbol\"==typeof Symbol.iterator?function(r){return typeof r}:function(r){return r&&\"function\"==typeof Symbol&&r.constructor===Symbol&&r!==Symbol.prototype?\"symbol\":typeof r})(r)}var requireFoolWebpack=eval(\"typeof require !== 'undefined' ? require : function (module) { throw new Error('Module \\\" + module + \\\" not found.') }\"),TERMINATE_METHOD_ID=\"__workerpool-terminate__\",worker={exit:function(){}},WorkerThreads,parentPort;if(\"undefined\"!=typeof self&&\"function\"==typeof postMessage&&\"function\"==typeof addEventListener)worker.on=function(r,e){addEventListener(r,function(r){e(r.data)})},worker.send=function(r){postMessage(r)};else{if(\"undefined\"==typeof process)throw new Error(\"Script must be executed as a worker\");try{WorkerThreads=requireFoolWebpack(\"worker_threads\")}catch(error){if(\"object\"!==_typeof(error)||null===error||\"MODULE_NOT_FOUND\"!==error.code)throw error}WorkerThreads&&null!==WorkerThreads.parentPort?(parentPort=WorkerThreads.parentPort,worker.send=parentPort.postMessage.bind(parentPort),worker.on=parentPort.on.bind(parentPort)):(worker.on=process.on.bind(process),worker.send=process.send.bind(process),worker.on(\"disconnect\",function(){process.exit(1)}),worker.exit=process.exit.bind(process))}function convertError(o){return Object.getOwnPropertyNames(o).reduce(function(r,e){return Object.defineProperty(r,e,{value:o[e],enumerable:!0})},{})}function isPromise(r){return r&&\"function\"==typeof r.then&&\"function\"==typeof r.catch}worker.methods={},worker.methods.run=function(r,e){r=new Function(\"return (\"+r+\").apply(null, arguments);\");return r.apply(r,e)},worker.methods.methods=function(){return Object.keys(worker.methods)};var currentRequestId=null;worker.on(\"message\",function(e){if(e===TERMINATE_METHOD_ID)return worker.exit(0);try{var r=worker.methods[e.method];if(!r)throw new Error('Unknown method \"'+e.method+'\"');currentRequestId=e.id;var o=r.apply(r,e.params);isPromise(o)?o.then(function(r){worker.send({id:e.id,result:r,error:null}),currentRequestId=null}).catch(function(r){worker.send({id:e.id,result:null,error:convertError(r)}),currentRequestId=null}):(worker.send({id:e.id,result:o,error:null}),currentRequestId=null)}catch(r){worker.send({id:e.id,result:null,error:convertError(r)})}}),worker.register=function(r){if(r)for(var e in r)r.hasOwnProperty(e)&&(worker.methods[e]=r[e]);worker.send(\"ready\")},worker.emit=function(r){currentRequestId&&worker.send({id:currentRequestId,isEvent:!0,payload:r})},__webpack_unused_export__=worker.register,__webpack_unused_export__=worker.emit}()}();";

/***/ }),

/***/ 397:
/***/ (function(module) {

// source of inspiration: https://github.com/sindresorhus/require-fool-webpack
var requireFoolWebpack = eval('typeof require !== \'undefined\' ' + '? require ' + ': function (module) { throw new Error(\'Module " + module + " not found.\') }');
module.exports = requireFoolWebpack;

/***/ }),

/***/ 744:
/***/ (function(__unused_webpack_module, exports) {

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

/**
 * worker must be started as a child process or a web worker.
 * It listens for RPC messages from the parent process.
 */
// source of inspiration: https://github.com/sindresorhus/require-fool-webpack
var requireFoolWebpack = eval('typeof require !== \'undefined\'' + ' ? require' + ' : function (module) { throw new Error(\'Module " + module + " not found.\') }');
/**
 * Special message sent by parent which causes the worker to terminate itself.
 * Not a "message object"; this string is the entire message.
 */

var TERMINATE_METHOD_ID = '__workerpool-terminate__'; // var nodeOSPlatform = require('./environment').nodeOSPlatform;
// create a worker API for sending and receiving messages which works both on
// node.js and in the browser

var worker = {
  exit: function exit() {}
};

if (typeof self !== 'undefined' && typeof postMessage === 'function' && typeof addEventListener === 'function') {
  // worker in the browser
  worker.on = function (event, callback) {
    addEventListener(event, function (message) {
      callback(message.data);
    });
  };

  worker.send = function (message) {
    postMessage(message);
  };
} else if (typeof process !== 'undefined') {
  // node.js
  var WorkerThreads;

  try {
    WorkerThreads = requireFoolWebpack('worker_threads');
  } catch (error) {
    if (_typeof(error) === 'object' && error !== null && error.code === 'MODULE_NOT_FOUND') ; else {
      throw error;
    }
  }

  if (WorkerThreads &&
  /* if there is a parentPort, we are in a WorkerThread */
  WorkerThreads.parentPort !== null) {
    var parentPort = WorkerThreads.parentPort;
    worker.send = parentPort.postMessage.bind(parentPort);
    worker.on = parentPort.on.bind(parentPort);
  } else {
    worker.on = process.on.bind(process);
    worker.send = process.send.bind(process); // register disconnect handler only for subprocess worker to exit when parent is killed unexpectedly

    worker.on('disconnect', function () {
      process.exit(1);
    });
    worker.exit = process.exit.bind(process);
  }
} else {
  throw new Error('Script must be executed as a worker');
}

function convertError(error) {
  return Object.getOwnPropertyNames(error).reduce(function (product, name) {
    return Object.defineProperty(product, name, {
      value: error[name],
      enumerable: true
    });
  }, {});
}
/**
 * Test whether a value is a Promise via duck typing.
 * @param {*} value
 * @returns {boolean} Returns true when given value is an object
 *                    having functions `then` and `catch`.
 */


function isPromise(value) {
  return value && typeof value.then === 'function' && typeof value["catch"] === 'function';
} // functions available externally


worker.methods = {};
/**
 * Execute a function with provided arguments
 * @param {String} fn     Stringified function
 * @param {Array} [args]  Function arguments
 * @returns {*}
 */

worker.methods.run = function run(fn, args) {
  var f = new Function('return (' + fn + ').apply(null, arguments);');
  return f.apply(f, args);
};
/**
 * Get a list with methods available on this worker
 * @return {String[]} methods
 */


worker.methods.methods = function methods() {
  return Object.keys(worker.methods);
};

var currentRequestId = null;
worker.on('message', function (request) {
  if (request === TERMINATE_METHOD_ID) {
    return worker.exit(0);
  }

  try {
    var method = worker.methods[request.method];

    if (method) {
      currentRequestId = request.id; // execute the function

      var result = method.apply(method, request.params);

      if (isPromise(result)) {
        // promise returned, resolve this and then return
        result.then(function (result) {
          worker.send({
            id: request.id,
            result: result,
            error: null
          });
          currentRequestId = null;
        })["catch"](function (err) {
          worker.send({
            id: request.id,
            result: null,
            error: convertError(err)
          });
          currentRequestId = null;
        });
      } else {
        // immediate result
        worker.send({
          id: request.id,
          result: result,
          error: null
        });
        currentRequestId = null;
      }
    } else {
      throw new Error('Unknown method "' + request.method + '"');
    }
  } catch (err) {
    worker.send({
      id: request.id,
      result: null,
      error: convertError(err)
    });
  }
});
/**
 * Register methods to the worker
 * @param {Object} methods
 */

worker.register = function (methods) {
  if (methods) {
    for (var name in methods) {
      if (methods.hasOwnProperty(name)) {
        worker.methods[name] = methods[name];
      }
    }
  }

  worker.send('ready');
};

worker.emit = function (payload) {
  if (currentRequestId) {
    worker.send({
      id: currentRequestId,
      isEvent: true,
      payload: payload
    });
  }
};

{
  exports.add = worker.register;
  exports.emit = worker.emit;
}

/***/ })

/******/ 	});
/************************************************************************/
/******/ 	// The module cache
/******/ 	var __webpack_module_cache__ = {};
/******/ 	
/******/ 	// The require function
/******/ 	function __webpack_require__(moduleId) {
/******/ 		// Check if module is in cache
/******/ 		var cachedModule = __webpack_module_cache__[moduleId];
/******/ 		if (cachedModule !== undefined) {
/******/ 			return cachedModule.exports;
/******/ 		}
/******/ 		// Create a new module (and put it into the cache)
/******/ 		var module = __webpack_module_cache__[moduleId] = {
/******/ 			// no module.id needed
/******/ 			// no module.loaded needed
/******/ 			exports: {}
/******/ 		};
/******/ 	
/******/ 		// Execute the module function
/******/ 		__webpack_modules__[moduleId](module, module.exports, __webpack_require__);
/******/ 	
/******/ 		// Return the exports of the module
/******/ 		return module.exports;
/******/ 	}
/******/ 	
/************************************************************************/
var __webpack_exports__ = {};
// This entry need to be wrapped in an IIFE because it need to be isolated against other modules in the chunk.
!function() {
var exports = __webpack_exports__;
var environment = __webpack_require__(828);
/**
 * Create a new worker pool
 * @param {string} [script]
 * @param {WorkerPoolOptions} [options]
 * @returns {Pool} pool
 */


exports.pool = function pool(script, options) {
  var Pool = __webpack_require__(345);

  return new Pool(script, options);
};
/**
 * Create a worker and optionally register a set of methods to the worker.
 * @param {Object} [methods]
 */


exports.worker = function worker(methods) {
  var worker = __webpack_require__(744);

  worker.add(methods);
};
/**
 * Sends an event to the parent worker pool.
 * @param {any} payload 
 */


exports.workerEmit = function workerEmit(payload) {
  var worker = __webpack_require__(744);

  worker.emit(payload);
};
/**
 * Create a promise.
 * @type {Promise} promise
 */


exports.Promise = __webpack_require__(219);
exports.platform = environment.platform;
exports.isMainThread = environment.isMainThread;
exports.cpus = environment.cpus;
}();
/******/ 	return __webpack_exports__;
/******/ })()
;
});

}(workerpool$1));

var workerpool = workerpool$1.exports;

const WINDOW_FEATURES = [
    'ArrayBuffer',
    'File',
    'FileReader',
    'SubtleCrypto',
    'Uint8Array',
    'WebAssembly',
    'fetch',
];
/**
 * Checks if the current browser is compatible with the features needed for
 * this library.
 *
 * @return {boolean}
 */
function isCompatible() {
    return WINDOW_FEATURES.every((x) => x in window);
}
/**
 * Throws an error if the current browser is incompatible with this library.
 */
function ensureCompatibility() {
    if (!isCompatible()) {
        throw new IncompatibleBrowserError();
    }
}

var _a$1, _ImageProvenance_toolkitResult, _ImageProvenance_ingredientIdRegExp;
const dbg$1 = debug('caisdk:ImageProvenance');
/**
 * A class that holds the C2PA provenance data of an asset
 *
 * @see {@link https://c2pa.org/public-draft/#_core_aspects_of_c2pa}
 *
 * @public
 */
class ImageProvenance {
    /**
     * Create a new `ImageProvenance` object. Typically this will only be returned from
     * `ContentAuth` calls.
     *
     * @param toolkitResult - The result from the WebAssembly toolkit (@contentauth/ask)
     *
     * @internal
     */
    constructor(toolkitResult, source) {
        var _b, _c;
        _ImageProvenance_toolkitResult.set(this, void 0);
        /**
         * The list of claims contained in this provenance data
         */
        Object.defineProperty(this, "claims", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * The original asset that this provenance is a part of
         */
        Object.defineProperty(this, "source", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        dbg$1('Creating ImageProvenance with data', toolkitResult);
        __classPrivateFieldSet(this, _ImageProvenance_toolkitResult, toolkitResult, "f");
        this.source = source;
        this.claims = Object.keys((_c = (_b = __classPrivateFieldGet(this, _ImageProvenance_toolkitResult, "f")) === null || _b === void 0 ? void 0 : _b.claims) !== null && _c !== void 0 ? _c : []).reduce((map, claimId) => map.set(claimId, new Claim(this, claimId, __classPrivateFieldGet(this, _ImageProvenance_toolkitResult, "f").claims[claimId])), new Map());
    }
    /**
     * This resolves an ID of either a claim or an ingredient so that we can identify it globally.
     *
     * @remarks
     * We should be able to access every claim/ingredient in one of two ways:
     *
     * - Claims should be able to be accessed by their ID, e.g. `mEiBD6JdB/na1TIxvcw9HMkbo6stDkkiNFcy8Lsp3oW5yOw`
     * - Ingredients should be able to be accessed by their associated claim ID and index,
     *   e.g. `mEiBD6JdB/na1TIxvcw9HMkbo6stDkkiNFcy8Lsp3oW5yOw[0]`
     *
     * @param id - the id of the claim or ingredient to get
     */
    resolveId(id) {
        var _b;
        const isIngredient = __classPrivateFieldGet(ImageProvenance, _a$1, "f", _ImageProvenance_ingredientIdRegExp).test(id);
        if (isIngredient) {
            const [, claimId, ingredientIdx] = id.match(__classPrivateFieldGet(ImageProvenance, _a$1, "f", _ImageProvenance_ingredientIdRegExp));
            return (_b = this.claims.get(claimId)) === null || _b === void 0 ? void 0 : _b.ingredients[parseInt(ingredientIdx, 10)];
        }
        return this.claims.get(id);
    }
    /**
     * Returns `true` if C2PA metadata exists on this image
     */
    get exists() {
        return !!__classPrivateFieldGet(this, _ImageProvenance_toolkitResult, "f").head;
    }
    /**
     * Gets the active (latest) claim in the provenance data
     */
    get activeClaim() {
        if (this.exists) {
            return this.claims.get(__classPrivateFieldGet(this, _ImageProvenance_toolkitResult, "f").head);
        }
    }
    /**
     * Returns the raw claim data from the WebAssembly toolkit
     */
    get data() {
        return __classPrivateFieldGet(this, _ImageProvenance_toolkitResult, "f");
    }
    /**
     * Any errors coming back from the toolkit
     */
    get errors() {
        var _b;
        return (_b = this.data.errors) !== null && _b !== void 0 ? _b : [];
    }
}
_a$1 = ImageProvenance, _ImageProvenance_toolkitResult = new WeakMap();
_ImageProvenance_ingredientIdRegExp = { value: /^(\S+)\[(\d+)\]$/ };

var _Source_hash;
debug('caisdk:ImageProvenance:Source');
/**
 * Represents the asset that the provenance data is extracted from
 */
class Source {
    /**
     * Creates a new source instance
     *
     * @param blob - The binary data of the source asset
     * @param metadata - Any additional metdata to include that we know up front
     *
     * @internal
     */
    constructor(blob, metadata = {}) {
        /**
         * The binary data of the source asset
         */
        Object.defineProperty(this, "blob", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        /**
         * Any metadata that we know/can derive from the asset (e.g. filename)
         */
        Object.defineProperty(this, "metadata", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        _Source_hash.set(this, void 0);
        this.blob = blob;
        this.metadata = metadata;
        if (blob instanceof File) {
            this.metadata = Object.assign(Object.assign({}, Source.metadataFromFile(blob)), this.metadata);
        }
    }
    /**
     * Gets the mime type of the asset
     */
    get type() {
        return this.blob.type;
    }
    /**
     * Gets the filename of the asset, if it is known or able to be derived
     */
    get filename() {
        return this.metadata.filename;
    }
    /**
     * Determines if the source is valid
     */
    isValid() {
        return Validator.isValidMimeType(this.type);
    }
    /**
     * Gets the binary data of this asset as an array buffer
     */
    arrayBuffer() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.blob.arrayBuffer();
        });
    }
    /**
     * Gets the size of this asset (in bytes)
     */
    size() {
        return __awaiter(this, void 0, void 0, function* () {
            const buffer = yield this.arrayBuffer();
            return buffer.byteLength;
        });
    }
    computeHash() {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof __classPrivateFieldGet(this, _Source_hash, "f") === 'undefined') {
                const arrayBuffer = yield this.arrayBuffer();
                if (arrayBuffer) {
                    __classPrivateFieldSet(this, _Source_hash, yield sha(arrayBuffer), "f");
                }
                else {
                    __classPrivateFieldSet(this, _Source_hash, null, "f");
                }
            }
            return __classPrivateFieldGet(this, _Source_hash, "f");
        });
    }
    /**
     * Generates a blob URL from the source data
     *
     * @returns An object with a `url` and `dispose` function
     */
    generateUrl() {
        return __awaiter(this, void 0, void 0, function* () {
            const url = URL.createObjectURL(this.blob);
            return {
                url,
                dispose: () => URL.revokeObjectURL(url),
            };
        });
    }
    /**
     * Attempts to get additional metadata for this source from its backing `File` object, if available
     *
     * @param file - The source data as a `File` object
     */
    static metadataFromFile(file) {
        return {
            filename: file.name,
        };
    }
    /**
     * Attemps to derive additional metadata (namely `filename`) from the URL
     *
     * @param url - The URL of the source data
     */
    static metadataFromUrl(url) {
        var _a;
        let path = url;
        try {
            const { pathname } = new URL(url);
            path = pathname;
        }
        catch (err) { }
        const filename = (_a = path.split('/').pop()) !== null && _a !== void 0 ? _a : '';
        return {
            filename,
        };
    }
}
_Source_hash = new WeakMap();

var _ContentAuth_instances, _a, _ContentAuth_config, _ContentAuth_pool, _ContentAuth_wasm, _ContentAuth_downloader, _ContentAuth_workerUrl, _ContentAuth_jobCounter, _ContentAuth_defaultConfig, _ContentAuth_fetchPool, _ContentAuth_fetchWasm, _ContentAuth_fetchDownloader;
const dbg = debug('caisdk:ContentAuth');
const dbgTask = debug('caisdk:ContentAuth:task');
/**
 * The Content Authenticity Initiative JavaScript SDK
 *
 * @packageDocumentation
 */
/**
 * The entry class for the SDK
 *
 * @remarks
 * You can create one instance of this class to process multiple images.
 * At a minimum, you will have to instantiate it with the URLs of the WASM and the worker:
 * ```ts
 * const sdk = new ContentAuth({ wasmSrc, workerSrc });
 * const provenance = await sdk.processImage(imageUrl);
 * ```
 *
 * @public
 */
class ContentAuth {
    /**
     * Creates a new ContentAuth instace that can be used to verify and read
     * metadata from multiple assets.
     *
     * @param cfg - Configuration object for overriding the defaults
     */
    constructor(cfg = {}) {
        _ContentAuth_instances.add(this);
        /**
         * Configuration options for this `ContentAuth` instance
         */
        _ContentAuth_config.set(this, void 0);
        /**
         * The web worker pool to run tasks on
         */
        _ContentAuth_pool.set(this, void 0);
        /**
         * The WebAssembly binary data
         */
        _ContentAuth_wasm.set(this, void 0);
        /**
         * Manages asset downloads when given URL inputs
         */
        _ContentAuth_downloader.set(this, void 0);
        /**
         * A blob URL containing the worker JS
         */
        _ContentAuth_workerUrl.set(this, void 0);
        /**
         * Assigns a unique ID for each job for debug logs
         */
        _ContentAuth_jobCounter.set(this, 0);
        __classPrivateFieldSet(this, _ContentAuth_config, Object.assign(Object.assign({}, __classPrivateFieldGet(ContentAuth, _a, "f", _ContentAuth_defaultConfig)), cfg), "f");
        dbg('Instantiating SDK', __classPrivateFieldGet(this, _ContentAuth_config, "f"));
        ensureCompatibility();
        if (!__classPrivateFieldGet(this, _ContentAuth_config, "f").wasmSrc) {
            throw new MissingWasmSourceError();
        }
        if (!__classPrivateFieldGet(this, _ContentAuth_config, "f").workerSrc) {
            throw new MissingWorkerSourceError();
        }
    }
    processImage(input) {
        var _b;
        return __awaiter(this, void 0, void 0, function* () {
            const jobId = __classPrivateFieldSet(this, _ContentAuth_jobCounter, (_b = __classPrivateFieldGet(this, _ContentAuth_jobCounter, "f"), ++_b), "f");
            const downloader = yield __classPrivateFieldGet(this, _ContentAuth_instances, "m", _ContentAuth_fetchDownloader).call(this);
            let source = null;
            // File extends Blob, so this can be used for both
            if (input instanceof Blob) {
                source = new Source(input);
            }
            else if (input instanceof HTMLImageElement) {
                const data = yield downloader.inspect(input.src);
                if (data) {
                    source = new Source(data, Source.metadataFromUrl(input.src));
                }
            }
            else if (typeof input === 'string') {
                const data = yield downloader.inspect(input);
                if (data) {
                    source = new Source(data, Source.metadataFromUrl(input));
                }
            }
            if (!((source === null || source === void 0 ? void 0 : source.blob) instanceof Blob)) {
                throw new InvalidInputError();
            }
            if (!source.isValid()) {
                throw new InvalidMimeTypeError(source.type);
            }
            dbgTask('[%s] Processing input', jobId, input);
            const pool = yield __classPrivateFieldGet(this, _ContentAuth_instances, "m", _ContentAuth_fetchPool).call(this);
            const wasm = yield __classPrivateFieldGet(this, _ContentAuth_instances, "m", _ContentAuth_fetchWasm).call(this);
            // TODO: Add support for transferable objects
            const buffer = yield source.arrayBuffer();
            const result = yield pool.exec('getReport', [wasm, buffer, source.type]);
            dbgTask('[%s] Received worker result', jobId, result);
            return new ImageProvenance(result, source);
        });
    }
    /**
     * Convenience function to process multiple images at once
     *
     * @param inputs Array of inputs to pass to `processImage`
     */
    processImages(inputs) {
        return __awaiter(this, void 0, void 0, function* () {
            // TODO: Figure out how to make TypeScript work without casting to `any`
            // It *may* be a byproduct of https://github.com/microsoft/TypeScript/issues/14107
            return Promise.all(inputs.map((input) => this.processImage(input)));
        });
    }
    /**
     * Allows you to generate a report from `application/c2pa` manifest data
     * @param source The source image this manifest data is attributed to
     * @param c2paData The `application/c2pa` manifest data
     */
    processC2pa(source, c2paData) {
        var _b;
        return __awaiter(this, void 0, void 0, function* () {
            const jobId = __classPrivateFieldSet(this, _ContentAuth_jobCounter, (_b = __classPrivateFieldGet(this, _ContentAuth_jobCounter, "f"), ++_b), "f");
            dbgTask('[%s] Processing input', jobId, c2paData);
            const pool = yield __classPrivateFieldGet(this, _ContentAuth_instances, "m", _ContentAuth_fetchPool).call(this);
            const wasm = yield __classPrivateFieldGet(this, _ContentAuth_instances, "m", _ContentAuth_fetchWasm).call(this);
            // TODO: Add support for transferable objects
            const buffer = yield c2paData.arrayBuffer();
            const result = yield pool.exec('getReport', [wasm, buffer, c2paData.type]);
            dbgTask('[%s] Received worker result', jobId, result);
            return new ImageProvenance(result, source);
        });
    }
    /**
     * Generates a URL that pre-loads the `assetUrl` into the Content Authenticity Verify site
     * for deeper inspection by users.
     *
     * @param assetUrl The URL of the asset you want to view in Verify
     */
    static generateVerifyUrl(assetUrl) {
        const url = new URL('https://verify.contentauthenticity.org/inspect');
        url.searchParams.set('source', assetUrl);
        return url.toString();
    }
    /**
     * Waits until all scheduled tasks have completed, then terminates the pool
     */
    destroy() {
        return __awaiter(this, void 0, void 0, function* () {
            if (__classPrivateFieldGet(this, _ContentAuth_pool, "f")) {
                return __classPrivateFieldGet(this, _ContentAuth_pool, "f").terminate();
            }
            if (__classPrivateFieldGet(this, _ContentAuth_workerUrl, "f")) {
                URL.revokeObjectURL(__classPrivateFieldGet(this, _ContentAuth_workerUrl, "f"));
            }
        });
    }
}
_a = ContentAuth, _ContentAuth_config = new WeakMap(), _ContentAuth_pool = new WeakMap(), _ContentAuth_wasm = new WeakMap(), _ContentAuth_downloader = new WeakMap(), _ContentAuth_workerUrl = new WeakMap(), _ContentAuth_jobCounter = new WeakMap(), _ContentAuth_instances = new WeakSet(), _ContentAuth_fetchPool = function _ContentAuth_fetchPool() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!__classPrivateFieldGet(this, _ContentAuth_pool, "f")) {
            const res = yield fetch(__classPrivateFieldGet(this, _ContentAuth_config, "f").workerSrc);
            if (!res.ok) {
                throw new InvalidWorkerSourceError(__classPrivateFieldGet(this, _ContentAuth_config, "f").workerSrc, res);
            }
            const src = yield res.text();
            // TODO: Check subresource integrity
            dbg('Fetched worker from %s (%d bytes)', __classPrivateFieldGet(this, _ContentAuth_config, "f").workerSrc, src.length);
            const workerBlob = new Blob([src], { type: 'application/javascript' });
            __classPrivateFieldSet(this, _ContentAuth_workerUrl, URL.createObjectURL(workerBlob), "f");
            __classPrivateFieldSet(this, _ContentAuth_pool, workerpool.pool(__classPrivateFieldGet(this, _ContentAuth_workerUrl, "f"), __classPrivateFieldGet(this, _ContentAuth_config, "f").poolOptions), "f");
        }
        return __classPrivateFieldGet(this, _ContentAuth_pool, "f");
    });
}, _ContentAuth_fetchWasm = function _ContentAuth_fetchWasm() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!__classPrivateFieldGet(this, _ContentAuth_wasm, "f")) {
            const wasmSrc = __classPrivateFieldGet(this, _ContentAuth_config, "f").wasmSrc;
            if (wasmSrc instanceof WebAssembly.Module) {
                __classPrivateFieldSet(this, _ContentAuth_wasm, wasmSrc, "f");
            }
            else {
                // We are offloading compilation to the worker instead of using WebAssembly.compileStreaming
                // here since the latter is not available in Safari < 15 (see https://caniuse.com/mdn-javascript_builtins_webassembly_compilestreaming).
                // It also allows us to check the integrity of the module before processing it (see https://github.com/contentauth/js-sdk/issues/70)
                dbg('Fetching WASM binary from url %s', wasmSrc);
                const pool = yield __classPrivateFieldGet(this, _ContentAuth_instances, "m", _ContentAuth_fetchPool).call(this);
                const res = yield fetch(wasmSrc);
                const buf = yield res.arrayBuffer();
                dbg('Sending WASM binary buffer to worker for compilation', {
                    size: buf.byteLength,
                });
                __classPrivateFieldSet(this, _ContentAuth_wasm, yield pool.exec('compileWasm', [buf]), "f");
                dbg('Compilation finished');
            }
        }
        return __classPrivateFieldGet(this, _ContentAuth_wasm, "f");
    });
}, _ContentAuth_fetchDownloader = function _ContentAuth_fetchDownloader() {
    return __awaiter(this, void 0, void 0, function* () {
        if (!__classPrivateFieldGet(this, _ContentAuth_downloader, "f")) {
            const pool = yield __classPrivateFieldGet(this, _ContentAuth_instances, "m", _ContentAuth_fetchPool).call(this);
            __classPrivateFieldSet(this, _ContentAuth_downloader, new Downloader(pool, __classPrivateFieldGet(this, _ContentAuth_config, "f").downloadOptions), "f");
        }
        return __classPrivateFieldGet(this, _ContentAuth_downloader, "f");
    });
};
_ContentAuth_defaultConfig = { value: {
        wasmSrc: null,
        workerSrc: null,
        poolOptions: {
            workerType: 'web',
        },
        downloadOptions: undefined,
    } };

export { ActionsAssertion, AdobeCryptoAddressesAssertion, AdobeDictionaryAssertion, Assertion, AssertionFactory, AssertionLabel, Asset, Claim, ContentAuth, CreativeWorkAssertion, error as Errors, ImageProvenance, Ingredient, RecorderFormat, Source, Validator };
